import { i18n } from '../i18n.js';
import { MoonfallCloud } from '../convex-client.js';
/**
 * UI Cards Module
 * Handles card creation, display, and interactions
 */

import { ActionIntent } from './action-intent.js';
import { AssetPreloader } from './asset-preloader.js';
import { Board } from './board.js';
import { CardbackArt } from './cardback-art.js';
import { CardBackRaster } from './card-back-raster.js';
import { CardDrag } from './card-drag.js';
import { CardDescEngraving } from './card-desc-engraving.js';
import { CardRestView } from './card-rest-view.js';
import { CardPlay } from './card-play.js';
import { CardTilt } from './card-tilt.js';
import { CrispHover } from './crisp-hover.js';
import { cleanStatusToken, splitStatusSegments } from './status-segments.js';
import { Effects } from './effects.js';
import { EnemyFan } from './enemy-fan.js';
import { Env } from './env.js';
import { FX } from './fx.js';
import { HoverHandoff } from './hover-handoff.js';
import { imageAttemptDeadlineMs } from './image-attempt-deadline.js';
import { SafeArea } from './safe-area.js';
import { SoundEffects } from './sound-effects.js';
import { Soundtrack } from './soundtrack.js';
import { StoryFeed } from './story-feed.js';
import { TouchPreview } from './touch-preview.js';
import { GameState } from '../game-state.js';
import { SocketHandler } from '../socket-handler.js';
import { LoadScheduler } from '../utils/load-scheduler.js';
import { uiScaleOf } from './ui-scale.js';
import { usedBoxSize } from './used-box-size.js';
let Cards = {};

// Create UI namespace if it doesn't exist

// Create an empty Cards object immediately to ensure it exists for other modules

(function () {
    'use strict';

    // Global debug flag - set to false to disable all debug output
    const DEBUG_ENABLED = false;

    // Hover enlargement uses physical CSS zoom when Env proves the required
    // geometry, with transform scale as the layout-neutral fallback. These
    // capabilities do not by themselves claim anything about raster sharpness.
    const _isSafari = Env
        ? Env.isSafari
        : /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const _useZoomHover = Env
        ? Env.useZoomHover
        : !_isSafari;
    const _zoomScalesMargins = typeof Env?.zoomScalesMargins === 'boolean'
        ? Env.zoomScalesMargins
        : null;

    const STELLAR_VIDEO_FALLBACK = '/assets/stellar_placeholder.mp4';
    const STELLAR_WEBM_LOOP_TYPE = 'video/webm; codecs="vp8"';
    const STELLAR_WEBM_ACTION_TYPE = 'video/webm; codecs="vp8, opus"';
    const STELLAR_LOOP_VOLUME = 0.7;
    const STELLAR_SUMMON_VOLUME = 1;
    const STELLAR_SUMMON_DUCK_FACTOR = 0.4;
    const STELLAR_SUMMON_DUCK_MS = 2500;
    const STELLAR_CAST_DUCK_MS = 4000;
    const STELLAR_AUDIO_FADE_MS = 200;
    const CARD_IMAGE_PLACEHOLDER = '/assets/art_placeholder.webp';
    /* How long an enlargement may wait for its art before presenting anyway.
       A card already rastered in the hand settles well inside this. */
    const HOVER_PREVIEW_ART_GRACE_MS = 220;
    const CARD_FACE_FONT_TIMEOUT_MS = 1500;
    /* The mana numeral's own face (`.card-cost` in cards.css). It is
       font-display: block, so it is warmed the moment this module loads: the
       preload is spent at once and no card ever waits on it. `waitForCardFonts`
       still awaits it before a face counts as settled. */
    const MANA_NUMERAL_FONT = '700 16px "Moonfall Mana Numerals"';
    try {
        document.fonts?.load?.(MANA_NUMERAL_FONT, '0123456789')?.catch?.(() => {});
    } catch (_) { /* no FontFaceSet to warm */ }
    /* How many status lines the RESTING card prints before folding the rest
       into a `+N` chip. `card.status` is free-form storyteller output split on
       `;` with no cap, and the resting board card is 37px wide at the 844x280
       page box, so an uncapped ledger climbs the card: measured, five statuses
       reached 16-18% of the mana cost and eight covered the cost and the name
       outright. Four plus the chip is five lines against the eight the CSS
       ceiling admits, so an ordinary stack never reaches the clip. The full
       ledger is one hover away — cards.css reveals every held-back line on the
       enlarged card, the hover preview and the story-feed rail. */
    /* ONE, not four. Measured on the owner's iPhone page box in WebKit against
       the real storyteller: an ESTABLISHED board card that had been painting its
       78-char description for seven seconds lost 20.1% of its rules INK at two
       real statuses ("Poisoned; Corroded"), and 100% at four plate lines — which
       is what TWO realistic statuses produce, because the storyteller caps a
       status at 40 characters and the resting board card fits ~23 per line.
       `descOpacity` and `artOpacity` were both 1 throughout: the text was not
       faded, it was covered by an opaque plate at z-index 4, and nothing gives
       it back while the status lasts. That is the owner's report — "the other
       cards on its board lose the card description ... card art is flawless
       ... only card description vanishes".
       The plate cannot move below the description (the gap is 1.193 card-units
       against a 1.635-unit minimum plate) and moving it up is what climbed over
       the mana disc. So it stays at the foot and holds ONE line, with the
       overflow chip on the same row: 0.1% of the ink at every status count.
       The full ledger is one tap away — the popup moves the plate to a side
       panel and releases the cap.
       Contract: tests/unit/styles/statusPlateSparesDescription.test.js. */
    const STATUS_PARTS_ON_RESTING_CARD = 1;

    /**
     * Race an art-readiness promise against the grace cap.
     * Resolves to the promise's value if it wins, or `null` if the cap fires
     * first — `null` means "still in flight", which is NOT a failure.
     */
    function withArtGrace(promise) {
        let timer = null;
        return Promise.race([
            Promise.resolve(promise).then((value) => {
                if (timer !== null) clearTimeout(timer);
                timer = null;
                return value;
            }, () => {
                if (timer !== null) clearTimeout(timer);
                timer = null;
                return false;
            }),
            new Promise((resolve) => {
                timer = setTimeout(() => resolve(null), HOVER_PREVIEW_ART_GRACE_MS);
            }),
        ]);
    }
    const CARD_IMAGE_RESOLVE_TIMEOUT_MS = 3000;
    // One attempt's window before the loader drops the request and retries.
    // Dropping `src` discards every byte received, so the window must outlast
    // any healthy download; it was 3 s, which cancelled and restarted the
    // Collection's first page forever below ~5 Mbps. The policy (30 s, then
    // doubling per failed attempt to 120 s) and its measurements live in
    // image-attempt-deadline.js, shared with retrying-image.ts.
    const CARD_IMAGE_LOAD_TIMEOUT_MS = imageAttemptDeadlineMs(0);
    // A sampled data: URL is a LOCAL decode, not a download: nothing is on the
    // wire, so an undecodable sample is known in a few seconds and the cached
    // authority can take over. Only network attempts get the long window.
    const CARD_IMAGE_DECODE_TIMEOUT_MS = 3000;
    const CARD_IMAGE_RETRY_DELAYS_MS = Object.freeze([1000, 3000, 8000, 20000, 30000]);
    const CARD_IMAGE_DETACHED_GRACE_MS = 5000;
    // Static cards render at their final DPR. Surfaces that physically enlarge
    // the same DOM card opt into their own reserve through
    // `rasterReserveScale`; popup cards render at their final geometry instead.
    // This keeps grids bounded without sacrificing a stable hover bitmap.
    const CARD_ART_OVERSAMPLE = 1;
    // A genuinely transformed card can settle a fraction beyond the preceding
    // frame's measurement. Physical popups measure exactly 1 and pay no extra
    // raster cost; compositor-enlarged callers retain a small coverage reserve.
    const TRANSFORM_RASTER_SETTLEMENT_RESERVE = 1.02;
    const VISUAL_EXPANSION_EPSILON = 0.001;

    /** Module-scope translation lookup with an honest English fallback. */
    function cardText(key, fallback) {
        try {
            const translated = i18n?.t?.(key);
            return translated && translated !== key ? translated : fallback;
        } catch (_) {
            return fallback;
        }
    }
    const CARD_ART_ATTACHMENT_GRACE_FRAMES = 3;
    const observedCardArt = new WeakSet();
    const connectedCardArt = new WeakSet();
    const pendingCardArtTasks = new WeakMap();
    const pendingCardArtReadiness = new WeakMap();
    const cardArtLoaderControllers = new WeakMap();
    const trackedCardArtOwners = new Set();
    const cardArtOwnerTimers = new WeakMap();
    const cardArtEnhancementGenerations = new WeakMap();
    const lockedRasterAuthorities = new WeakMap();
    const transientLockedRasterAuthorities = new WeakSet();
    const lockedRasterRefreshGenerations = new WeakMap();
    const lockedRasterRefreshTasks = new WeakMap();
    // paint -> the last EXACT pixels it showed, held while it shows a resize
    // refit of them (THE RESIZE FRAME PAINTS THE NEW SIZE, CHEAPLY).
    const refitSourcePixels = new WeakMap();
    const pendingLockedRasterRefreshTargets = new Set();
    const descriptionFitGenerations = new WeakMap();
    const titleFitGenerations = new WeakMap();
    let cardArtResizeObserver = null;
    let cardArtOwnerObserver = null;
    let lockedRasterRefreshFrame = null;
    let refreshAllLockedRasters = false;
    let lockedRasterDprQuery = null;
    let lockedRasterDprListener = null;
    let lockedRasterGlobalListenersBound = false;
    const stellarSummonPlayed = new Set();
    const stellarSummonPending = new Set();
    const activeSummonVideos = new Map();
    const stellarCastPlayed = new Set();
    const stellarCastSoundSuppressions = new Map();
    const MAX_STELLAR_CAST_TRACKING = 128;
    let summonStagingRoot = null;
    let stellarBoardSnapshot = null;

    function isMoonFuseInterimPlaceholderSource(value) {
        if (typeof value !== 'string' || !value.trim()) return false;
        try {
            let pathname = new URL(
                value.trim(),
                window.location?.href || 'http://localhost/',
            ).pathname;
            for (let pass = 0; pass < 3; pass += 1) {
                const decoded = decodeURIComponent(pathname);
                if (decoded === pathname) break;
                pathname = decoded;
            }
            return pathname === CARD_IMAGE_PLACEHOLDER;
        } catch (_) {
            return false;
        }
    }

    /* "Still being made" has TWO server vocabularies, and the client must
       speak both. Legacy card rows carry
       ['pending', 'generating', 'awaiting_art_selection']
       (convex/foundryLegacyMigrationData.js LEGACY_ACTIVE_CARD_STATUSES) —
       but a card that is fusing RIGHT NOW has no card row at all: its status
       is the discovery-operation state machine (convex/schema.ts:
       generating_text | generating_art | ...), which foundry.js forwards
       verbatim as forgeStatus. Knowing only the legacy list meant a live
       fusion's interim preview fell through every placeholder branch and
       painted an empty art well ("no longer is a placeholder art while
       loading"). Showing the placeholder is still gated on there being no
       art to show (hasImmediateMoonFuseArt), so a card that HAS its art —
       the normal case for awaiting_art_selection — is unaffected. */
    const MOON_FUSE_ACTIVE_FORGE_STATUSES = new Set([
        'pending',
        'generating',
        'generating_text',
        'generating_art',
        'awaiting_art_selection',
    ]);

    function isMoonFuseCardInProgress(card) {
        const status = String(card?.forgeStatus || '').trim().toLowerCase();
        if (!MOON_FUSE_ACTIVE_FORGE_STATUSES.has(status)) return false;
        return card?.collection === 'Moon Foundry'
            || card?.origin === 'foundry'
            || Boolean(card?.forgeStatus);
    }

    function hasImmediateMoonFuseArt(card, imageCandidates) {
        if (imageCandidates.directUrls.length > 0) return true;

        const rawPicture = typeof card?.picture === 'string' ? card.picture.trim() : '';
        if (rawPicture && !isMoonFuseInterimPlaceholderSource(rawPicture)) return true;

        const peekUrl = MoonfallCloud?.media?.peekUrl;
        if (typeof peekUrl !== 'function') return false;
        return imageCandidates.assetIds.some((assetId) => {
            try {
                return Boolean(sanitizeDirectMediaUrl(peekUrl(assetId)));
            } catch (_) {
                return false;
            }
        });
    }

    function routeActionDrop(dropData, targetType, targetId, targetIndex) {
        if (ActionIntent && typeof ActionIntent.routeOrPlay === 'function') {
            return ActionIntent.routeOrPlay(dropData, targetType, targetId, targetIndex);
        }
        if (Number.isInteger(targetIndex)) {
            return SocketHandler.playAction(dropData.instanceId, targetType, targetId, targetIndex);
        }
        return SocketHandler.playAction(dropData.instanceId, targetType, targetId);
    }

    function isStellarCard(card) {
        if (!card) return false;
        if (card.isStellar === true || card.stellar === true) return true;
        if (typeof card.isStellar === 'string' && card.isStellar.toLowerCase() === 'true') return true;
        return false;
    }

    function supportsStellarMediaType(mediaType) {
        try {
            const probe = document.createElement('video');
            const result = String(probe.canPlayType?.(mediaType) || '').trim().toLowerCase();
            return result === 'probably' || result === 'maybe';
        } catch (_) { }
        return false;
    }

    function preferSupportedWebm(webmUrl, mp4Url, mediaType) {
        if ((_isSafari || Env?.isSafari === true) && mp4Url) return mp4Url;
        if (webmUrl && supportsStellarMediaType(mediaType)) return webmUrl;
        return mp4Url || '';
    }

    function resolveStellarLoopVideoSource(card, isTestStellar) {
        const source = preferSupportedWebm(
            card?.stellarVideoWebmUrl,
            card?.stellarVideoUrl,
            STELLAR_WEBM_LOOP_TYPE
        );
        if (source) return source;
        if (isTestStellar) return STELLAR_VIDEO_FALLBACK;
        return '';
    }

    function resolveStellarSummonVideoSource(card) {
        return preferSupportedWebm(
            card?.stellarSummonVideoWebmUrl,
            card?.stellarSummonVideoUrl,
            STELLAR_WEBM_ACTION_TYPE
        );
    }

    function resolveStellarCastVideoSource(card) {
        return preferSupportedWebm(
            card?.stellarCastVideoWebmUrl,
            card?.stellarCastVideoUrl,
            STELLAR_WEBM_ACTION_TYPE
        );
    }

    function resolveMp4Fallback(selectedSource, mp4Source) {
        return selectedSource && mp4Source && selectedSource !== mp4Source ? mp4Source : '';
    }

    function isSfxMuted() {
        return SoundEffects?.isMuted?.() === true;
    }

    // Stellar action audio is ordinary SFX — it follows the Sound Effects
    // channel LEVEL (0..1), not just its mute. Falls back to full volume when
    // the SFX engine is absent so summon cinematics never go silent by accident.
    function sfxEffectiveVolume() {
        const se = SoundEffects;
        if (se && typeof se.getEffectiveVolume === 'function') {
            const v = Number(se.getEffectiveVolume());
            return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 1;
        }
        if (se && typeof se.isMuted === 'function') return se.isMuted() ? 0 : 1;
        return 1;
    }

    // Live-follow: when the player changes the SFX slider mid-cinematic, retune
    // any summon or cast video that is actually playing to the new level.
    function applySfxVolumeToSummonVideos() {
        const level = sfxEffectiveVolume();
        document.querySelectorAll('video.stellar-summon-video, video.stellar-cast-video').forEach((video) => {
            if (video.paused) return;
            video.muted = level <= 0;
            video.volume = STELLAR_SUMMON_VOLUME * level;
        });
    }
    document.addEventListener('moonfall:sfx-changed', applySfxVolumeToSummonVideos);

    function sanitizeDirectMediaUrl(value) {
        if (typeof value !== 'string') return null;
        const trimmed = value.trim();
        if (
            !trimmed
            || isMoonFuseInterimPlaceholderSource(trimmed)
            || trimmed.startsWith('/api/card-image/')
        ) return null;
        if (
            trimmed.startsWith('http') ||
            trimmed.startsWith('data:') ||
            trimmed.startsWith('blob:') ||
            trimmed.startsWith('/') ||
            trimmed.startsWith('/assets/') ||
            trimmed.startsWith('assets/')
        ) {
            return trimmed;
        }
        return null;
    }

    function pushUnique(list, value) {
        if (value && !list.includes(value)) list.push(value);
    }

    function decodeLegacyAssetId(value) {
        try {
            return decodeURIComponent(value);
        } catch (_) {
            return value;
        }
    }

    function resolveMediaUrl(media, assetId) {
        return new Promise((resolve) => {
            let settled = false;
            let timer = null;
            const finish = (outcome) => {
                if (settled) return;
                settled = true;
                if (timer !== null) clearTimeout(timer);
                resolve(outcome);
            };

            timer = setTimeout(() => {
                console.warn('[Cards] Timed out resolving card image:', assetId);
                finish({ url: null, retryable: true });
            }, CARD_IMAGE_RESOLVE_TIMEOUT_MS);
            let request;
            try {
                request = media.getUrl(assetId);
            } catch (err) {
                console.warn('[Cards] Failed to resolve card image from Convex:', err);
                finish({ url: null, retryable: true });
                return;
            }
            Promise.resolve(request).then(
                // A fulfilled null is not proof that a card will never have art:
                // newly published/generated media can become resolvable after
                // the card snapshot has already reached the client. Keep the
                // real asset id alive on the same bounded retry schedule.
                (url) => finish({ url, retryable: !sanitizeDirectMediaUrl(url) }),
                (err) => {
                    console.warn('[Cards] Failed to resolve card image from Convex:', err);
                    finish({ url: null, retryable: true });
                },
            );
        });
    }

    function resolveCardImageCandidates(card) {
        const directUrls = [];
        const assetIds = [];
        const authoritativeUrl = sanitizeDirectMediaUrl(card?.imageUrl);
        if (authoritativeUrl) {
            return { directUrls: [authoritativeUrl], assetIds };
        }

        const rawPicture = typeof card?.picture === 'string' ? card.picture.trim() : '';
        const directPictureUrl = sanitizeDirectMediaUrl(rawPicture);
        if (directPictureUrl) {
            return { directUrls: [directPictureUrl], assetIds };
        } else if (rawPicture) {
            const legacyProxyMatch = rawPicture.match(/^\/api\/card-image\/(.+)$/);
            const pictureAssetId = legacyProxyMatch
                ? decodeLegacyAssetId(legacyProxyMatch[1])
                : rawPicture.replace(/^\/+/, '');
            pushUnique(assetIds, pictureAssetId);
        }

        const rawCardId = typeof card?.id === 'string' ? card.id.trim() : '';
        pushUnique(assetIds, rawCardId.replace(/^\/+/, ''));
        return { directUrls, assetIds };
    }

    function cardArtProfile(options = {}) {
        return {
            fit: options.fit === 'cover' ? 'cover' : 'contain',
            positionX: 0.5,
            positionY: 0.5,
            dpr: window.devicePixelRatio || 1,
            oversample: CARD_ART_OVERSAMPLE,
            ...options,
        };
    }

    function normalizeRasterReserveScale(value) {
        const scale = Number(value);
        return Number.isFinite(scale) ? Math.max(1, scale) : CARD_ART_OVERSAMPLE;
    }

    function rasterReserveScaleForPaint(paint) {
        return normalizeRasterReserveScale(paint?.dataset?.cardArtRasterOversample);
    }

    function declaredRasterReserveForPaint(paint) {
        return normalizeRasterReserveScale(
            paint?.closest?.('.card')?.dataset?.cardRasterReserveScale
        );
    }

    function cardArtProfileForImage(img, rect, options = {}) {
        return cardArtProfile({
            fit: img.dataset.cardArtFit,
            displayWidth: rect.width,
            displayHeight: rect.height,
            oversample: rasterReserveScaleForPaint(img),
            ...options,
        });
    }

    /* THE BOX THE RASTER IS BAKED AGAINST, and it must never be a transformed one.
       `.card-art-paint` is `object-fit: cover` AND the sampler bakes its own
       `cover` crop into the canvas, so the slice of the painting on screen is
       decided by the aspect of whatever box this function returns.
       `getBoundingClientRect` reports the box AFTER transforms, so a card inside
       one is measured in its projected shape. The card-pack fan puts every card
       inside `perspective(...) rotateY(...)`: in Firefox a resting revealed card
       baked a 271x310 canvas — aspect 0.874, its projection — while the very
       same card's hover popup baked 586x699, the real 0.838. Against the
       1280x1589 painting that is 0.921 of the artwork's height at rest and 0.961
       hovered, so the painting re-framed by 4% the moment the player hovered a
       card they had just revealed. Chrome's fan card happened to land near the
       right aspect and drifted 0.15%, which is why a Chrome-only sweep called it
       clean.
       The used size is the honest ruler: transform-free, and sub-pixel rather
       than the integer `offsetWidth` rounding that produced that 0.15%. The old
       shape gated the layout path on `reserve > 1` and fell through to the rect
       otherwise — but the oversample is a RESOLUTION knob and may not decide
       which ruler is used. Contract: tests/unit/ui/cardArtRasterRuler.test.js */
    function cardArtLayoutRect(paint) {
        if (!paint) return { width: 0, height: 0 };
        /* A COMPUTED LENGTH IS ONLY A LENGTH WHEN IT ENDS IN `px`.
           For an element that is not being rendered, `getComputedStyle`
           returns the SPECIFIED value — `auto`, or the literal `100%` this
           very art node declares for its height — and `parseFloat('100%')` is
           100, a plausible-looking pixel count that is not one. The deck
           builder's read-only tiles sample their art while the pane is still
           closed, so trusting that number baked every tile against a fake
           42x100 box. Require the unit, and otherwise fall through to the
           offset box, which is 0 for an unrendered node and therefore honest. */
        const usedLength = (value) => (
            typeof value === 'string' && value.endsWith('px')
                ? Number.parseFloat(value)
                : NaN
        );
        const view = paint.ownerDocument?.defaultView;
        if (view?.getComputedStyle) {
            const style = view.getComputedStyle(paint);
            const usedWidth = usedLength(style.width);
            const usedHeight = usedLength(style.height);
            if (usedWidth > 0 && usedHeight > 0) {
                return { width: usedWidth, height: usedHeight };
            }
        }
        const layoutWidth = Number(paint.offsetWidth);
        const layoutHeight = Number(paint.offsetHeight);
        if (layoutWidth > 0 && layoutHeight > 0) {
            return { width: layoutWidth, height: layoutHeight };
        }
        // Last resort: a node with no used size and no offset size is not being
        // laid out, so its rect is not transformed either.
        const rect = paint.getBoundingClientRect?.();
        return rect?.width > 0 && rect?.height > 0
            ? { width: rect.width, height: rect.height }
            : { width: 0, height: 0 };
    }

    function roundedRasterMetric(value) {
        return Math.round(Number(value || 0) * 1000) / 1000;
    }

    function lockedRasterProfile(paint, rect, overrides = {}) {
        return cardArtProfileForImage(paint, rect, {
            positionX: Number.isFinite(Number(paint?.dataset?.cardArtRasterPositionX))
                ? Number(paint.dataset.cardArtRasterPositionX)
                : 0.5,
            positionY: Number.isFinite(Number(paint?.dataset?.cardArtRasterPositionY))
                ? Number(paint.dataset.cardArtRasterPositionY)
                : 0.5,
            oversample: rasterReserveScaleForPaint(paint),
            dpr: window.devicePixelRatio || 1,
            forceCanvas: true,
            ...overrides,
        });
    }

    function lockedRasterRequestSignature(source, profile) {
        return [
            source,
            roundedRasterMetric(profile.displayWidth),
            roundedRasterMetric(profile.displayHeight),
            profile.fit === 'cover' ? 'cover' : 'contain',
            roundedRasterMetric(profile.positionX),
            roundedRasterMetric(profile.positionY),
            roundedRasterMetric(profile.dpr),
            roundedRasterMetric(profile.oversample),
        ].join('|');
    }

    function releaseUnusedRasterCanvas(canvas) {
        if (!canvas) return;
        try {
            canvas.width = 1;
            canvas.height = 1;
        } catch (_) {
            // Immutable test canvases and restricted implementations are safe
            // to leave for garbage collection when a stale render is rejected.
        }
    }

    /** Whether this engine's sampler has the createImageBitmap tier (Chromium). */
    function samplerHasBitmapTier(sampler = window.MoonfallRasterSampler) {
        try {
            return typeof sampler?.renderDecodedImageHighQuality === 'function'
                && sampler?.supportsBitmapDownscale?.() === true;
        } catch (_) {
            return false;
        }
    }

    function lockedRasterBitmapUpgradeOwed(paint) {
        return samplerHasBitmapTier()
            && !['bitmap', 'canvas2d'].includes(paint?.dataset?.cardArtRasterTier);
    }

    /*
     * ONE BITMAP REDUCTION PER PICTURE AND SIZE (round 15, 28 September 2026,
     * Chrome phone seat, wave D CPU profile of the Collection). The same
     * picture at the same size was reduced two or three times per open: a
     * card's normal and Stellar tiles each ran their own createImageBitmap
     * reduction (~80-200 ms of main thread apiece at 4x CPU; 19 calls for 16
     * tiles). A reduction's pixels depend only on the picture and the request
     * (the signature: source, box, crop, DPR and reserve), so one reduction
     * serves every card that shows that picture at that size:
     *   - a card asking while one is in flight joins it and gets a copy of its
     *     pixels (the last one to claim it takes the reduction's own canvas);
     *   - a card asking after one settled copies that settled canvas: a
     *     bitmap-tier canvas on the page whose request is this one and whose
     *     size is exactly this request's planned raster. A canvas that kept
     *     larger pixels for a smaller box (a shrink re-stamps its request, see
     *     lockedRasterPixelsCoverShrunkenProfile) is not the same pixels and is
     *     never copied.
     * Same pixels, never a smaller rendition. The joined reduction runs while
     * ANY card still wants it and at the urgency of the most urgent one.
     * Contract: tests/unit/ui/cards.rasterReductionReuse.test.js.
     */
    const sharedBitmapReductions = new Map();

    function plannedRasterSize(authority, profile) {
        const plan = window.MoonfallRasterSampler?.computeRasterPlan;
        if (typeof plan !== 'function' || !(authority?.naturalWidth > 0) || !(authority?.naturalHeight > 0)) {
            return null;
        }
        try {
            const planned = plan({
                sourceWidth: authority.naturalWidth,
                sourceHeight: authority.naturalHeight,
                displayWidth: profile.displayWidth,
                displayHeight: profile.displayHeight,
                fit: profile.fit,
                positionX: profile.positionX,
                positionY: profile.positionY,
                dpr: profile.dpr,
                oversample: profile.oversample,
            });
            return planned.direct ? null : planned;
        } catch (_) {
            return null;
        }
    }

    /** A settled bitmap-tier canvas holding exactly this request's pixels, or null. */
    function settledBitmapTwin(paint, source, authority, profile) {
        if (!source || !samplerHasBitmapTier()) return null;
        const planned = plannedRasterSize(authority, profile);
        if (!planned) return null;
        const signature = lockedRasterRequestSignature(source, profile);
        const candidates = /** @type {NodeListOf<HTMLCanvasElement>} */ (document.querySelectorAll(
            '.card-image > canvas.card-art-paint[data-card-art-raster-tier="bitmap"]'
        ));
        for (const canvas of candidates) {
            if (
                canvas !== paint
                && canvas.dataset.cardArtSource === source
                && canvas.dataset.cardArtRasterRequest === signature
                && canvas.width === planned.targetWidth
                && canvas.height === planned.targetHeight
            ) return canvas;
        }
        return null;
    }

    /** The twin's pixels as a render result a caller can install, or null. */
    function copyOfSettledTwin(paint, source, authority, profile) {
        const twin = settledBitmapTwin(paint, source, authority, profile);
        const copy = twin ? copySettledCanvas(twin) : null;
        return copy ? { canvas: copy, sampled: true, fallback: false, tier: 'bitmap' } : null;
    }

    /*
     * ONE RASTER, AT THE BITMAP TIER, WHEN THE PICTURE IS ALREADY DECODED
     * (replay 01a175f7 deck pager, 27 October 2026). A card's first lock is a
     * canvas-2D raster that, on Chromium, the bitmap reduction replaces a
     * moment later. With the picture decoded off the main thread
     * (decodePicture), that canvas-2D raster was the dearest work left in a
     * deck builder page turn: its stepped reduction's last draw cost ~170 ms
     * a card at 6x CPU (1.4 s of an 8-card page), for pixels nobody kept. So
     * on that tier a card whose picture arrived decoded is rastered once, by
     * the bitmap reduction, which reads the same held decode (4-9 ms a step).
     * Resolves the rendered canvas, or null (no bitmap tier, a direct plan,
     * no longer wanted): the caller then locks canvas-2D as before.
     */
    async function bitmapTierRasterFor(paint, source, authority, profile, isCurrent) {
        const sampler = window.MoonfallRasterSampler;
        if (!authority || !samplerHasBitmapTier(sampler)) return null;
        const signature = lockedRasterRequestSignature(source, profile);
        let rendered = null;
        try {
            rendered = copyOfSettledTwin(paint, source, authority, profile)
                || await reduceBitmapShared(sampler, authority, profile, signature, {
                    isCurrent,
                    urgent: () => rasterWorkIsUrgent(paint),
                });
        } catch (_) {
            rendered = null;
        }
        if (!rendered?.canvas || rendered.tier !== 'bitmap' || !isCurrent()) {
            releaseUnusedRasterCanvas(rendered?.canvas);
            return null;
        }
        rendered.canvas.dataset.cardArtRasterTier = 'bitmap';
        return rendered;
    }

    /**
     * The bitmap reduction for `signature`, shared by every paint asking for
     * it while it is in flight. `consumer` = { isCurrent(), urgent() }.
     */
    function reduceBitmapShared(sampler, authority, profile, signature, consumer) {
        let entry = sharedBitmapReductions.get(signature);
        if (!entry) {
            entry = { consumers: new Set([consumer]), waiting: 1, handedOriginal: false, promise: null };
            const current = entry;
            const anyone = (test) => {
                for (const each of current.consumers) {
                    if (test(each)) return true;
                }
                return false;
            };
            sharedBitmapReductions.set(signature, entry);
            let task;
            try {
                task = sampler.renderDecodedImageHighQuality(authority, profile, {
                    // Each step of the reduction is a raster step (see MAIN-THREAD
                    // SLICES FOR RASTER WORK), unsliced for a popup the player is
                    // waiting for (AN ENLARGED CARD ... IS NOT SLICED), judged at
                    // each step: a refresh begun before the card was enlarged
                    // joins its urgency from the next step on.
                    schedule: (work) => scheduleRasterStep(work, {
                        urgent: anyone((each) => each.urgent()),
                    }),
                    // A STEP NOBODY WANTS IS NEVER RUN (raster-sampler.ts).
                    isCurrent: () => anyone((each) => each.isCurrent()),
                });
            } catch (error) {
                task = Promise.reject(error);
            }
            entry.promise = Promise.resolve(task);
            // Retired before any claim runs: a paint asking once it has
            // settled copies the settled canvas instead (settledBitmapTwin).
            const retire = () => {
                if (sharedBitmapReductions.get(signature) === current) sharedBitmapReductions.delete(signature);
            };
            entry.promise.then(retire, retire);
        } else {
            entry.consumers.add(consumer);
            entry.waiting += 1;
        }
        return claimSharedReduction(entry);
    }

    async function claimSharedReduction(entry) {
        let rendered;
        try {
            rendered = await entry.promise;
        } finally {
            entry.waiting -= 1;
        }
        if (!rendered?.canvas) return rendered;
        // Claims run in the order the paints joined; every one before the
        // last copies the pixels while the reduction's own canvas is intact.
        if (entry.waiting === 0 && !entry.handedOriginal) {
            entry.handedOriginal = true;
            return rendered;
        }
        const copy = copySettledCanvas(rendered.canvas);
        return copy
            ? { ...rendered, canvas: copy }
            : { ...rendered, canvas: null, sampled: false, fallback: true };
    }

    /*
     * THE RESIZE FRAME PAINTS THE NEW SIZE, CHEAPLY (wave F, 28 September
     * 2026). On the bitmap tier the resize frame's canvas-2D raster of the
     * whole picture (1280x1589 per card) had come to dominate a resize: ~30 ms
     * of main thread per card at 4x CPU, 200-530 ms frames for a 13-tile page,
     * drawImage 1.5 s per there-and-back resize (Chrome phone seat CPU
     * profiles, wave E), for pixels the bitmap reduction replaces a moment
     * later. So the resize frame refits the card's OWN settled pixels to the
     * new raster size instead: one small canvas draw, cropped to the new shape
     * exactly as `object-fit: cover` showed the old canvas in the new box
     * before round 14 (never blurrier than the baseline's frame), and the exact
     * reduction lands in place as before (the same final pixels).
     * A refit is always drawn from the last EXACT pixels, held here until the
     * exact ones land, never from an earlier refit: a window dragged through
     * many sizes resamples once, not once per frame.
     * Contract: tests/unit/ui/cards.resizeFrameIsCheap.test.js.
     */
    function releaseRefitSource(paint) {
        const held = refitSourcePixels.get(paint);
        if (!held) return;
        refitSourcePixels.delete(paint);
        releaseUnusedRasterCanvas(held);
    }

    function rasterPosition(value) {
        const position = Number(value);
        return Number.isFinite(position) ? Math.max(0, Math.min(1, position)) : 0.5;
    }

    /**
     * What a refit draws: the last exact pixels (held, or copied now from the
     * paint), the part of them the new shape shows, and the new raster size.
     * Null when the paint has no pixels to refit.
     */
    function refitPlanFor(paint, planned, profile) {
        const held = refitSourcePixels.get(paint) || null;
        const exact = held || (paint.width > 1 && paint.height > 1 ? copySettledCanvas(paint) : null);
        if (!exact) return null;
        // The cover crop of the new shape, taken from the old pixels at the
        // raster's own position; a contain raster keeps the picture's shape.
        let sx = 0;
        let sy = 0;
        let sw = exact.width;
        let sh = exact.height;
        if (profile.fit === 'cover') {
            const targetAspect = planned.targetWidth / planned.targetHeight;
            const heldAspect = sw / sh;
            if (targetAspect > heldAspect) {
                sh = sw / targetAspect;
                sy = (exact.height - sh) * rasterPosition(profile.positionY);
            } else if (targetAspect < heldAspect) {
                sw = sh * targetAspect;
                sx = (exact.width - sw) * rasterPosition(profile.positionX);
            }
        }
        return {
            exact,
            held: Boolean(held),
            crop: { sx, sy, sw, sh, width: planned.targetWidth, height: planned.targetHeight },
        };
    }

    /**
     * Re-raster a locked paint at its current geometry. `resized`: the paint is
     * on screen and its box or DPR just changed (the resize frame), so its old
     * pixels are the wrong size NOW — see "THE RESIZE FRAME PAINTS THE NEW
     * SIZE" below.
     */
    async function refreshLockedCardRaster(paint, profileOverrides = {}, { resized = false } = {}) {
        const source = paint?.dataset?.cardArtSource || '';
        const paintLock = paint?.dataset?.cardArtPaintLock || '';
        if (!paint?.isConnected || !source || !paintLock) return false;
        const rect = cardArtLayoutRect(paint);
        if (!(rect?.width > 0) || !(rect?.height > 0)) return false;
        const profile = lockedRasterProfile(paint, rect, profileOverrides);
        const requestSignature = lockedRasterRequestSignature(source, profile);
        /* Geometry alone no longer decides "already current". The synchronous
           first lock renders through canvas-2D; on engines where the bitmap
           reduction is measurably better the paint is still owed one upgrade at
           identical geometry, and a pure geometry check would skip it forever.
           The tier is stamped on the installed canvas, so this settles after
           exactly one upgrade rather than re-rendering on every refresh. */
        const bitmapUpgradeOwed = lockedRasterBitmapUpgradeOwed(paint);
        if (!bitmapUpgradeOwed) {
            if (paint.dataset.cardArtRasterRequest === requestSignature) return true;
            if (lockedRasterPixelsCoverShrunkenProfile(paint, source, profile)) {
                // Firefox can deliver a popup's first ResizeObserver sample a
                // frame late with a one-layout-pixel smaller content box. The
                // locked bitmap already contains every pixel the new profile
                // needs, so preserve that immutable paint and truthfully stamp
                // the smaller geometry instead of flashing a replacement.
                stampLockedRasterProfile(paint, source, profile);
                return true;
            }
            if (lockedRasterPixelsStandForProfile(paint, source, profile)) return true;
        }

        // ResizeObserver, the global DPR/resize monitor, and an interaction
        // presentation can all discover the same owed raster in adjacent
        // frames. They are observers of one immutable request, not competing
        // generations: join the active task when its signature is identical.
        // A genuinely different geometry/profile still starts a new generation
        // below and invalidates the obsolete work through ownsRefresh().
        const activeRefresh = lockedRasterRefreshTasks.get(paint);
        if (
            activeRefresh?.requestSignature === requestSignature
            && activeRefresh.source === source
            && activeRefresh.paintLock === paintLock
        ) {
            return activeRefresh.promise;
        }

        const refresh = (async () => {
            let generation = (lockedRasterRefreshGenerations.get(paint) || 0) + 1;
            lockedRasterRefreshGenerations.set(paint, generation);
            const ownsRefresh = () => (
                lockedRasterRefreshGenerations.get(paint) === generation
                && paint.isConnected
                && paint.dataset.cardArtSource === source
                && paint.dataset.cardArtPaintLock === paintLock
            );
            const sampler = window.MoonfallRasterSampler;
            const privateAuthority = lockedRasterAuthorities.get(paint) || null;
            let authority = privateAuthority
                || sampler?.getDecodedImage?.(source)
                || null;
            if (!authority && typeof sampler?.prepareDecodedImage === 'function') {
                let prepared = false;
                try {
                    prepared = await sampler.prepareDecodedImage(source);
                } catch (_) {
                    prepared = false;
                }
                if (!prepared || !ownsRefresh()) return false;
                authority = sampler.getDecodedImage?.(source) || null;
            }
            if (
                !authority
                || !ownsRefresh()
                || typeof sampler?.renderDecodedImage !== 'function'
            ) {
                return false;
            }

            const currentRect = cardArtLayoutRect(paint);
            if (!(currentRect?.width > 0) || !(currentRect?.height > 0)) return false;
            const currentProfile = lockedRasterProfile(
                paint,
                currentRect,
                profileOverrides,
            );
            const currentSignature = lockedRasterRequestSignature(source, currentProfile);
            if (currentSignature !== requestSignature) return false;
            /* THE RESIZE FRAME PAINTS THE NEW SIZE (round 14, 26 September
               2026). On Chromium the re-raster below is the createImageBitmap
               reduction, sliced through MAIN-THREAD SLICES FOR RASTER WORK, and
               it landed 300-450 ms after a resize; until then the old, smaller
               bitmap was stretched over the grown box (the phone seat's
               Collection kept a 237x282 backing for a box needing 260x311:
               blurry). The baseline painted within ~150 ms. So a paint whose
               pixels were baked for another box is repainted NOW, in this
               frame, with the canvas-2D raster at the new size — what Firefox
               and WebKit always paint here — and the bitmap tier then replaces
               it in place when its reduction lands. Without the bitmap tier,
               this raster is the final one. */
            const bitmapTier = samplerHasBitmapTier(sampler);
            if (
                resized
                && paint.tagName === 'CANVAS'
                && paint.dataset.cardArtRasterRequest !== currentSignature
            ) {
                let first = null;
                // THE RESIZE FRAME PAINTS THE NEW SIZE, CHEAPLY: on the bitmap
                // tier, a refit of the pixels already settled, sized exactly
                // as the reduction will be.
                const planned = bitmapTier ? plannedRasterSize(authority, currentProfile) : null;
                const refit = planned ? refitPlanFor(paint, planned, currentProfile) : null;
                const refitted = refit && installSettledCanvasPaint(
                    paint,
                    source,
                    refit.exact,
                    paintLock,
                    paint.dataset.cardArtQuality === 'direct' ? 'direct' : 'enhanced',
                    {
                        profile: currentProfile,
                        authority,
                        retainAuthority: Boolean(privateAuthority),
                        refit,
                    },
                );
                if (refit && !refitted && !refit.held) releaseUnusedRasterCanvas(refit.exact);
                if (!refitted) {
                    try {
                        first = sampler.renderDecodedImage(authority, currentProfile);
                    } catch (_) {
                        first = null;
                    }
                }
                if (refitted || (first?.canvas && (first.sampled || first.materialized))) {
                    if (!refitted && !bitmapTier) first.canvas.dataset.cardArtRasterTier = 'canvas2d';
                    const installed = refitted || installSettledCanvasPaint(
                        paint,
                        source,
                        first.canvas,
                        paintLock,
                        first.sampled ? 'enhanced' : 'direct',
                        {
                            profile: currentProfile,
                            authority,
                            retainAuthority: Boolean(privateAuthority),
                        },
                    );
                    // A paint that could not keep its node was swapped: the
                    // new canvas owes its own upgrade (its first resize sample).
                    if (!installed || !bitmapTier || !paint.isConnected) return installed;
                    // The install moved this paint on a generation; the upgrade
                    // below is still this refresh's own.
                    generation = lockedRasterRefreshGenerations.get(paint);
                } else {
                    releaseUnusedRasterCanvas(first?.canvas);
                    if (!bitmapTier) return false;
                }
            }
            /* Still wanted: this refresh owns the paint, and the paint still
               has the box it is reducing for (a card resized again, hidden or
               gone is not). Asked as each queued step starts: A STEP NOBODY
               WANTS IS NEVER RUN (raster-sampler.ts). One layout read per step
               of ~100 ms of reduction; the same read the final check makes. */
            const stillWanted = () => {
                if (!ownsRefresh()) return false;
                const rect = cardArtLayoutRect(paint);
                return rect?.width > 0 && rect?.height > 0 && lockedRasterRequestSignature(
                    source,
                    lockedRasterProfile(paint, rect, profileOverrides),
                ) === currentSignature;
            };
            let rendered;
            try {
                /* Prefer the engine-gated `createImageBitmap` reduction where it is
                   measurably better (Chromium: 4.12x more retained detail on real
                   card art; neutral on WebKit, harmful on Firefox, so the sampler
                   gates it). This is the one raster entry point that is ALREADY
                   async and already re-validates geometry on both sides of its
                   await, which is why the upgrade lives here rather than in the
                   synchronous first lock. Resolves to the plain canvas-2D result on
                   every engine and every failure path. On the bitmap tier ONE
                   BITMAP REDUCTION PER PICTURE AND SIZE serves every card: a
                   settled twin is copied, one in flight is joined. */
                if (typeof sampler.renderDecodedImageHighQuality !== 'function') {
                    rendered = sampler.renderDecodedImage(authority, currentProfile);
                } else if (bitmapTier) {
                    rendered = copyOfSettledTwin(paint, source, authority, currentProfile)
                        || await reduceBitmapShared(sampler, authority, currentProfile, currentSignature, {
                            isCurrent: stillWanted,
                            urgent: () => rasterWorkIsUrgent(paint),
                        });
                } else {
                    rendered = await sampler.renderDecodedImageHighQuality(authority, currentProfile, {
                        schedule: (work) => scheduleRasterStep(work, { urgent: rasterWorkIsUrgent(paint) }),
                        isCurrent: stillWanted,
                    });
                }
            } catch (_) {
                return false;
            }
            if (!ownsRefresh()) {
                releaseUnusedRasterCanvas(rendered?.canvas);
                return false;
            }
            if (!rendered?.canvas || (!rendered.sampled && !rendered.materialized)) {
                releaseUnusedRasterCanvas(rendered?.canvas);
                return false;
            }
            // Carry the achieved tier onto the canvas that is about to replace this
            // paint, so the upgrade guard above settles instead of looping.
            rendered.canvas.dataset.cardArtRasterTier = rendered.tier === 'bitmap'
                ? 'bitmap'
                : 'canvas2d';

            const finalRect = cardArtLayoutRect(paint);
            const finalProfile = finalRect?.width > 0 && finalRect?.height > 0
                ? lockedRasterProfile(paint, finalRect, profileOverrides)
                : null;
            if (
                !finalProfile
                || lockedRasterRequestSignature(source, finalProfile) !== currentSignature
            ) {
                releaseUnusedRasterCanvas(rendered.canvas);
                return false;
            }
            return installSettledCanvasPaint(
                paint,
                source,
                rendered.canvas,
                paintLock,
                rendered.sampled ? 'enhanced' : 'direct',
                {
                    profile: finalProfile,
                    authority,
                    // Initial reserve canvases stay governed by the sampler's
                    // bounded decoded-source LRU. Only a paint explicitly pinned
                    // by a real interaction carries private authority forward.
                    retainAuthority: Boolean(privateAuthority),
                },
            );
        })();
        lockedRasterRefreshTasks.set(paint, {
            requestSignature,
            source,
            paintLock,
            promise: refresh,
        });
        try {
            return await refresh;
        } finally {
            if (lockedRasterRefreshTasks.get(paint)?.promise === refresh) {
                lockedRasterRefreshTasks.delete(paint);
            }
        }
    }

    function scheduleLockedRasterRefresh(target = null) {
        if (target?.dataset?.cardArtPaintLock) {
            pendingLockedRasterRefreshTargets.add(target);
        } else {
            // Window resize, orientation, and DPR changes can affect every
            // connected card even when ResizeObserver has not delivered yet.
            refreshAllLockedRasters = true;
        }
        if (lockedRasterRefreshFrame !== null) return;
        const schedule = typeof window.requestAnimationFrame === 'function'
            ? window.requestAnimationFrame.bind(window)
            : (callback) => setTimeout(callback, 16);
        lockedRasterRefreshFrame = true;
        const frame = schedule(() => {
            lockedRasterRefreshFrame = null;
            /* The card frames' paints, and every paint a ResizeObserver
               queued: a locked picture outside a card frame (the Deck
               Builder's deck list) was dropped whenever its delivery came
               before the window's own refresh, as in WebKit. */
            const paints = refreshAllLockedRasters
                ? Array.from(new Set([
                    ...document.querySelectorAll(
                        '.card-image > .card-art-paint[data-card-art-paint-lock]'
                    ),
                    ...pendingLockedRasterRefreshTargets,
                ]))
                : Array.from(pendingLockedRasterRefreshTargets);
            refreshAllLockedRasters = false;
            pendingLockedRasterRefreshTargets.clear();
            paints.forEach((paint) => {
                void refreshLockedCardRaster(paint, {}, { resized: true });
            });
        });
        if (lockedRasterRefreshFrame !== null) lockedRasterRefreshFrame = frame;
    }

    function armLockedRasterDprMonitor() {
        if (typeof window.matchMedia !== 'function') return;
        if (lockedRasterDprQuery && lockedRasterDprListener) {
            if (typeof lockedRasterDprQuery.removeEventListener === 'function') {
                lockedRasterDprQuery.removeEventListener('change', lockedRasterDprListener);
            } else {
                lockedRasterDprQuery.removeListener?.(lockedRasterDprListener);
            }
        }
        lockedRasterDprQuery = window.matchMedia(
            `(resolution: ${window.devicePixelRatio || 1}dppx)`
        );
        lockedRasterDprListener = () => {
            armLockedRasterDprMonitor();
            scheduleLockedRasterRefresh();
        };
        if (typeof lockedRasterDprQuery.addEventListener === 'function') {
            lockedRasterDprQuery.addEventListener('change', lockedRasterDprListener);
        } else {
            lockedRasterDprQuery.addListener?.(lockedRasterDprListener);
        }
    }

    function ensureLockedRasterGlobalRefresh() {
        if (lockedRasterGlobalListenersBound) return;
        lockedRasterGlobalListenersBound = true;
        window.addEventListener('resize', scheduleLockedRasterRefresh, { passive: true });
        window.addEventListener('orientationchange', scheduleLockedRasterRefresh, { passive: true });
        armLockedRasterDprMonitor();
    }

    function stopCardArtOwnerObserverWhenIdle() {
        if (trackedCardArtOwners.size > 0 || !cardArtOwnerObserver) return;
        cardArtOwnerObserver.disconnect();
        cardArtOwnerObserver = null;
    }

    function untrackCardArtOwner(paint) {
        trackedCardArtOwners.delete(paint);
        const timer = cardArtOwnerTimers.get(paint);
        if (timer !== undefined) clearTimeout(timer);
        cardArtOwnerTimers.delete(paint);
        stopCardArtOwnerObserverWhenIdle();
    }

    function sweepDetachedCardArtOwners() {
        trackedCardArtOwners.forEach((paint) => {
            // A document realm can disappear before its final queued mutation
            // record is delivered (page teardown, jsdom worker disposal). At
            // that point no presentation work is legal and global `window` /
            // `document` access inside the ordinary disposer may already be
            // gone. Release the strong owner and timer without touching the
            // retired realm; WeakMap-owned loader state then dies with it.
            if (!paint?.ownerDocument?.defaultView) {
                trackedCardArtOwners.delete(paint);
                const timer = cardArtOwnerTimers.get(paint);
                if (timer !== undefined) clearTimeout(timer);
                cardArtOwnerTimers.delete(paint);
                return;
            }
            if (paint.isConnected) {
                connectedCardArt.add(paint);
                return;
            }
            if (connectedCardArt.has(paint)) disposeCardArtImage(paint);
        });
        stopCardArtOwnerObserverWhenIdle();
    }

    function trackCardArtOwner(paint) {
        if (!paint || trackedCardArtOwners.has(paint)) return;
        trackedCardArtOwners.add(paint);
        if (paint.isConnected) connectedCardArt.add(paint);
        if (!cardArtOwnerObserver && typeof MutationObserver === 'function') {
            cardArtOwnerObserver = new MutationObserver(sweepDetachedCardArtOwners);
            cardArtOwnerObserver.observe(document.documentElement || document, {
                childList: true,
                subtree: true,
            });
        }
        const timer = setTimeout(() => {
            cardArtOwnerTimers.delete(paint);
            if (paint.isConnected) connectedCardArt.add(paint);
            else disposeCardArtImage(paint);
        }, CARD_IMAGE_DETACHED_GRACE_MS);
        timer.unref?.();
        cardArtOwnerTimers.set(paint, timer);
    }

    function releaseDetachedCardArtAfterGrace(img, framesLeft = CARD_ART_ATTACHMENT_GRACE_FRAMES) {
        const schedule = typeof window.requestAnimationFrame === 'function'
            ? (callback) => window.requestAnimationFrame(callback)
            : (callback) => setTimeout(callback, 16);
        schedule(() => {
            if (!observedCardArt.has(img)) return;
            if (img.isConnected) {
                connectedCardArt.add(img);
                return;
            }
            if (framesLeft > 1) {
                releaseDetachedCardArtAfterGrace(img, framesLeft - 1);
                return;
            }
            disposeCardArtImage(img);
        });
    }

    function ensureCardArtResizeObserver(img) {
        if (!img) return;
        trackCardArtOwner(img);
        if (typeof window.ResizeObserver !== 'function' || observedCardArt.has(img)) return;

        if (!cardArtResizeObserver) {
            cardArtResizeObserver = new window.ResizeObserver((entries) => {
                entries.forEach((entry) => {
                    const target = entry.target;
                    if (!target.isConnected) {
                        // Once a paint has been connected, a detached resize
                        // delivery is its disposal signal. This applies to
                        // locked canvases too: ResizeObserver retains observed
                        // nodes strongly, so leaving a removed lock observed
                        // would leak every retired hand/board generation.
                        if (connectedCardArt.has(target)) {
                            disposeCardArtImage(target);
                        }
                        return;
                    }
                    if (target.dataset.cardArtPaintLock) {
                        connectedCardArt.add(target);
                        // ResizeObserver can fire repeatedly while responsive
                        // layout settles. Render locked physical pixels once
                        // per animation frame from the latest geometry so a
                        // resize cannot churn intermediate backing canvases.
                        scheduleLockedRasterRefresh(target);
                        return;
                    }
                    connectedCardArt.add(target);
                    if (target.dataset.cardArtDirectOnly === 'true') return;
                    if (target.dataset.cardArtLoadPending === 'true') return;
                    const rect = cardArtLayoutRect(target);
                    if (!(rect.width > 0) || !(rect.height > 0)) return;
                    const sourceUrl = target.dataset.cardArtSource;
                    if (sourceUrl) {
                        void enhanceCardArt(target, sourceUrl, cardArtProfileForImage(target, rect));
                    }
                });
            });
        }
        observedCardArt.add(img);
        cardArtResizeObserver.observe(img);
        releaseDetachedCardArtAfterGrace(img);
    }

    function stopCardArtResizeObservation(img) {
        if (!img || !observedCardArt.has(img)) return;
        cardArtResizeObserver?.unobserve?.(img);
        observedCardArt.delete(img);
        connectedCardArt.delete(img);
    }

    function measuredCardArtProfile(img, profile) {
        if (Number(profile.displayWidth) > 0 && Number(profile.displayHeight) > 0) {
            return profile;
        }
        const rect = cardArtLayoutRect(img);
        if (!(rect?.width > 0) || !(rect?.height > 0)) return null;
        return {
            ...profile,
            displayWidth: rect.width,
            displayHeight: rect.height,
        };
    }

    /**
     * Apply the shared DPR-aware, stepped high-quality raster path to card art.
     * The authoritative direct URL remains painted on every unsupported or
     * failed path, and the sampler owns stale-request protection.
     */
    function enhanceCardArt(img, sourceUrl, profile = {}) {
        if (!img || !sourceUrl || !sanitizeDirectMediaUrl(sourceUrl)) {
            return Promise.resolve({
                url: sourceUrl || '',
                sampled: false,
                fallback: true,
                applied: false,
                stale: false,
            });
        }
        const priorDeclaredSource = img.dataset.cardArtSource || '';
        const priorSampleSource = img.dataset.cardArtSampleSource || '';
        const priorQuality = img.dataset.cardArtQuality || '';
        const priorPaint = img.getAttribute('src') || '';
        const priorEnhancedPaint = priorQuality === 'enhanced'
            && priorPaint.startsWith('data:')
            && priorSampleSource === sourceUrl;
        const priorDirectPaint = priorQuality === 'direct'
            && priorPaint === sourceUrl
            && (!priorDeclaredSource || priorDeclaredSource === sourceUrl);
        const priorSettledPaint = priorEnhancedPaint || priorDirectPaint;
        const priorPaintStillActive = () => img.getAttribute('src') === priorPaint;

        const samplingProfile = cardArtProfile({
            ...profile,
            applyGuard: () => !img.dataset.cardArtPaintLock,
        });
        img.classList.add('card-art-paint');
        if (img.dataset.cardArtSampleSource !== sourceUrl) {
            delete img.dataset.cardArtSampleSource;
        }
        img.dataset.cardArtSource = sourceUrl;
        img.dataset.cardArtFit = samplingProfile.fit;
        ensureCardArtResizeObserver(img);

        // A data URL is only evidence for the authority source that produced
        // it. When a DOM image is reused for another card, keeping those old
        // pixels while the new sample loads would show the wrong card and,
        // worse, let a fallback relabel them as the new source. Switch to the
        // new direct authority behind the ordinary loading gate immediately.
        if (
            !priorSettledPaint
            && priorPaint
            && priorPaint !== CARD_IMAGE_PLACEHOLDER
            && priorPaint !== sourceUrl
        ) {
            img.dataset.cardArtQuality = 'pending';
            img.classList.add('card-art-loading');
            useSharedArtRequestMode(img, sourceUrl);
            img.setAttribute('src', sourceUrl);
        }
        const measuredProfile = measuredCardArtProfile(img, samplingProfile);
        if (!measuredProfile) {
            if (typeof window.ResizeObserver === 'function') {
                if (!priorSettledPaint) img.dataset.cardArtQuality = 'pending';
                return Promise.resolve({
                    url: img.getAttribute('src') || sourceUrl,
                    sampled: false,
                    fallback: false,
                    applied: false,
                    stale: false,
                    deferred: true,
                });
            }
            img.dataset.cardArtQuality = priorSettledPaint ? priorQuality : 'degraded';
            revealSettledCardArt(img);
            return Promise.resolve({
                url: img.getAttribute('src') || sourceUrl,
                sampled: false,
                fallback: true,
                applied: false,
                stale: false,
            });
        }

        const sampler = window.MoonfallRasterSampler;
        if (!sampler || typeof sampler.enhanceImage !== 'function') {
            img.dataset.cardArtQuality = priorSettledPaint ? priorQuality : 'degraded';
            revealSettledCardArt(img);
            return Promise.resolve({
                url: sourceUrl,
                sampled: false,
                fallback: true,
                applied: false,
                stale: false,
            });
        }

        if (!priorSettledPaint) img.dataset.cardArtQuality = 'pending';
        const enhancementGeneration = {};
        const readinessGeneration = pendingCardArtReadiness.get(img) || null;
        const loaderGeneration = img.dataset.cardArtLoaderId || '';
        cardArtEnhancementGenerations.set(img, enhancementGeneration);
        const ownsEnhancement = () => (
            cardArtEnhancementGenerations.get(img) === enhancementGeneration
            && (pendingCardArtReadiness.get(img) || null) === readinessGeneration
            && (img.dataset.cardArtLoaderId || '') === loaderGeneration
        );
        let task;
        try {
            task = sampler.enhanceImage(img, sourceUrl, measuredProfile);
        } catch (_) {
            task = Promise.reject(_);
        }
        const settlement = Promise.resolve(task).then((result) => {
            if (!ownsEnhancement() || img.dataset.cardArtSource !== sourceUrl || result?.stale) {
                return { ...result, applied: false, stale: true };
            }
            // A popup can inherit truthful pixels from its source card while
            // the sampler request created with the detached popup is still in
            // flight. Firefox commonly resolves that older fallback one frame
            // later. Read the live authority here: the old request must never
            // downgrade transferred direct/enhanced paint to `degraded`.
            const liveQuality = img.dataset.cardArtQuality || '';
            const livePaint = img.getAttribute('src') || '';
            const liveSettledPaint = (liveQuality === 'enhanced'
                && livePaint.startsWith('data:')
                && img.dataset.cardArtSampleSource === sourceUrl)
                || (liveQuality === 'direct' && livePaint === sourceUrl);
            const retainedEnhancedPaint = result?.retained && priorEnhancedPaint;
            if ((result?.sampled && result?.applied) || retainedEnhancedPaint) {
                img.dataset.cardArtQuality = 'enhanced';
                img.dataset.cardArtSampleSource = sourceUrl;
            } else if (!result?.fallback && !result?.sampled && result?.applied) {
                img.dataset.cardArtQuality = 'direct';
                delete img.dataset.cardArtSampleSource;
            } else if (liveSettledPaint) {
                img.dataset.cardArtQuality = liveQuality;
            } else if (priorSettledPaint && priorPaintStillActive()) {
                img.dataset.cardArtQuality = priorQuality;
            } else {
                delete img.dataset.cardArtSampleSource;
                if (livePaint === sourceUrl && !(result?.retained && !priorEnhancedPaint)) {
                    img.dataset.cardArtQuality = 'degraded';
                } else {
                    img.dataset.cardArtQuality = 'pending';
                    img.classList.add('card-art-loading');
                    useSharedArtRequestMode(img, sourceUrl);
                    img.setAttribute('src', sourceUrl);
                }
            }
            if (revealSettledCardArt(img)) return result;
            return settleUndecodedCardArtPaint(
                img,
                sourceUrl,
                measuredProfile,
                result,
                ownsEnhancement,
            );
        }).catch(() => {
            if (ownsEnhancement() && img.dataset.cardArtSource === sourceUrl) {
                if (priorSettledPaint && priorPaintStillActive()) {
                    img.dataset.cardArtQuality = priorQuality;
                } else {
                    img.dataset.cardArtQuality = 'degraded';
                    delete img.dataset.cardArtSampleSource;
                    if (img.getAttribute('src') !== sourceUrl) {
                        useSharedArtRequestMode(img, sourceUrl);
                        img.setAttribute('src', sourceUrl);
                    }
                }
                if (!revealSettledCardArt(img)) {
                    return settleUndecodedCardArtPaint(
                        img,
                        sourceUrl,
                        measuredProfile,
                        {
                            url: sourceUrl,
                            sampled: false,
                            fallback: true,
                            applied: false,
                            stale: false,
                        },
                        ownsEnhancement,
                    );
                }
            }
            return {
                url: sourceUrl,
                sampled: false,
                fallback: true,
                applied: false,
                stale: img.dataset.cardArtSource !== sourceUrl,
            };
        });
        pendingCardArtTasks.set(img, settlement);
        settlement.then(
            () => {
                if (pendingCardArtTasks.get(img) === settlement) pendingCardArtTasks.delete(img);
                if (cardArtEnhancementGenerations.get(img) === enhancementGeneration) {
                    cardArtEnhancementGenerations.delete(img);
                }
            },
            () => {
                if (pendingCardArtTasks.get(img) === settlement) pendingCardArtTasks.delete(img);
                if (cardArtEnhancementGenerations.get(img) === enhancementGeneration) {
                    cardArtEnhancementGenerations.delete(img);
                }
            },
        );
        return settlement;
    }

    function refreshCardRaster(cardElement) {
        if (cardElement?.dataset?.cardDirectArtOnly === 'true') return Promise.resolve(false);
        const img = cardElement?.querySelector?.('.card-image > img.card-art-paint');
        const sourceUrl = img?.dataset?.cardArtSource;
        if (
            !img
            || !sourceUrl
            || !img.isConnected
            || img.dataset.cardArtPaintLock
        ) return Promise.resolve(false);
        const rect = cardArtLayoutRect(img);
        if (!(rect.width > 0) || !(rect.height > 0)) return Promise.resolve(false);
        return enhanceCardArt(img, sourceUrl, cardArtProfileForImage(img, rect));
    }

    /* `pixels`: the picture already decoded off the main thread
       (MoonfallRasterSampler.decodePicture), read by the first draw instead of
       the element. */
    function lockCardRaster(cardElement, reason = 'interaction', pixels = null) {
        const img = cardElement?.querySelector?.('.card-image > img.card-art-paint');
        if (!img) {
            const canvas = cardElement?.querySelector?.('.card-image > canvas.card-art-paint');
            if (!canvas || !(canvas.width > 0 && canvas.height > 0)) return false;
            if (!canvas.dataset.cardArtPaintLock || reason !== 'initial') {
                canvas.dataset.cardArtPaintLock = reason;
            }
            const cachedAuthority = window.MoonfallRasterSampler?.getDecodedImage?.(
                canvas.dataset.cardArtSource || ''
            );
            if (
                cachedAuthority
                && reason !== 'initial'
                && rasterReserveScaleForPaint(canvas) > 1
            ) {
                lockedRasterAuthorities.set(canvas, cachedAuthority);
            }
            ensureCardArtResizeObserver(canvas);
            ensureLockedRasterGlobalRefresh();
            return true;
        }
        if (img.dataset.cardArtPaintLock) {
            if (hasDrawableCardArtPixels(img)) return true;
            // A stale lock left on a newly assigned data URL must not block the
            // URL's eventual decode or the sampler callback that can replace it.
            delete img.dataset.cardArtPaintLock;
        }
        const sourceUrl = img.dataset.cardArtSource;
        const paint = img.getAttribute('src') || '';
        if (!sourceUrl || !paint) return false;
        const sampler = window.MoonfallRasterSampler;

        // Pointer entry can beat the asynchronous sampler by a frame after a
        // board rebuild. If browser decoding has already produced authority
        // pixels, synchronously prepare this surface's declared raster reserve
        // before any enlargement. The discarded IMG is locked as well so its in-flight
        // sampler cannot perform a late mutation off-DOM.
        //
        // COMPLETE, not merely sized: the canvas drawn here is final. A picture
        // still downloading already knows its size from its header, and WebKit
        // draws nothing from an unfinished WebP — a transparent canvas settled
        // as 'enhanced' that nothing on WebKit ever redraws (measured 25
        // September 2026: 1-3 Collection cards per page on the WebKit phone
        // seat, once the <img> joined the preloader's in-flight request).
        if (
            img.dataset.cardArtQuality !== 'enhanced'
            && img.complete === true
            && img.naturalWidth > 0
            && img.naturalHeight > 0
            && typeof sampler?.renderDecodedImage === 'function'
        ) {
            const rect = cardArtLayoutRect(img);
            if (rect.width > 0 && rect.height > 0) {
                // Draw from the sampler's detached decoded copy when there is
                // one: WebKit sometimes hands a canvas nothing from the card's
                // own on-screen <img> (raster-sampler drewNothing), and
                // remembering that <img> would replace the reliable copy.
                const decodedCopy = sampler.getDecodedImage?.(sourceUrl) || null;
                const profile = cardArtProfileForImage(img, rect, { forceCanvas: true });
                // A twin already settled at this exact request is these very
                // pixels, at the bitmap tier: copied, neither drawn nor reduced
                // again (ONE BITMAP REDUCTION PER PICTURE AND SIZE).
                const twin = copyOfSettledTwin(img, sourceUrl, decodedCopy || img, profile);
                const rendered = twin || sampler.renderDecodedImage(
                    decodedCopy || img,
                    pixels ? { ...profile, pixels } : profile,
                );
                if (rendered?.canvas && (rendered.sampled || rendered.materialized)) {
                    /* The on-screen <img> becomes the authority only once it
                       has DRAWN. Remembered before the draw, an element that
                       handed the canvas nothing stayed in the sampler's store,
                       and the enhancement after this lock sampled it into an
                       empty data URL settled as the picture: solid black
                       tiles on the WebKit phone (round 14, 26 September
                       2026). Not remembered, that enhancement draws a
                       detached copy of the same URL. */
                    if (!decodedCopy && !twin) sampler.rememberDecodedImage?.(sourceUrl, img);
                    img.dataset.cardArtPaintLock = reason;
                    return installSettledCanvasPaint(
                        img,
                        sourceUrl,
                        rendered.canvas,
                        reason,
                        rendered.sampled ? 'enhanced' : 'direct',
                        {
                            profile,
                            authority: decodedCopy || img,
                            retainAuthority: reason !== 'initial' && profile.oversample > 1,
                        },
                    );
                }
            }
        }

        // A hover is a soft lock: when a higher-quality replacement is already
        // running, let it finish and retry before enlargement. Drag/action
        // callers may still freeze the best currently settled paint because
        // their interaction must remain synchronous.
        if (reason === 'hover' && pendingCardArtTasks.has(img)) return false;

        // Never turn an undecoded/pending URL into a permanent lock. That would
        // reject the sampler result and preserve the exact soft browser
        // downscale that the anti-aliasing path exists to replace.
        if (!isSettledCardArt(img)) return false;

        // Preserve truthful quality (`enhanced`, `direct`, or `degraded`) while
        // freezing the final node. A lock is a continuity guarantee, never a
        // quality upgrade.
        img.dataset.cardArtPaintLock = reason;
        const cachedAuthority = sampler?.getDecodedImage?.(sourceUrl);
        if (
            cachedAuthority
            && reason !== 'initial'
            && rasterReserveScaleForPaint(img) > 1
        ) {
            lockedRasterAuthorities.set(img, cachedAuthority);
        }
        ensureCardArtResizeObserver(img);
        ensureLockedRasterGlobalRefresh();
        revealSettledCardArt(img);
        return true;
    }

    /* The first lock of a card whose picture arrived decoded, at the bitmap
       tier (bitmapTierRasterFor). Resolves like lockCardRaster; false leaves
       the card to it. */
    async function lockCardRasterAtBitmapTier(cardElement, reason) {
        const sampler = window.MoonfallRasterSampler;
        const img = cardElement?.querySelector?.('.card-image > img.card-art-paint');
        const source = img?.dataset?.cardArtSource || '';
        if (
            !img
            || !source
            || img.dataset.cardArtPaintLock
            || img.dataset.cardArtQuality === 'enhanced'
            || !hasDrawableCardArtPixels(img)
        ) return false;
        const rect = cardArtLayoutRect(img);
        if (!(rect.width > 0) || !(rect.height > 0)) return false;
        const profile = cardArtProfileForImage(img, rect, { forceCanvas: true });
        const signature = lockedRasterRequestSignature(source, profile);
        const decodedCopy = sampler?.getDecodedImage?.(source) || null;
        const stillWanted = () => {
            if (!img.isConnected || img.dataset.cardArtPaintLock || img.dataset.cardArtSource !== source) return false;
            const now = cardArtLayoutRect(img);
            return now.width > 0 && now.height > 0 && lockedRasterRequestSignature(
                source,
                cardArtProfileForImage(img, now, { forceCanvas: true }),
            ) === signature;
        };
        const rendered = await bitmapTierRasterFor(img, source, decodedCopy || img, profile, stillWanted);
        if (!rendered) return false;
        if (!decodedCopy) sampler?.rememberDecodedImage?.(source, img);
        img.dataset.cardArtPaintLock = reason;
        return installSettledCanvasPaint(
            img,
            source,
            rendered.canvas,
            reason,
            rendered.sampled ? 'enhanced' : 'direct',
            {
                profile,
                authority: decodedCopy || img,
                retainAuthority: reason !== 'initial' && profile.oversample > 1,
            },
        );
    }

    function releaseHoverRasterAuthority(cardElement) {
        const paint = cardElement?.querySelector?.('.card-image > .card-art-paint');
        if (!paint || paint.dataset.cardArtPaintLock !== 'hover') return;
        // In-place cards declare future enlargement explicitly. Retain their
        // decoder while that DOM card lives so a viewport change can rebuild
        // its bounded reserve; WeakMap ownership ends with the card node.
        if (declaredRasterReserveForPaint(paint) > 1) return;
        lockedRasterAuthorities.delete(paint);
        transientLockedRasterAuthorities.delete(paint);
    }

    function retainTransientRasterAuthority(paint, authority) {
        if (!paint || !authority) return;
        lockedRasterAuthorities.set(paint, authority);
        transientLockedRasterAuthorities.add(paint);
        setTimeout(() => {
            if (!transientLockedRasterAuthorities.has(paint)) return;
            transientLockedRasterAuthorities.delete(paint);
            lockedRasterAuthorities.delete(paint);
        }, 0);
    }

    async function ensureStaticTransferAuthority(cardElement) {
        const paint = cardElement?.querySelector?.('.card-image > .card-art-paint');
        if (!paint || rasterReserveScaleForPaint(paint) > 1) return true;
        const source = paint.dataset.cardArtSource || '';
        if (!source) return false;
        const ownsPaint = () => (
            cardElement.isConnected
            && paint.isConnected
            && cardElement.querySelector?.('.card-image > .card-art-paint') === paint
            && paint.dataset.cardArtSource === source
        );
        if (!ownsPaint()) return false;
        const currentPaint = paint.getAttribute?.('src') || '';
        if (
            paint.tagName === 'IMG'
            && paint.naturalWidth > 0
            && paint.naturalHeight > 0
            && currentPaint === source
        ) return true;

        const sampler = window.MoonfallRasterSampler;
        let authority = sampler?.getDecodedImage?.(source) || null;
        if (!authority && typeof sampler?.prepareDecodedImage === 'function') {
            let prepared = false;
            try {
                prepared = await sampler.prepareDecodedImage(source);
            } catch (_) {
                prepared = false;
            }
            if (!prepared || !ownsPaint()) return false;
            authority = sampler.getDecodedImage?.(source) || null;
        }
        if (!authority || !ownsPaint()) return false;
        // Pin only through the caller's immediate transfer. A zero-delay
        // cleanup also covers hover cancellation before the popup is built.
        retainTransientRasterAuthority(paint, authority);
        return true;
    }

    // A pinned static-transfer authority lets the popup builders reuse decoded
    // pixels synchronously, but it is an optimization: a successful lock is the
    // interaction contract, so pin failure must never poison the result.
    async function pinStaticTransferAuthority(cardElement) {
        try {
            await ensureStaticTransferAuthority(cardElement);
        } catch (_) { /* popup settlement re-samples from source instead */ }
    }

    function cardPaintLockedNow(cardElement) {
        const paint = cardElement?.querySelector?.('.card-image > .card-art-paint');
        return Boolean(paint?.dataset?.cardArtPaintLock);
    }

    async function prepareCardRasterForInteraction(cardElement, reason = 'hover') {
        if (lockCardRaster(cardElement, reason)) {
            await pinStaticTransferAuthority(cardElement);
            // A re-render can replace the locked paint while the pin was in
            // flight; readiness must describe the card's current paint node,
            // so a lost generation falls through to settle the replacement.
            if (cardPaintLockedNow(cardElement)) return true;
        }
        let img = cardElement?.querySelector?.('.card-image > img.card-art-paint');
        if (!img) return false;
        // ResizeObserver and deferred card construction can leave a connected,
        // measurable image between registration and its first sampler task.
        // An interaction at that exact boundary must start the missing work,
        // rather than consuming the user's first hover without a preview.
        let settlement = pendingCardArtTasks.get(img) || refreshCardRaster(cardElement);
        while (settlement) {
            await settlement;
            if (lockCardRaster(cardElement, reason)) {
                await pinStaticTransferAuthority(cardElement);
                if (cardPaintLockedNow(cardElement)) return true;
            }

            // A ResizeObserver delivery may have superseded the first request
            // while it was settling. Join that newer authority task instead of
            // freezing pending pixels or recursively starting another sample.
            img = cardElement?.querySelector?.('.card-image > img.card-art-paint');
            if (!img) return false;
            const latestSettlement = pendingCardArtTasks.get(img);
            if (!latestSettlement || latestSettlement === settlement) return false;
            settlement = latestSettlement;
        }
        return false;
    }

    // A source URL being warm does not make a fresh IMG node drawable, and it
    // says nothing about final-geometry raster settlement. Each loader owns a
    // per-node readiness promise which resolves only when this exact paint is
    // terminal (decoded direct/enhanced/degraded pixels, installed canvas, or
    // an explicit source-less unavailable-art terminal). Presentation consumers may join
    // it without treating the preloader's bounded warm gate as art failure.
    function beginCardArtReadiness(img, loaderId) {
        const previous = pendingCardArtReadiness.get(img);
        if (previous && !previous.settled) {
            previous.settled = true;
            previous.resolve(false);
        }
        let resolve;
        const promise = new Promise((done) => { resolve = done; });
        const readiness = { loaderId, promise, resolve, settled: false };
        pendingCardArtReadiness.set(img, readiness);
        return readiness;
    }

    function settleCardArtReadiness(img, ready) {
        const readiness = pendingCardArtReadiness.get(img);
        if (!readiness || readiness.settled) return;
        readiness.settled = true;
        readiness.resolve(ready === true);
    }

    function hasDrawableCardArtPixels(paint) {
        if (!paint) return false;
        if (paint.tagName === 'CANVAS') return paint.width > 0 && paint.height > 0;
        // Complete, not merely sized (see lockCardRaster): a picture still
        // downloading is not settled, and locking it would reject the sampler's
        // canvas when it lands.
        return paint.complete !== false && paint.naturalWidth > 0 && paint.naturalHeight > 0;
    }

    function isSettledCardArt(img, loadedEvent = false) {
        return ['direct', 'enhanced', 'degraded'].includes(img?.dataset?.cardArtQuality)
            && (loadedEvent || hasDrawableCardArtPixels(img));
    }

    function revealSettledCardArt(img, loadedEvent = false) {
        if (!img || !isSettledCardArt(img, loadedEvent)) return false;
        img.classList.remove('card-art-loading');
        settleCardArtReadiness(img, true);
        return true;
    }

    // Hand instanceIds from the previous render, for the draw-in animation.
    // null = no baseline yet (first hydration / new game) → never animate.
    let lastHandIds = null;

    function resetHandTracking() {
        lastHandIds = null;
        // A new game holds no enemy card back for a traveller of the last one,
        // and no hand still waits for the last one's deal.
        heldOpponentDepartures.clear();
        lastOpponentHand = null;
        releaseHandHold({ cancel: true });
        handHoldWaivedFor = null;
    }

    function applyArtMaterialize(img) {
        const src = img && (img.dataset.cardArtSource || img.getAttribute('src'));
        if (!src) return;
        img.classList.add('card-art-loading');
    }

    function settleUndecodedCardArtPaint(
        img,
        sourceUrl,
        profile,
        result,
        ownsGeneration = () => true,
    ) {
        const sampler = window.MoonfallRasterSampler;
        const currentPaint = img?.getAttribute?.('src') || '';
        let phase = img?.dataset?.cardArtQuality === 'enhanced'
            && currentPaint.startsWith('data:')
            ? 'sample'
            : 'direct';

        if (!ownsGeneration()) {
            return Promise.resolve({ ...result, applied: false, stale: true });
        }

        // The sampler has already decoded the authority source by the time it
        // returns a sampled URL. Prefer a ready canvas immediately so the card
        // never depends on a second data-URL decode just to become visible.
        if (
            ownsGeneration()
            && phase === 'sample'
            && typeof sampler?.renderCachedImage === 'function'
        ) {
            let rendered;
            try {
                rendered = sampler.renderCachedImage(sourceUrl, {
                    ...profile,
                    forceCanvas: true,
                });
            } catch (_) {
                rendered = null;
            }
            if (rendered?.canvas && (rendered.sampled || rendered.materialized)) {
                if (installSettledCanvasPaint(
                    img,
                    sourceUrl,
                    rendered.canvas,
                    'settled-sampler',
                    rendered.sampled ? 'enhanced' : 'direct',
                    {
                        profile: { ...profile, forceCanvas: true },
                        authority: sampler.getDecodedImage?.(sourceUrl) || null,
                    },
                )) return Promise.resolve(result);
            }
            releaseUnusedRasterCanvas(rendered?.canvas);
        }

        return new Promise((resolve) => {
            let timer = null;
            let finished = false;
            let expectedPaint = currentPaint;

            const ownsPaint = () => (
                ownsGeneration()
                && img.dataset.cardArtSource === sourceUrl
                && img.getAttribute('src') === expectedPaint
            );

            const cleanup = () => {
                if (timer !== null) clearTimeout(timer);
                timer = null;
                img.removeEventListener('load', onLoad);
                img.removeEventListener('error', onError);
            };

            const finish = (value = result) => {
                if (finished) return;
                finished = true;
                cleanup();
                resolve(value);
            };

            const armTimeout = () => {
                if (timer !== null) clearTimeout(timer);
                timer = setTimeout(
                    onTimeout,
                    phase === 'sample' ? CARD_IMAGE_DECODE_TIMEOUT_MS : CARD_IMAGE_LOAD_TIMEOUT_MS,
                );
            };

            const showRetryingWell = () => {
                if (!ownsPaint()) {
                    finish({ ...result, applied: false, stale: true });
                    return;
                }
                const controller = cardArtLoaderControllers.get(img);
                if (controller && controller.loaderId === img.dataset.cardArtLoaderId) {
                    controller.scheduleRetry();
                } else {
                    // Settled-transfer clones intentionally have no loader
                    // controller. If their derived paint and decoded cache
                    // both fail later, reattach the exact declared source;
                    // absence of a controller is not absence of art identity.
                    void loadCardArtImage(img, sourceUrl, profile, {
                        directArtOnly: img.dataset.cardArtDirectOnly === 'true',
                    }).catch(() => undefined);
                }
                finish({ ...result, applied: false, fallback: true, retrying: true });
            };

            const recoverAuthority = () => {
                if (!ownsPaint()) {
                    finish({ ...result, applied: false, stale: true });
                    return;
                }
                const authority = sampler?.getDecodedImage?.(sourceUrl) || null;
                let rendered = null;
                if (authority && typeof sampler?.renderDecodedImage === 'function') {
                    try {
                        rendered = sampler.renderDecodedImage(authority, {
                            ...profile,
                            forceCanvas: true,
                        });
                    } catch (_) {
                        rendered = null;
                    }
                }
                if (rendered?.canvas && (rendered.sampled || rendered.materialized)) {
                    const installed = installSettledCanvasPaint(
                        img,
                        sourceUrl,
                        rendered.canvas,
                        'settled-recovery',
                        rendered.sampled ? 'enhanced' : 'direct',
                        {
                            profile: { ...profile, forceCanvas: true },
                            authority,
                        },
                    );
                    if (installed) {
                        finish(result);
                        return;
                    }
                }
                releaseUnusedRasterCanvas(rendered?.canvas);
                phase = 'direct';
                img.dataset.cardArtQuality = 'degraded';
                img.classList.add('card-art-loading');
                expectedPaint = sourceUrl;
                useSharedArtRequestMode(img, expectedPaint);
                img.setAttribute('src', expectedPaint);
                armTimeout();
            };

            function onLoad() {
                if (!ownsPaint()) {
                    finish({ ...result, applied: false, stale: true });
                    return;
                }
                if (phase === 'direct') img.dataset.cardArtQuality = 'degraded';
                if (revealSettledCardArt(img, true)) {
                    finish(phase === 'direct'
                        ? { ...result, sampled: false, fallback: true, applied: true }
                        : result);
                    return;
                }
                recoverAuthority();
            }

            function onError() {
                if (!ownsPaint()) {
                    finish({ ...result, applied: false, stale: true });
                    return;
                }
                if (phase === 'sample') recoverAuthority();
                else showRetryingWell();
            }

            function onTimeout() {
                if (!ownsPaint()) {
                    finish({ ...result, applied: false, stale: true });
                    return;
                }
                if (phase === 'sample') recoverAuthority();
                else showRetryingWell();
            }

            img.addEventListener('load', onLoad);
            img.addEventListener('error', onError);
            armTimeout();
        });
    }

    /* A RE-RASTER REPAINTS THE CANVAS ALREADY ON SCREEN (round 14, 26
       September 2026, board-resize). Swapping in a new canvas node on every
       resize, DPR change or tier upgrade took the old one off the page and put
       a new one on: three pictures read as loading in front for 4-12 s on
       Firefox and WebKit, 21 reappear flickers and three pop-ins per resize.
       The new pixels are copied 1:1 into the existing canvas instead: resized
       and drawn in one task, so no frame ever shows it cleared, and a same-size
       blit, so not one pixel of the render changes on the way. Returns
       'painted', 'untouched' (no 2D context: nothing was done), or 'cleared'
       (the copy failed after the resize cleared it: the caller must swap).
       `crop` (a resize refit, refitPlanFor): the part of `rendered` to draw,
       scaled to the new raster size, in the same one draw. */
    function repaintCanvasInPlace(target, rendered, crop = null) {
        let context = null;
        try {
            context = target.getContext('2d');
        } catch (_) {
            context = null;
        }
        if (!context || typeof context.drawImage !== 'function') return 'untouched';
        try {
            if (crop) {
                target.width = crop.width;
                target.height = crop.height;
                context.imageSmoothingEnabled = true;
                context.imageSmoothingQuality = 'high';
                context.drawImage(rendered, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, crop.width, crop.height);
                return 'painted';
            }
            target.width = rendered.width;
            target.height = rendered.height;
            context.drawImage(rendered, 0, 0);
            return 'painted';
        } catch (_) {
            return 'cleared';
        }
    }

    function installSettledCanvasPaint(
        next,
        source,
        canvas,
        paintLock,
        quality = 'enhanced',
        {
            profile = null,
            authority = null,
            retainAuthority = null,
            rasterMetadataSource = null,
            // A resize refit (refitPlanFor): `canvas` is the last exact
            // pixels, drawn into `next` through the plan's crop at the new
            // size, and held for the next refit. Only ever in place.
            refit = null,
        } = {},
    ) {
        if (!next || !source || !canvas) return false;
        const rect = cardArtLayoutRect(next);
        const measuredProfile = profile || (
            rect?.width > 0 && rect?.height > 0
                ? lockedRasterProfile(next, rect)
                : null
        );
        // A canvas being re-rastered keeps its node (repaintCanvasInPlace);
        // anything else — an <img>, a canvas without a 2D context — is
        // replaced by the render as before.
        const repaint = next.tagName === 'CANVAS' && next !== canvas
            ? repaintCanvasInPlace(next, canvas, refit?.crop || null)
            : 'untouched';
        const inPlace = repaint === 'painted';
        // A refit's source is the old pixels at the old size: it never takes
        // the seat itself. The caller paints the new size another way (a
        // 'cleared' canvas is repainted in this same task).
        if (refit && !inPlace) return false;
        const paint = inPlace ? next : canvas;
        if (refit) {
            refitSourcePixels.set(next, canvas);
        } else {
            // Exact pixels have landed: nothing is refitted from the old ones.
            releaseRefitSource(next);
        }
        if (!inPlace) {
            canvas.className = next.className;
            canvas.style.cssText = next.style.cssText;
        } else if (refit) {
            // Refitted pixels have reached no tier: the exact reduction is owed.
            delete paint.dataset.cardArtRasterTier;
        } else if (canvas.dataset.cardArtRasterTier) {
            paint.dataset.cardArtRasterTier = canvas.dataset.cardArtRasterTier;
        } else {
            // The tier belongs to the pixels: a render that reached none (the
            // resize frame's canvas-2D raster) never keeps the old one's.
            delete paint.dataset.cardArtRasterTier;
        }
        paint.classList.add('card-art-paint', 'card-art-canvas');
        paint.classList.remove('card-art-loading');
        paint.dataset.cardArtSource = source;
        paint.dataset.cardArtFit = next.dataset.cardArtFit;
        paint.dataset.cardArtQuality = quality;
        paint.dataset.cardArtPaintLock = paintLock;
        paint.dataset.cardArtRasterKey = `${source}|${paint.width}x${paint.height}`;
        if (rasterMetadataSource) {
            // An exact canvas copy inherits the request that produced those
            // immutable pixels. Never stamp the current DPR/geometry onto an
            // older bitmap: the retained request mismatch is what makes the
            // global resize/DPR monitor replace it with correctly sized pixels.
            [
                'cardArtRasterRequest',
                'cardArtRasterOversample',
                'cardArtRasterPositionX',
                'cardArtRasterPositionY',
                'cardArtRasterDisplayWidth',
                'cardArtRasterDisplayHeight',
            ].forEach((key) => {
                const value = rasterMetadataSource.dataset?.[key];
                if (value) paint.dataset[key] = value;
            });
        } else if (measuredProfile) {
            paint.dataset.cardArtRasterOversample = `${measuredProfile.oversample}`;
            paint.dataset.cardArtRasterPositionX = `${measuredProfile.positionX}`;
            paint.dataset.cardArtRasterPositionY = `${measuredProfile.positionY}`;
            paint.dataset.cardArtRasterDisplayWidth = `${measuredProfile.displayWidth}`;
            paint.dataset.cardArtRasterDisplayHeight = `${measuredProfile.displayHeight}`;
            paint.dataset.cardArtRasterRequest = lockedRasterRequestSignature(
                source,
                measuredProfile,
            );
        } else if (next.dataset.cardArtRasterRequest && !inPlace) {
            canvas.dataset.cardArtRasterRequest = next.dataset.cardArtRasterRequest;
            canvas.dataset.cardArtRasterOversample = next.dataset.cardArtRasterOversample;
            canvas.dataset.cardArtRasterPositionX = next.dataset.cardArtRasterPositionX;
            canvas.dataset.cardArtRasterPositionY = next.dataset.cardArtRasterPositionY;
            canvas.dataset.cardArtRasterDisplayWidth = next.dataset.cardArtRasterDisplayWidth;
            canvas.dataset.cardArtRasterDisplayHeight = next.dataset.cardArtRasterDisplayHeight;
        }
        const accessibleName = next.alt || next.getAttribute?.('aria-label') || '';
        if (accessibleName && !inPlace) {
            canvas.setAttribute('role', 'img');
            canvas.setAttribute('aria-label', accessibleName);
        }
        /* A decorative picture stays decorative: the Deck Builder's deck list
           names each card beside its `alt="" aria-hidden="true"` art, and the
           canvas that took that art's place put an unnamed image beside the
           name (27 October 2026). */
        if (!inPlace && (next.getAttribute?.('aria-hidden') === 'true'
            || (!accessibleName && next.getAttribute?.('alt') === ''))) {
            canvas.setAttribute('aria-hidden', 'true');
        }
        const rasterOversample = rasterMetadataSource
            ? Number(rasterMetadataSource.dataset?.cardArtRasterOversample || 1)
            : Number(measuredProfile?.oversample || 1);
        const declaredReserve = declaredRasterReserveForPaint(next);
        const shouldRetainAuthority = typeof retainAuthority === 'boolean'
            ? retainAuthority
            : rasterOversample > 1 || declaredReserve > 1;
        const retainedAuthority = shouldRetainAuthority
            ? authority
                || lockedRasterAuthorities.get(next)
                || window.MoonfallRasterSampler?.getDecodedImage?.(source)
                || null
            : null;
        if (retainedAuthority) lockedRasterAuthorities.set(paint, retainedAuthority);
        else if (inPlace) lockedRasterAuthorities.delete(paint);
        lockedRasterRefreshGenerations.set(next, (
            lockedRasterRefreshGenerations.get(next) || 0
        ) + 1);
        if (inPlace) {
            // Same node: its ResizeObserver, owner tracking and readiness
            // carry on. The render was a scratch buffer (a refit's is held).
            if (!refit) releaseUnusedRasterCanvas(canvas);
            ensureLockedRasterGlobalRefresh();
            settleCardArtReadiness(next, true);
            return true;
        }
        if (observedCardArt.has(next) && cardArtResizeObserver) {
            cardArtResizeObserver.unobserve(next);
            observedCardArt.delete(next);
            connectedCardArt.delete(next);
        }
        untrackCardArtOwner(next);
        next.replaceWith(canvas);
        if (next.tagName === 'CANVAS') {
            // The replacement already owns the authoritative source/profile.
            // Retire the previous high-DPR backing store immediately instead
            // of waiting for GC after every resize or DPR transition.
            releaseUnusedRasterCanvas(next);
        }
        ensureCardArtResizeObserver(canvas);
        ensureLockedRasterGlobalRefresh();
        settleCardArtReadiness(next, true);
        return true;
    }

    function copyCanvasPaints(sourceRoot, cloneRoot) {
        if (!sourceRoot || !cloneRoot) return 0;
        const sourceCanvases = sourceRoot.matches?.('canvas.card-art-paint')
            ? [sourceRoot]
            : Array.from(sourceRoot.querySelectorAll?.('canvas.card-art-paint') || []);
        const cloneCanvases = cloneRoot.matches?.('canvas.card-art-paint')
            ? [cloneRoot]
            : Array.from(cloneRoot.querySelectorAll?.('canvas.card-art-paint') || []);
        let copiedCount = 0;
        sourceCanvases.forEach((sourceCanvas, index) => {
            const cloneCanvas = cloneCanvases[index];
            if (!cloneCanvas || !(sourceCanvas.width > 0 && sourceCanvas.height > 0)) return;
            try {
                cloneCanvas.width = sourceCanvas.width;
                cloneCanvas.height = sourceCanvas.height;
                const context = cloneCanvas.getContext('2d');
                if (!context) return;
                context.imageSmoothingEnabled = true;
                context.imageSmoothingQuality = 'high';
                context.drawImage(sourceCanvas, 0, 0);
                cloneCanvas.classList.remove('card-art-loading');
                copiedCount += 1;
            } catch (_) {
                // The ordinary source URL remains the renderer's fallback when
                // a restricted canvas implementation cannot be copied.
            }
        });
        return copiedCount;
    }

    /* `pixels`: the picture already decoded off the main thread
       (decodeWarmedCardPicture), read by the draw instead of the decoded copy. */
    function settleCachedRasterPaint(cardElement, profileOverrides = {}, pixels = null) {
        const next = cardElement?.querySelector?.('.card-image > img.card-art-paint');
        const source = next?.dataset?.cardArtSource || '';
        if (!next || !source) return false;
        /* THE LAYOUT BOX, not the painted one. `object-fit` resolves against the
           content box, so that is the box the bake must match; a card inside a
           3D flip (the whole card-pack fan) reports a PROJECTED rect here and
           bakes its crop to a shape the card does not have. */
        const rect = cardArtLayoutRect(next);
        if (!(rect.width > 0 && rect.height > 0)) return false;
        if (typeof window.MoonfallRasterSampler?.renderCachedImage !== 'function') return false;
        // The element has already been scaled to its final popup geometry.
        // In-place reserves belong only to cards that will enlarge later;
        // a popup is already measured at final geometry, so applying the
        // source card's reserve again would multiply pixel area needlessly.
        const renderProfile = cardArtProfileForImage(next, rect, {
            ...profileOverrides,
            oversample: Number.isFinite(profileOverrides.oversample)
                ? Math.max(1, profileOverrides.oversample)
                : 1,
            forceCanvas: true,
        });
        // A twin settled at this exact request is copied (ONE BITMAP
        // REDUCTION PER PICTURE AND SIZE): no canvas-2D raster, no upgrade.
        const rendered = copyOfSettledTwin(
            next,
            source,
            window.MoonfallRasterSampler?.getDecodedImage?.(source) || null,
            renderProfile,
        ) || window.MoonfallRasterSampler.renderCachedImage(
            source,
            pixels ? { ...renderProfile, pixels } : renderProfile,
        );
        if (!rendered?.canvas || (!rendered.sampled && !rendered.materialized)) return false;
        return installSettledCanvasPaint(
            next,
            source,
            rendered.canvas,
            'settled-cache',
            rendered.sampled ? 'enhanced' : 'direct',
            {
                profile: renderProfile,
                authority: window.MoonfallRasterSampler?.getDecodedImage?.(source) || null,
            },
        );
    }

    /* settleCachedRasterPaint at the bitmap tier, for a warmed picture already
       decoded off the main thread (bitmapTierRasterFor). False leaves the card
       to settleCachedRasterPaint. */
    async function settleCachedRasterPaintAtBitmapTier(cardElement, profileOverrides = {}) {
        const sampler = window.MoonfallRasterSampler;
        const next = cardElement?.querySelector?.('.card-image > img.card-art-paint');
        const source = next?.dataset?.cardArtSource || '';
        const authority = source ? sampler?.getDecodedImage?.(source) || null : null;
        if (!next || !authority) return false;
        const profileFor = (rect) => cardArtProfileForImage(next, rect, {
            ...profileOverrides,
            oversample: Number.isFinite(profileOverrides.oversample)
                ? Math.max(1, profileOverrides.oversample)
                : 1,
            forceCanvas: true,
        });
        const rect = cardArtLayoutRect(next);
        if (!(rect.width > 0 && rect.height > 0)) return false;
        const renderProfile = profileFor(rect);
        const signature = lockedRasterRequestSignature(source, renderProfile);
        const stillWanted = () => {
            if (!next.isConnected || next.dataset.cardArtPaintLock || next.dataset.cardArtSource !== source) return false;
            const now = cardArtLayoutRect(next);
            return now.width > 0 && now.height > 0
                && lockedRasterRequestSignature(source, profileFor(now)) === signature;
        };
        const rendered = await bitmapTierRasterFor(next, source, authority, renderProfile, stillWanted);
        if (!rendered) return false;
        return installSettledCanvasPaint(
            next,
            source,
            rendered.canvas,
            'settled-cache',
            rendered.sampled ? 'enhanced' : 'direct',
            { profile: renderProfile, authority },
        );
    }

    /* THE FIRST DRAW READS A PICTURE ALREADY DECODED (see completeCandidate).
       A warmed picture is a decoded copy in the sampler's store, but on
       Chromium "decoded" there means decoded for the compositor: its first
       canvas draw still decodes it on the main thread (replay 01a175f7: six
       of a deck builder page's eight cards, ~400 ms apiece at 6x CPU). The
       copy is handed to the sampler to decode off the main thread first.
       A promise of { pixels, release } or null; null itself (no promise) when
       there is nothing to wait for, so the lock stays in this task. */
    function decodeWarmedCardPicture(cardElement) {
        const sampler = window.MoonfallRasterSampler;
        if (typeof sampler?.decodePicture !== 'function') return null;
        const paint = cardElement?.querySelector?.('.card-image > img.card-art-paint');
        const source = paint?.dataset?.cardArtSource || '';
        const warmed = source ? sampler.getDecodedImage?.(source) : null;
        if (!warmed) return null;
        try {
            return Promise.resolve(sampler.decodePicture(warmed)).catch(() => null);
        } catch (_) {
            return null;
        }
    }

    function cardVisualExpansionScale(cardElement) {
        const expansionFor = (element) => {
            const layoutWidth = Number(element?.offsetWidth);
            const layoutHeight = Number(element?.offsetHeight);
            const visual = element?.getBoundingClientRect?.();
            return Math.max(
                layoutWidth > 0 && visual?.width > 0 ? visual.width / layoutWidth : 1,
                layoutHeight > 0 && visual?.height > 0 ? visual.height / layoutHeight : 1,
            );
        };
        const paint = cardElement?.querySelector?.('.card-image > .card-art-paint');
        // Mid-flip or degenerate transforms clamp to 1; the cap guards the
        // sampler against runaway requests (real popups top out near 3.6x).
        // Fractional borders and percentage art wells can expand the paint
        // slightly farther than the rounded outer card, so both axes and both
        // boxes participate in the coverage floor.
        return Math.min(6, Math.max(
            1,
            expansionFor(cardElement),
            expansionFor(paint),
        ));
    }

    function waitForCardRasterLayoutFrame() {
        return new Promise((resolve) => {
            const schedule = typeof window.requestAnimationFrame === 'function'
                ? window.requestAnimationFrame.bind(window)
                : (callback) => setTimeout(callback, 16);
            schedule(() => resolve());
        });
    }

    function lockedRasterPixelsCoverProfile(paint, profile, { slackPx = 0 } = {}) {
        const fit = paint?.dataset?.cardArtFit === 'cover' ? 'cover' : 'contain';
        const positionX = Number(paint?.dataset?.cardArtRasterPositionX);
        const positionY = Number(paint?.dataset?.cardArtRasterPositionY);
        // `slackPx` forgives layout jitter in the DISPLAY size (CSS px), never
        // in the physical grid the bitmap must actually hold.
        const requiredWidth = Math.ceil(
            Math.max(0, Number(profile?.displayWidth || 0) - slackPx)
            * Number(profile?.dpr || 0)
            * Number(profile?.oversample || 0)
        );
        const requiredHeight = Math.ceil(
            Math.max(0, Number(profile?.displayHeight || 0) - slackPx)
            * Number(profile?.dpr || 0)
            * Number(profile?.oversample || 0)
        );
        return Boolean(
            paint?.dataset?.cardArtRasterRequest
            && fit === profile?.fit
            && Number.isFinite(positionX)
            && Number.isFinite(positionY)
            && Math.abs(positionX - Number(profile.positionX)) <= 0.001
            && Math.abs(positionY - Number(profile.positionY)) <= 0.001
            && requiredWidth > 0
            && requiredHeight > 0
            && paint.width >= requiredWidth
            && paint.height >= requiredHeight
            && lockedRasterKeepsShape(paint, fit, profile)
        );
    }

    /* PIXELS COVER A BOX ONLY IN ITS SHAPE (round 15, 28 September 2026,
       Chrome phone seat, the Collection's cards-resize and reopen). Cover art
       is cropped to its box's shape when it is baked, so a raster baked for a
       wider box still holds enough pixels for a narrower one, but not its
       crop: kept and restamped, object-fit cropped it a second time and the
       card showed its art zoomed in (a 265x282 backing for a 237x282 box after
       the grid's refit passed through 88x94 on its way back to 79x94; the
       baseline's 1.10 reopen backing ratio came from a 260x282 one). The
       backing must have the box's shape, within its own rounding. */
    function lockedRasterKeepsShape(paint, fit, profile) {
        if (fit !== 'cover') return true;
        const wantWidth = Number(profile?.displayWidth || 0);
        const wantHeight = Number(profile?.displayHeight || 0);
        if (!(wantWidth > 0) || !(wantHeight > 0) || !(paint.width > 0) || !(paint.height > 0)) return false;
        const want = wantWidth / wantHeight;
        const have = paint.width / paint.height;
        // Each backing side is rounded to a whole pixel: allow that, twice over.
        const tolerance = want * (0.005 + 1 / paint.width + 1 / paint.height);
        return Math.abs(have - want) <= tolerance;
    }

    /* LAYOUT JITTER IS NOT A RESIZE, AND AN ENLARGEMENT IS NOT ONE EITHER.
       Traced 2026-09-18 on the revealed enemy-hand card in Chrome: under the
       in-place hover's CSS zoom the art's used size is reported unzoomed, but
       it wobbles by a few hundredths of a pixel from frame to frame as the
       zoomed box snaps to device pixels (112.266 → 112.25 → 112.266). Every
       wobble changed the request signature, and every frame then decoded the
       WebP source synchronously on the main thread (50–60 ms), baked a new
       canvas and swapped it in — so the 200 ms grow painted in three or four
       steps, which the player saw as "3 discrete movements". Pixels baked for
       a display within half a CSS pixel of this one, at the same crop and
       sampling, ARE this display's pixels: they stand, and the stamp keeps
       naming the geometry they were baked for.
       On the engine path that grows the used size itself (the unit-scaling
       hover), the display genuinely climbs toward the declared reserve — which
       was baked for exactly that. While the owning engine says the card is
       enlarging (`data-card-raster-enlarging` on the card), a bitmap that still
       covers the screen's physical pixels one-to-one stands too; re-baking it
       at the reserve multiple of an already enlarged display would be the same
       decode per frame, for pixels nobody can see. A resting card whose layout
       grew (a viewport change) is not enlarging, and still rebuilds its
       reserve. */
    const LOCKED_RASTER_LAYOUT_JITTER_PX = 0.5;

    function lockedRasterPixelsStandForProfile(paint, source, profile) {
        const priorWidth = Number(paint?.dataset?.cardArtRasterDisplayWidth);
        const priorHeight = Number(paint?.dataset?.cardArtRasterDisplayHeight);
        if (!(priorWidth > 0) || !(priorHeight > 0)) return false;
        const sameSampling = paint.dataset.cardArtRasterRequest === lockedRasterRequestSignature(source, {
            ...profile,
            displayWidth: priorWidth,
            displayHeight: priorHeight,
        });
        if (!sameSampling) return false;
        const jitterOnly = Math.abs(Number(profile.displayWidth) - priorWidth) <= LOCKED_RASTER_LAYOUT_JITTER_PX
            && Math.abs(Number(profile.displayHeight) - priorHeight) <= LOCKED_RASTER_LAYOUT_JITTER_PX;
        if (jitterOnly) return true;
        const enlarging = paint.closest?.('.card')?.dataset?.cardRasterEnlarging === 'true';
        return enlarging && lockedRasterPixelsCoverProfile(
            paint,
            { ...profile, oversample: 1 },
            { slackPx: LOCKED_RASTER_LAYOUT_JITTER_PX },
        );
    }

    function lockedRasterPixelsCoverShrunkenProfile(paint, source, profile) {
        const priorWidth = Number(paint?.dataset?.cardArtRasterDisplayWidth);
        const priorHeight = Number(paint?.dataset?.cardArtRasterDisplayHeight);
        if (
            !(priorWidth > 0)
            || !(priorHeight > 0)
            || Number(profile?.displayWidth) > priorWidth
            || Number(profile?.displayHeight) > priorHeight
        ) return false;
        const priorGeometryAtCurrentSampling = lockedRasterRequestSignature(source, {
            ...profile,
            displayWidth: priorWidth,
            displayHeight: priorHeight,
        });
        return paint.dataset.cardArtRasterRequest === priorGeometryAtCurrentSampling
            && lockedRasterPixelsCoverProfile(paint, profile);
    }

    function stampLockedRasterProfile(paint, source, profile) {
        paint.dataset.cardArtRasterOversample = `${profile.oversample}`;
        paint.dataset.cardArtRasterPositionX = `${profile.positionX}`;
        paint.dataset.cardArtRasterPositionY = `${profile.positionY}`;
        paint.dataset.cardArtRasterDisplayWidth = `${profile.displayWidth}`;
        paint.dataset.cardArtRasterDisplayHeight = `${profile.displayHeight}`;
        paint.dataset.cardArtRasterRequest = lockedRasterRequestSignature(source, profile);
    }

    function restoreCardRasterReserve(cardElement) {
        const paint = cardElement?.querySelector?.(
            '.card-image > canvas.card-art-paint'
        );
        const source = paint?.dataset?.cardArtSource || '';
        const reserve = declaredRasterReserveForPaint(paint);
        if (!paint?.isConnected || !source || reserve <= 1) return reserve <= 1;
        const rect = paint.getBoundingClientRect?.();
        if (!(rect?.width > 0) || !(rect?.height > 0)) return false;
        const profile = lockedRasterProfile(paint, rect, { oversample: reserve });
        if (!lockedRasterPixelsCoverProfile(paint, profile)) {
            // Keep the current truthful bitmap visible while rebuilding the
            // declared reserve. Never relabel an undersized backing store.
            void refreshLockedCardRaster(paint, { oversample: reserve })
                .catch(() => undefined);
            return false;
        }
        stampLockedRasterProfile(paint, source, profile);
        return true;
    }

    function isCurrentLockedRasterGeometry(cardElement, profileOverrides = {}) {
        const paint = cardElement?.querySelector?.(
            '.card-image > canvas.card-art-paint'
        );
        if (
            !paint?.isConnected
            || !(paint.width > 0)
            || !(paint.height > 0)
            || !paint.dataset.cardArtPaintLock
            || !['direct', 'enhanced'].includes(paint.dataset.cardArtQuality)
        ) return false;
        const source = paint.dataset.cardArtSource || '';
        const rect = cardArtLayoutRect(paint);
        if (!source || !(rect.width > 0) || !(rect.height > 0)) return false;
        const profile = lockedRasterProfile(paint, rect, profileOverrides);
        const requestIsCurrent = paint.dataset.cardArtRasterRequest
            === lockedRasterRequestSignature(source, profile);
        // A physical popup can consume a card's declared reserve without
        // changing one output pixel: e.g. 120px × 3.5 and 420px × 1 both need
        // the same grid. Treat that immutable, same-crop overprovisioned canvas
        // as ready instead of requiring an evicted decoder to reproduce it.
        return paint.dataset.cardArtRasterKey === `${source}|${paint.width}x${paint.height}`
            && (requestIsCurrent || lockedRasterPixelsCoverProfile(paint, profile))
            && !lockedRasterBitmapUpgradeOwed(paint);
    }

    async function settleCurrentLockedRasterGeometry(
        cardElement,
        profileOverrides = {},
        { waitForLayout = false } = {},
    ) {
        if (waitForLayout) await waitForCardRasterLayoutFrame();
        if (!cardElement?.isConnected) return false;
        if (isCurrentLockedRasterGeometry(cardElement, profileOverrides)) return true;
        const paint = cardElement.querySelector('.card-image > canvas.card-art-paint');
        if (
            !paint
            || !await refreshLockedCardRaster(paint, profileOverrides)
        ) return false;
        return isCurrentLockedRasterGeometry(cardElement, profileOverrides);
    }

    /*
     * MAIN-THREAD SLICES FOR RASTER WORK. A card's downscale is synchronous
     * canvas work that costs tens of milliseconds. Settling a page of cards
     * whose art is already decoded ran every one of them back to back in a
     * single task — 1.1 s without a frame on the Collection's open (Chrome,
     * 24 September 2026), so the press could not even show it was pressed.
     * Raster steps now share slices: once a slice has run past its budget,
     * the next step waits for a later task, one step per task, in order.
     * Nothing about the pixels changes; only when they are drawn.
     */
    const RASTER_SLICE_BUDGET_MS = 12;
    // Raster work done in the current task's slice, and whether a slice is open.
    let rasterSliceSpentMs = 0;
    let rasterSliceTimer = null;
    const rasterSliceQueue = [];

    function rasterSliceNow() {
        return typeof performance !== 'undefined' && typeof performance.now === 'function'
            ? performance.now()
            : Date.now();
    }

    function openRasterSlice() {
        if (rasterSliceTimer === null) rasterSliceTimer = setTimeout(nextRasterSlice, 0);
    }

    /** Run one raster step, counting its cost against this task's slice. */
    function runRasterWork(work) {
        const startedAt = rasterSliceNow();
        try {
            return work();
        } finally {
            rasterSliceSpentMs += rasterSliceNow() - startedAt;
        }
    }

    function nextRasterSlice() {
        rasterSliceTimer = null;
        rasterSliceSpentMs = 0;
        if (rasterSliceQueue.length === 0) return;
        openRasterSlice();
        // At least one queued step per task, then more while this one has room.
        do {
            const step = rasterSliceQueue.shift();
            try {
                step.resolve(runRasterWork(step.work));
            } catch (error) {
                step.reject(error);
            }
        } while (rasterSliceQueue.length > 0 && rasterSliceSpentMs < RASTER_SLICE_BUDGET_MS);
    }

    /* AN ENLARGED CARD THE PLAYER IS WAITING FOR IS NOT SLICED (round 15, 28
       September 2026, the match's hand-card-enlarge step: Chrome phone 281 ms
       in the baseline, 822-1185 ms with the slices). The slices exist so a PAGE
       of cards settles without one long task. A popup is one card, hidden
       until its final raster lands (the card contract forbids showing the soft
       canvas-2D paint and swapping it after first paint), and on a 4x-CPU phone
       one step of its Chromium reduction spends the whole budget: every later
       step waited a task, a full rendering frame ran between them and other
       cards' queued steps went first, so the popup appeared two to four frames
       late with nothing gained on screen. A card inside `.card-hover-preview`
       (every popup surface: the battle's tap inspection, the Mulligan, the
       Collection, packs, the Exchange, Moon Dream, the Foundry, the Deck
       Builder) runs its steps at once, as before the slices; its work still
       counts against the slice, so queued page work yields after it.
       Contract: tests/unit/ui/cards.enlargedCardArrivesWhole.test.js. */
    function rasterWorkIsUrgent(element) {
        try {
            return Boolean(element?.closest?.('.card-hover-preview'));
        } catch (_) {
            return false;
        }
    }

    /**
     * A synchronous raster step: run now if this task's slice has room, or if
     * it is `urgent` (`{ now: true, value }`, exactly as if called directly),
     * or queue it for a later task (`{ now: false, later }`). The check and
     * the work are one synchronous step, so steps released in the same
     * microtask burst cannot all pass.
     */
    function rasterStep(work, { urgent = false } = {}) {
        openRasterSlice();
        if (urgent || (rasterSliceQueue.length === 0 && rasterSliceSpentMs < RASTER_SLICE_BUDGET_MS)) {
            return { now: true, value: runRasterWork(work) };
        }
        return {
            now: false,
            later: new Promise((resolve, reject) => {
                rasterSliceQueue.push({ work, resolve, reject });
            }),
        };
    }

    /** `rasterStep` as a promise, for callers that await every step anyway. */
    function scheduleRasterStep(work, options) {
        const step = rasterStep(work, options);
        return step.now ? Promise.resolve(step.value) : step.later;
    }

    async function settleCardRasterForPresentation(
        cardElement,
        card,
        {
            oversample = 1,
            warmTimeoutMs = 450,
            stableGeometry = false,
            sourceCard = null,
            coverVisualExpansion = false,
        } = {},
    ) {
        if (!cardElement?.isConnected) return false;
        // A card enlarged by a transform keeps a base-sized layout box (which
        // raster requests are keyed to once a reserve is stamped) while the
        // display grows by the scale factor, so its settled canvas truthfully
        // matches its layout yet under-covers the pixels actually shown. Call
        // sites in that position opt in to flooring the oversample at the
        // measured expansion. `applyPreviewScale` no longer needs it — popups
        // are sized physically now, so their expansion measures 1 and this
        // floor is a no-op there — but the opt-in stays because it is what
        // makes the under-coverage impossible to reintroduce silently. It is
        // opt-in, not ambient:
        // in-place surfaces (announce floats, Moon Dream decks) transfer
        // canvases stamped under other profiles, and an ambient floor
        // rewrites their signature comparisons into refresh dependencies
        // that fail without decoded authority.
        const visualExpansion = coverVisualExpansion
            ? cardVisualExpansionScale(cardElement)
            : 1;
        const reservedVisualExpansion = visualExpansion > 1 + VISUAL_EXPANSION_EPSILON
            ? Math.min(6, visualExpansion * TRANSFORM_RASTER_SETTLEMENT_RESERVE)
            : 1;
        const profile = {
            oversample: Math.max(
                Number.isFinite(oversample) ? Math.max(1, oversample) : 1,
                reservedVisualExpansion,
            ),
            forceCanvas: true,
        };
        const finishSettlement = () => settleCurrentLockedRasterGeometry(
            cardElement,
            profile,
            { waitForLayout: stableGeometry === true },
        );
        // A prepared visible source is the strongest and fastest authority for
        // a popup. Transfer it before starting a redundant warm, but still
        // revalidate after final CSS geometry below.
        if (
            sourceCard
            && transferSettledRasterPaint(sourceCard, cardElement, profile)
        ) return finishSettlement();

        const preloader = AssetPreloader;
        if (card && typeof preloader?.warmCards === 'function') {
            try {
                const warm = preloader.warmCards([card], { priority: 'high' });
                if (warm && typeof warm.then === 'function') {
                    if (typeof preloader.gateWarm === 'function') {
                        await preloader.gateWarm(warm, warmTimeoutMs);
                    } else {
                        // The interaction must never inherit the decoder's
                        // safety timeout when the bounded reveal gate is not
                        // available. Observe failure and let strict final-size
                        // settlement below decide whether reveal is safe.
                        void Promise.resolve(warm).catch(() => undefined);
                    }
                }
            } catch (_) {
                // Warming is an optimization. Settlement below remains the
                // authority and will reject the presentation if pixels cannot
                // be materialized truthfully.
            }
        }
        if (!cardElement.isConnected) return false;

        // Held through finishSettlement, whose bitmap-tier upgrade reads it
        // too (see completeCandidate).
        const decoding = decodeWarmedCardPicture(cardElement);
        const decoded = decoding ? await decoding : null;
        if (decoding && !cardElement.isConnected) {
            decoded?.release?.();
            return false;
        }
        try {
            if (
                decoded?.pixels
                && cardElement.isConnected
                && await settleCachedRasterPaintAtBitmapTier(cardElement, profile)
            ) return await finishSettlement();
            if (!cardElement.isConnected) return false;
            const firstLock = rasterStep(
                () => cardElement.isConnected && settleCachedRasterPaint(cardElement, profile, decoded?.pixels || null),
                { urgent: rasterWorkIsUrgent(cardElement) },
            );
            if (firstLock.now ? firstLock.value : await firstLock.later) return await finishSettlement();
        } finally {
            decoded?.release?.();
        }
        if (!cardElement.isConnected) return false;

        // A cache miss can precede even the source URL: asset-only cards may
        // still be resolving through Convex, or a chosen URL may still be
        // decoding. Join the exact loader generation rather than treating the
        // preloader's bounded warm gate as failure. That readiness resolves
        // only from a truthful terminal paint or explicit unavailable art.
        const pendingImage = cardElement.querySelector(
            '.card-image > img.card-art-paint'
        );
        // The explicit source-less unavailable-art state is a truthful,
        // presentable terminal (docs/asset-loading.md law 2): the face presents
        // with its frame and text, without inventing replacement artwork. Rejecting it here
        // would let announce floats and reveals silently swallow a
        // gameplay-visible event because art was missing.
        const isUnavailableArtTerminal = () => Boolean(
            pendingImage
            && pendingImage.classList.contains('card-art-unavailable')
            && !pendingImage.classList.contains('card-art-loading')
        );
        const readiness = pendingImage
            ? pendingCardArtReadiness.get(pendingImage)
            : null;
        if (readiness) {
            let ready = false;
            try {
                ready = await readiness.promise;
            } catch (_) {
                ready = false;
            }
            if (!cardElement.isConnected) return false;
            if (!ready) return isUnavailableArtTerminal();
            const settledCanvas = cardElement.querySelector(
                '.card-image > canvas.card-art-paint'
            );
            if (settledCanvas?.width > 0 && settledCanvas?.height > 0) {
                return finishSettlement();
            }
            const joinedLock = rasterStep(
                () => cardElement.isConnected && settleCachedRasterPaint(cardElement, profile),
                { urgent: rasterWorkIsUrgent(cardElement) },
            );
            if (joinedLock.now ? joinedLock.value : await joinedLock.later) return finishSettlement();
        }

        // A cache miss can still coincide with the card's own in-flight sampler
        // task. Join that authority path, then require a locked canvas or
        // another successful final-geometry materialization. Never expose an
        // unresolved IMG merely because the warm timed out.
        let prepared = false;
        try {
            prepared = await prepareCardRasterForInteraction(cardElement, 'hover');
        } catch (_) {
            prepared = false;
        }
        if (!cardElement.isConnected) return false;
        if (!prepared) return isUnavailableArtTerminal();
        const canvas = cardElement.querySelector('.card-image > canvas.card-art-paint');
        if (canvas && canvas.width > 0 && canvas.height > 0) {
            return finishSettlement();
        }
        const lastLock = rasterStep(
            () => cardElement.isConnected && settleCachedRasterPaint(cardElement, profile),
            { urgent: rasterWorkIsUrgent(cardElement) },
        );
        if (lastLock.now ? lastLock.value : await lastLock.later) return finishSettlement();
        return cardElement.isConnected && isUnavailableArtTerminal();
    }

    function waitForCardFonts(fontTimeoutMs) {
        const fonts = document.fonts;
        if (!fonts) return Promise.resolve(true);

        const timeoutMs = Number.isFinite(fontTimeoutMs)
            ? Math.max(0, fontTimeoutMs)
            : CARD_FACE_FONT_TIMEOUT_MS;
        let readiness;
        try {
            // Do not inherit an unrelated page font's load. These are the
            // faces a card front actually paints: name, description, and the
            // mana numeral's own face, whose metrics place the digit.
            if (typeof fonts.load === 'function') {
                readiness = Promise.all([
                    fonts.load('700 16px "Spectral"'),
                    fonts.load('500 16px "Spectral"'),
                    fonts.load(MANA_NUMERAL_FONT, '0123456789'),
                ]);
            } else if (fonts.status === 'loaded') {
                return Promise.resolve(true);
            } else if (fonts.ready && typeof fonts.ready.then === 'function') {
                readiness = fonts.ready;
            } else {
                return Promise.resolve(true);
            }
        } catch (_) {
            return Promise.resolve(false);
        }
        return new Promise((resolve) => {
            let settled = false;
            let timeoutId = null;
            const finish = (ready) => {
                if (settled) return;
                settled = true;
                if (timeoutId !== null) clearTimeout(timeoutId);
                resolve(ready === true);
            };
            timeoutId = setTimeout(() => finish(false), timeoutMs);
            Promise.resolve(readiness).then(
                () => finish(true),
                () => finish(false),
            );
        });
    }

    /* INK, NOT FONT BOXES. A description line's client rect is its FONT box —
       Spectral's is 1.52em against a 1.2 line height — and the room above a
       capital or below the baseline paints nothing, so only ink says whether
       `.card-desc` (overflow: hidden) cuts a letter. Measured 2026-09-25 on
       real catalog texts: the fitter's integer comparison and its 1px floor
       left long descriptions losing up to 2.3px of their letters at phone to
       laptop card sizes in every engine, and this gate refused those enemy
       summons their flight. True when the first line's ink rises above the box
       or the last line's sinks below it by more than `tolerance` pixels. */
    let descriptionInkContext;
    function descriptionInkEscapes(descElement, tolerance = 0.25) {
        if (typeof document.createRange !== 'function') return false;
        const box = descElement.getBoundingClientRect?.();
        if (!(box?.height > 0)) return false;
        const range = document.createRange();
        range.selectNodeContents(descElement);
        const rects = Array.from(range.getClientRects?.() || [])
            .filter((rect) => rect.width > 0 && rect.height > 0);
        if (!rects.length) return false;
        const top = rects.reduce((a, b) => (b.top < a.top ? b : a));
        const bottom = rects.reduce((a, b) => (b.bottom > a.bottom ? b : a));
        // Font boxes inside the box: the ink is too.
        if (top.top >= box.top - tolerance && bottom.bottom <= box.bottom + tolerance) return false;
        if (descriptionInkContext === undefined) {
            try { descriptionInkContext = document.createElement('canvas').getContext('2d'); } catch (_) { descriptionInkContext = null; }
        }
        const context = descriptionInkContext;
        const layout = CardDescEngraving.measure(descElement);
        if (!context || typeof context.measureText !== 'function' || !layout?.lines?.length) return false;
        const style = window.getComputedStyle(descElement);
        context.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        const first = context.measureText(layout.lines[0]);
        const last = context.measureText(layout.lines[layout.lines.length - 1]);
        const fontBox = first.fontBoundingBoxAscent + first.fontBoundingBoxDescent;
        if (!(fontBox > 0) || !Number.isFinite(first.actualBoundingBoxAscent)) return false;
        const inkTop = top.top + Math.max(0, first.fontBoundingBoxAscent - first.actualBoundingBoxAscent)
            * (top.height / fontBox);
        const inkBottom = bottom.bottom - Math.max(0, last.fontBoundingBoxDescent - last.actualBoundingBoxDescent)
            * (bottom.height / fontBox);
        return inkTop < box.top - tolerance || inkBottom > box.bottom + tolerance;
    }

    function cardTextHasPaintGeometry(cardElement) {
        const elements = cardElement?.querySelectorAll?.(
            '.card-cost, .card-name, .card-desc, .card-status'
        );
        if (!elements) return false;
        const contains = (outer, inner) => inner.left >= outer.left - 1
            && inner.right <= outer.right + 1
            && inner.top >= outer.top - 1
            && inner.bottom <= outer.bottom + 1;
        let measuredTextNodes = 0;
        for (const element of elements) {
            if (!(element.textContent || '').trim()) continue;
            measuredTextNodes += 1;
            const box = element.getBoundingClientRect?.();
            if (!(box?.width > 0) || !(box?.height > 0)) return false;
            const range = document.createRange?.();
            if (!range) return false;
            range.selectNodeContents(element);
            const ink = range.getBoundingClientRect?.();
            const lineRects = range.getClientRects?.();
            /* AN EXPLICIT `max-height` IS A DECLARATION OF INTENT, NOT A MISS.
               This gate asks whether the face has PAINTED, and `enemy-replay`
               cancels the whole summon glide when it says no — so "the text
               does not all fit" has to mean "the layout is not settled yet",
               never "the designer bounded this box".

               `.card-status` is the one card text element with a cap
               (`max-height: 50.5% - 0.25u`, cards.css), and it needs one:
               `card.status` is free-form storyteller output split on `;` with
               no length limit, and uncapped the plate climbed over the mana
               cost and the card's own top edge. Without this branch a single
               long status would fail containment and `scrollHeight`, and every
               enemy Character on that board would appear with no animation at
               all — the exact "a readiness gate deleted the feature" failure
               this file already carries a warning about.

               A capped box still has to prove its text painted where it
               belongs: ink exists, it is contained ACROSS the box, and it
               STARTS inside it. Only the deliberately clipped tail — which
               overflows the bottom, since the plate lays its lines out from
               its own top edge — is forgiven. */
            const clipped = element.clientHeight > 0
                && element.scrollHeight > element.clientHeight + 1;
            const capped = clipped
                && window.getComputedStyle?.(element)?.maxHeight !== 'none';
            const rects = lineRects ? Array.from(lineRects) : null;
            if (
                !(ink?.width > 0)
                || !(ink?.height > 0)
                || (rects && rects.length === 0)
            ) return false;
            if (capped) {
                if (
                    ink.left < box.left - 1
                    || ink.right > box.right + 1
                    || ink.top < box.top - 1
                ) return false;
                continue;
            }
            /* A description's font boxes may leave the box while its letters do
               not (descriptionInkEscapes): it is judged across by its boxes and
               vertically by its ink. */
            if (element.classList?.contains('card-desc')) {
                if (
                    ink.left < box.left - 1
                    || ink.right > box.right + 1
                    || descriptionInkEscapes(element, 1)
                ) return false;
                continue;
            }
            /* So may a title's: two of Spectral's 1.52em font boxes are taller
               than the bar's two 1.05em lines, and a wrapped title near full
               size was refused its flight (2026-09-25). It is judged across by
               its boxes and vertically by the bar's own fit (titleFitsBar). */
            if (element.classList?.contains('card-name')) {
                if (
                    ink.left < box.left - 1
                    || ink.right > box.right + 1
                    || !titleFitsBar(element)
                ) return false;
                continue;
            }
            if (
                !contains(box, ink)
                || (rects && rects.some((rect) => !contains(box, rect)))
                || clipped
            ) return false;
        }
        return measuredTextNodes > 0;
    }

    /**
     * Finalize the whole textual face of a connected card. Enemy travelers use
     * this narrow gate because a late title/description/font pass makes a DOM-
     * complete card look blank for its first motion frames. A bounded font miss
     * rejects the optional traveler instead of pretending the face is ready;
     * authoritative board rendering remains independent.
     */
    async function settleCardTextForPresentation(
        cardElement,
        { fontTimeoutMs = CARD_FACE_FONT_TIMEOUT_MS, stableGeometry = false } = {},
    ) {
        if (!cardElement?.isConnected) return false;
        const nameElement = cardElement.querySelector('.card-name');
        if (nameElement) {
            // This gate owns the staged traveler now. Cancel creation-time
            // timeout/font callbacks even when the bounded font wait fails, so
            // a removed staging card cannot retain late typography work.
            titleFitGenerations.set(
                nameElement,
                (titleFitGenerations.get(nameElement) || 0) + 1,
            );
        }
        if (!await waitForCardFonts(fontTimeoutMs) || !cardElement.isConnected) return false;

        if (nameElement) {
            if (!fitCardTitle(nameElement)) return false;
        }
        fitCardDescription(cardElement, { immediate: true });

        if (stableGeometry === true) await waitForCardRasterLayoutFrame();
        if (!cardElement.isConnected) return false;

        // One final synchronous pass closes geometry drift between insertion
        // and the render frame. Nothing is scheduled after this ownership pass.
        if (nameElement && !fitCardTitle(nameElement)) return false;
        fitCardDescription(cardElement, { immediate: true });
        return cardTextHasPaintGeometry(cardElement);
    }

    async function settleCardFaceForPresentation(cardElement, card, options = {}) {
        const { fontTimeoutMs = CARD_FACE_FONT_TIMEOUT_MS, ...rasterOptions } = options;
        const [rasterOutcome, textOutcome] = await Promise.allSettled([
            settleCardRasterForPresentation(cardElement, card, rasterOptions),
            settleCardTextForPresentation(cardElement, {
                fontTimeoutMs,
                stableGeometry: rasterOptions.stableGeometry === true,
            }),
        ]);
        const connected = cardElement?.isConnected === true;
        const rasterReady = connected
            && rasterOutcome.status === 'fulfilled'
            && rasterOutcome.value === true;
        const textReady = connected
            && textOutcome.status === 'fulfilled'
            && textOutcome.value === true;
        // Keep the two authorities separate. Typography can reject an optional
        // traveler without claiming that the board's independent art pipeline
        // failed (and vice versa).
        return {
            connected,
            rasterReady,
            textReady,
            ready: rasterReady && textReady,
        };
    }

    /**
     * THE REST OF WHAT A REBUILT CARD SHOWED. A snapshot rebuilds the hand (a
     * revealed enemy card, a changed board card) and the paint travels with
     * transferSettledRasterPaint; this carries the two things it does not,
     * for the same card on the same surface:
     *
     *   - the fitted title. A new title opens at the one-line estimate and is
     *     measured on a timer, so a title the fitter had wrapped onto two
     *     lines showed tiny (or cut) for the frames until that timer ran. The
     *     fit is copied; the scheduled fit still runs as the verifier, in one
     *     task, and lands on the same size;
     *   - a playing Stellar loop. A new <video> needs its own media pipeline
     *     and `playing` event before the loop shows, so the still art showed
     *     and the loop restarted from frame 0 on every snapshot. The playing
     *     element itself moves into the new card in the same task, so it is
     *     never paused (a media element is paused only if it is still out of
     *     the document once the task ends).
     */
    function transferCardPresentation(previousCard, nextCard) {
        if (!previousCard || !nextCard || previousCard === nextCard) return;
        transferTitleFit(previousCard, nextCard);
        transferStellarLoopVideo(previousCard, nextCard);
    }

    function transferTitleFit(previousCard, nextCard) {
        const previous = previousCard.querySelector?.('.card-name');
        const next = nextCard.querySelector?.('.card-name');
        if (!previous || !next || !previous.textContent || previous.textContent !== next.textContent) return false;
        const painted = /\*\s*([0-9.]+)\s*\)\s*$/.exec(previous.style.fontSize || '');
        const ratio = painted ? Number(painted[1]) : titleRatios.get(previous);
        if (!Number.isFinite(ratio)) return false;
        next.classList.toggle('card-name--wrap', previous.classList.contains('card-name--wrap'));
        applyTitleRatio(next, ratio);
        // Its predecessor's fit is its fit, unless that was a stand-in too.
        if (!standInTitles.has(previous)) standInTitles.delete(next);
        return true;
    }

    function transferStellarLoopVideo(previousCard, nextCard) {
        const previousVideo = previousCard.querySelector?.('video.stellar-loop-video');
        const nextVideo = nextCard.querySelector?.('video.stellar-loop-video');
        if (!previousVideo || !nextVideo || previousVideo === nextVideo) return false;
        if (previousVideo.dataset.playing !== 'true' || previousVideo.dataset.mediaReady !== 'true') return false;
        const source = (video) => video.getAttribute('src') || video.currentSrc || '';
        if (!source(previousVideo) || source(previousVideo) !== source(nextVideo)) return false;
        nextVideo.replaceWith(previousVideo);
        // The unused twin never plays: release its request.
        try {
            nextVideo.removeAttribute('src');
            nextVideo.load();
        } catch (_) { /* media optional */ }
        return true;
    }

    /** An exact copy of a settled canvas's pixels and the tier they reached, or null. */
    function copySettledCanvas(previous) {
        try {
            const copied = document.createElement('canvas');
            copied.width = previous.width;
            copied.height = previous.height;
            const context = copied.getContext('2d');
            if (!context) return null;
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = 'high';
            context.drawImage(previous, 0, 0);
            if (previous.dataset.cardArtRasterTier) {
                copied.dataset.cardArtRasterTier = previous.dataset.cardArtRasterTier;
            }
            return copied;
        } catch (_) {
            return null;
        }
    }

    function transferSettledRasterPaint(previousCard, nextCard, profileOverrides = {}) {
        const previousIsPaintNode = ['IMG', 'CANVAS'].includes(previousCard?.tagName);
        const previous = previousIsPaintNode
            ? previousCard
            : previousCard?.querySelector?.('.card-image > .card-art-paint');
        const next = nextCard?.querySelector?.('.card-image > img.card-art-paint');
        if (!previous || !next) return false;
        const previousIsCanvas = previous.tagName === 'CANVAS';
        const previousIsImage = previous.tagName === 'IMG';
        const source = previous.dataset.cardArtSource || previous.getAttribute('src') || '';
        const sourceQuality = previous.dataset.cardArtQuality;
        const paint = previousIsCanvas
            ? previous.dataset.cardArtRasterKey || ''
            : previous.getAttribute('src') || '';
        const sourceFit = previous.dataset.cardArtFit || next.dataset.cardArtFit;
        // Intrinsic dimensions prove that drawable pixels exist. `complete`
        // can remain false for a frame in Chromium/Firefox during a source or
        // decoded-data lifecycle even though drawImage is already valid.
        const decoded = previousIsCanvas
            ? previous.width > 0 && previous.height > 0
            : previous.naturalWidth > 0 && previous.naturalHeight > 0;
        // Blob-backed samples belong to the source that the sampler settled.
        // A stale or undecoded blob cannot borrow a cached authority to transfer.
        const decodedBlobSample = paint.startsWith('blob:')
            && decoded
            && previous.dataset.cardArtSampleSource === source;
        const enhancedPaint = sourceQuality === 'enhanced'
            && (previousIsCanvas ? Boolean(paint) : paint.startsWith('data:') || decodedBlobSample);
        const directPaint = sourceQuality === 'direct'
            && decoded
            && (previousIsCanvas ? Boolean(paint) : paint === source);
        const degradedAuthorityPaint = sourceQuality === 'degraded'
            && previousIsImage
            && decoded
            && paint === source;
        if (
            !source
            || source !== next.dataset.cardArtSource
            || sourceFit !== next.dataset.cardArtFit
            || (!enhancedPaint && !directPaint && !degradedAuthorityPaint)
        ) return false;

        const previousRect = cardArtLayoutRect(previous);
        // Same ruler on both sides of the transfer — see cardArtLayoutRect.
        const nextRect = cardArtLayoutRect(next);
        const storedDisplayWidth = Number(previous.dataset.cardArtRasterDisplayWidth);
        const storedDisplayHeight = Number(previous.dataset.cardArtRasterDisplayHeight);
        const storedRect = storedDisplayWidth > 0 && storedDisplayHeight > 0
            ? { width: storedDisplayWidth, height: storedDisplayHeight }
            : null;
        const renderRect = nextRect.width > 0 && nextRect.height > 0
            ? nextRect
            : storedRect || previousRect;
        const renderProfile = renderRect.width > 0 && renderRect.height > 0
            ? cardArtProfileForImage(next, renderRect, profileOverrides)
            : null;
        /* THE SAME PIXELS, NOT A RE-RENDER OF THEM (flicker hunt, 25 September
           2026, the summon). A settled canvas baked for exactly this request
           (source, crop, box, DPR and reserve) already IS the render below, at
           the tier it reached: on Chromium a lock is upgraded once to the
           createImageBitmap reduction, which the synchronous re-render cannot
           produce. Re-rendering handed every rebuilt card a canvas-2D paint
           owing that upgrade again, so ~200 ms after a summon the upgrade
           swapped the canvas of every card left in the hand, and the board
           card seated from its prepared probe failed the same-task
           presentation check and showed its frame 55-90 ms before its art.
           The exact copy keeps tier and request, and costs one same-size blit
           instead of a stepped downscale.
           Contract: tests/unit/ui/cards.settledCanvasKeepsTier.test.js. */
        if (
            previousIsCanvas
            && (enhancedPaint || directPaint)
            && renderProfile
            && previous.dataset.cardArtRasterRequest === lockedRasterRequestSignature(source, renderProfile)
        ) {
            const copied = copySettledCanvas(previous);
            if (copied) {
                return installSettledCanvasPaint(
                    next,
                    source,
                    copied,
                    'settled-transfer',
                    sourceQuality,
                    {
                        rasterMetadataSource: previous,
                        authority: lockedRasterAuthorities.get(previous)
                            || window.MoonfallRasterSampler?.getDecodedImage?.(source)
                            || null,
                    },
                );
            }
        }
        // The sampler retains a small LRU of decoded authority images. Prefer
        // that original source over a smaller grid/thumbnail sample so popup
        // pixels are prepared for the popup's own geometry before insertion.
        if (renderProfile && typeof window.MoonfallRasterSampler?.renderCachedImage === 'function') {
            const rendered = window.MoonfallRasterSampler.renderCachedImage(source, renderProfile);
            if (rendered?.canvas && (rendered.sampled || rendered.materialized)) {
                return installSettledCanvasPaint(
                    next,
                    source,
                    rendered.canvas,
                    'settled-transfer',
                    rendered.sampled ? 'enhanced' : 'direct',
                    {
                        profile: renderProfile,
                        authority: window.MoonfallRasterSampler?.getDecodedImage?.(source) || null,
                    },
                );
            }
        }

        // A locked paint keeps its decoded source in a private WeakMap even
        // after the shared LRU evicts that URL. Re-render that authority at
        // the replacement's final geometry before considering a bitmap copy;
        // otherwise a small grid canvas can be exposed, then replaced by the
        // first ResizeObserver delivery after the popup becomes visible.
        const retainedPaintAuthority = lockedRasterAuthorities.get(previous) || null;
        if (transientLockedRasterAuthorities.has(previous)) {
            transientLockedRasterAuthorities.delete(previous);
            lockedRasterAuthorities.delete(previous);
        }
        if (
            retainedPaintAuthority
            && renderProfile
            && typeof window.MoonfallRasterSampler?.renderDecodedImage === 'function'
        ) {
            let rendered;
            try {
                rendered = window.MoonfallRasterSampler.renderDecodedImage(
                    retainedPaintAuthority,
                    renderProfile,
                );
            } catch (_) {
                rendered = null;
            }
            if (rendered?.canvas && (rendered.sampled || rendered.materialized)) {
                return installSettledCanvasPaint(
                    next,
                    source,
                    rendered.canvas,
                    'settled-transfer',
                    rendered.sampled ? 'enhanced' : 'direct',
                    { profile: renderProfile, authority: retainedPaintAuthority },
                );
            }
            releaseUnusedRasterCanvas(rendered?.canvas);
        }

        // If interaction beats the asynchronous grid sampler, derive the
        // popup's final physical-pixel canvas synchronously from the already
        // decoded source. The canvas is ready before insertion, so immediate
        // hover never shows a soft direct reduction and never swaps pixels a
        // frame later. Drawing a cross-origin image is permitted; because the
        // canvas is displayed rather than exported, a tainted canvas is safe.
        const fullCardPaintCanBeRendered = previousIsImage && decoded && (
            directPaint || degradedAuthorityPaint || (enhancedPaint && !previousIsPaintNode)
        );
        if (
            fullCardPaintCanBeRendered
            && renderProfile
            && typeof window.MoonfallRasterSampler?.renderDecodedImage === 'function'
        ) {
            const rendered = window.MoonfallRasterSampler.renderDecodedImage(
                previous,
                renderProfile,
            );
            if (rendered?.canvas && (rendered.sampled || rendered.materialized)) {
                return installSettledCanvasPaint(
                    next,
                    source,
                    rendered.canvas,
                    'settled-transfer',
                    rendered.sampled ? 'enhanced' : 'direct',
                    { profile: renderProfile, authority: previous },
                );
            }
        }

        // A canvas already contains immutable physical pixels. When the
        // decoded authority LRU has been evicted, copy those exact pixels into
        // the replacement generation synchronously. cloneNode() alone does not
        // copy a canvas bitmap, so make the draw explicit.
        if (
            previousIsCanvas
            && (enhancedPaint || directPaint)
        ) {
            const copied = copySettledCanvas(previous);
            if (!copied) return false;
            try {
                return installSettledCanvasPaint(
                    next,
                    source,
                    copied,
                    'settled-transfer',
                    sourceQuality,
                    {
                        rasterMetadataSource: previous,
                        authority: retainedPaintAuthority
                            || window.MoonfallRasterSampler?.getDecodedImage?.(source)
                            || null,
                    },
                );
            } catch (_) {
                return false;
            }
        }

        // A lightweight thumbnail sample may have a different aspect ratio
        // from a full card face. Never stretch that derived bitmap when its
        // decoded authority source has already left the bounded cache.
        if (previousIsPaintNode && enhancedPaint) return false;

        // Degraded authority is useful only as decoded input for a freshly
        // rendered final-size canvas. Never clone it or relabel it as direct
        // when the synchronous renderer could not produce settled pixels.
        if (degradedAuthorityPaint) return false;

        // A data URL assignment can resolve the sampler promise before the new
        // browser image node exposes intrinsic pixels. cloneNode() preserves
        // attributes, not decoded bitmap state, so cloning here would create a
        // transparent first frame and a later blur-to-sharp transition.
        if (previousIsImage && !decoded) return false;

        // Clone the already-decoded image node instead of assigning its URL to
        // the preview's fresh node. Firefox otherwise reports the new data-URL
        // image incomplete for one painted frame, and the detached preview's
        // earlier sampler request can still race the transfer. Replacing that
        // node preserves the decoded browser image resource and strands every
        // stale loader/sampler callback on the discarded element.
        const transferred = previous.cloneNode(false);
        useSharedArtRequestMode(transferred, paint);
        transferred.setAttribute('src', paint);
        transferred.className = next.className;
        transferred.style.cssText = next.style.cssText;
        transferred.dataset.cardArtSource = source;
        transferred.dataset.cardArtFit = next.dataset.cardArtFit;
        transferred.dataset.cardArtQuality = enhancedPaint ? 'enhanced' : 'direct';
        transferred.dataset.cardArtPaintLock = 'settled-transfer';
        if (renderProfile) {
            transferred.dataset.cardArtRasterOversample = `${renderProfile.oversample}`;
            transferred.dataset.cardArtRasterPositionX = `${renderProfile.positionX}`;
            transferred.dataset.cardArtRasterPositionY = `${renderProfile.positionY}`;
            transferred.dataset.cardArtRasterDisplayWidth = `${renderProfile.displayWidth}`;
            transferred.dataset.cardArtRasterDisplayHeight = `${renderProfile.displayHeight}`;
        }
        delete transferred.dataset.cardArtLoaderId;
        delete transferred.dataset.artMaterializeBound;
        transferred.classList.remove('card-art-loading');
        next.replaceWith(transferred);
        const cachedAuthority = window.MoonfallRasterSampler?.getDecodedImage?.(source);
        if (cachedAuthority && renderProfile?.oversample > 1) {
            lockedRasterAuthorities.set(transferred, cachedAuthority);
        }
        ensureCardArtResizeObserver(transferred);
        ensureLockedRasterGlobalRefresh();
        settleCardArtReadiness(next, true);
        return true;
    }

    let cardArtLoaderId = 0;

    function loadCardImageWithFallbacks(
        img,
        candidates,
        artProfile = {},
        { directArtOnly = false } = {},
    ) {
        if (!img) return;
        cardArtLoaderControllers.get(img)?.dispose?.();
        const loaderId = String(++cardArtLoaderId);
        const directUrls = [...(candidates?.directUrls || [])];
        const assetIds = [...(candidates?.assetIds || [])];
        let assets = [];
        const attemptedUrls = new Set();
        let directIndex = 0;
        let assetIndex = 0;
        let requestVersion = 0;
        let candidateVersion = 0;
        let candidateTimer = null;
        let retryTimer = null;
        let retryAttempt = 0;
        let roundRetryable = false;
        let wakeListenersBound = false;
        let disposed = false;
        // Bind lifecycle listeners to the image's own realm. A final queued
        // detach mutation may be delivered while a test/page global is already
        // retiring; resolving `window` or `document` again at disposal time
        // would then throw and strand the rest of the cleanup sweep.
        const ownerDocument = img.ownerDocument;
        const ownerWindow = ownerDocument?.defaultView;
        let everConnected = img.isConnected;
        const createdAt = Date.now();
        img.dataset.cardArtLoaderId = loaderId;
        if (directArtOnly) img.dataset.cardArtDirectOnly = 'true';
        else delete img.dataset.cardArtDirectOnly;
        img.classList.add('card-art-paint', 'card-art-loading');
        beginCardArtReadiness(img, loaderId);

        const ownsImage = () => !disposed && img.dataset.cardArtLoaderId === loaderId;

        const noteConnection = () => {
            if (img.isConnected) everConnected = true;
            return img.isConnected;
        };

        const clearRasterAuthority = () => {
            delete img.dataset.cardArtSource;
            delete img.dataset.cardArtSampleSource;
            delete img.dataset.cardArtFit;
            delete img.dataset.cardArtQuality;
            delete img.dataset.cardArtLoadPending;
        };

        const clearCandidateTimer = () => {
            if (candidateTimer === null) return;
            clearTimeout(candidateTimer);
            candidateTimer = null;
        };

        const unbindWakeListeners = () => {
            if (!wakeListenersBound) return;
            wakeListenersBound = false;
            ownerWindow?.removeEventListener?.('online', wakeRetry);
            ownerWindow?.removeEventListener?.('pageshow', wakeRetry);
            ownerDocument?.removeEventListener?.('visibilitychange', wakeRetry);
        };

        const clearRetryTimer = () => {
            if (retryTimer !== null) clearTimeout(retryTimer);
            retryTimer = null;
            unbindWakeListeners();
        };

        const dispose = ({ invalidate = true, cancelSource = false } = {}) => {
            if (disposed) return;
            disposed = true;
            clearCandidateTimer();
            clearRetryTimer();
            requestVersion += 1;
            candidateVersion += 1;
            img.onload = null;
            img.onerror = null;
            if (cancelSource) img.removeAttribute('src');
            if (invalidate && img.dataset.cardArtLoaderId === loaderId) {
                delete img.dataset.cardArtLoaderId;
                settleCardArtReadiness(img, false);
            }
            if (cardArtLoaderControllers.get(img)?.loaderId === loaderId) {
                cardArtLoaderControllers.delete(img);
            }
        };

        const resetRound = () => {
            directIndex = 0;
            assetIndex = 0;
            roundRetryable = false;
            attemptedUrls.clear();
            assets = assetIds.map((id) => ({ id, peekChecked: false }));
        };

        const showSourceLessWell = ({ retrying }) => {
            clearCandidateTimer();
            candidateVersion += 1;
            img.onload = null;
            img.onerror = null;
            clearRasterAuthority();
            img.classList.remove('character-portrait', 'placeholder-art', 'moon-fuse-placeholder-art');
            img.removeAttribute('src');
            img.removeAttribute('data-asset-id');
            img.classList.toggle('card-art-retrying', retrying);
            img.classList.toggle('card-art-unavailable', !retrying);
            img.classList.toggle('card-art-loading', retrying);
            if (!retrying) settleCardArtReadiness(img, false);
        };

        const canRetry = () => {
            if (!ownsImage()) return false;
            if (noteConnection()) return true;
            if (everConnected || Date.now() - createdAt >= CARD_IMAGE_DETACHED_GRACE_MS) {
                dispose();
                return false;
            }
            return true;
        };

        const startRetryRound = () => {
            if (!canRetry()) return;
            clearRetryTimer();
            resetRound();
            void advance();
        };

        function wakeRetry() {
            if (!ownsImage() || retryTimer === null) return;
            if (ownerDocument?.visibilityState === 'hidden') return;
            startRetryRound();
        }

        const bindWakeListeners = () => {
            if (wakeListenersBound) return;
            wakeListenersBound = true;
            ownerWindow?.addEventListener?.('online', wakeRetry, { passive: true });
            ownerWindow?.addEventListener?.('pageshow', wakeRetry, { passive: true });
            ownerDocument?.addEventListener?.('visibilitychange', wakeRetry, { passive: true });
        };

        const scheduleRetry = () => {
            if (!canRetry()) return;
            showSourceLessWell({ retrying: true });
            clearRetryTimer();
            const delay = CARD_IMAGE_RETRY_DELAYS_MS[
                Math.min(retryAttempt, CARD_IMAGE_RETRY_DELAYS_MS.length - 1)
            ];
            retryAttempt += 1;
            img.dataset.cardArtRetryAttempt = String(retryAttempt);
            retryTimer = setTimeout(startRetryRound, delay);
            bindWakeListeners();
        };

        const showUnavailableArt = () => {
            if (!ownsImage()) return;
            clearRetryTimer();
            showSourceLessWell({ retrying: false });
            delete img.dataset.cardArtRetryAttempt;
            if (cardArtLoaderControllers.get(img)?.loaderId === loaderId) {
                cardArtLoaderControllers.delete(img);
            }
        };

        const candidateSourceIsCurrent = (sourceUrl) => {
            if (img.getAttribute('src') !== sourceUrl) return false;
            const currentSrc = img.currentSrc || '';
            if (!currentSrc) return true;
            try {
                return new URL(currentSrc, window.location?.href || 'http://localhost/').href
                    === new URL(sourceUrl, window.location?.href || 'http://localhost/').href;
            } catch (_) {
                return currentSrc === sourceUrl;
            }
        };

        const failCandidate = (version, sourceUrl) => {
            if (
                !ownsImage()
                || version !== candidateVersion
                || !candidateSourceIsCurrent(sourceUrl)
            ) return;
            clearCandidateTimer();
            candidateVersion += 1;
            img.onload = null;
            img.onerror = null;
            // A declared source remains authoritative regardless of protocol.
            // Network URLs may recover, a blob may be revalidated by its owner,
            // and even a malformed data URL must not silently become a
            // different card. The capped mounted-owner loop below is the one
            // truthful failure policy for all of them.
            roundRetryable = true;
            clearRasterAuthority();
            img.classList.remove('character-portrait');
            img.removeAttribute('src');
            requestVersion += 1;
            // Once an identity has produced a concrete URL, a transport
            // failure may not advance to a different picture/card candidate.
            // Begin a fresh round after backoff so the same first declared
            // identity is resolved and attempted again.
            scheduleRetry();
        };

        const completeCandidate = (version, sourceUrl, event) => {
            if (
                !ownsImage()
                || version !== candidateVersion
                || !candidateSourceIsCurrent(sourceUrl)
                // A load event is authority only when the current candidate
                // produced drawable pixels. Programmatic events do not get a
                // weaker path than browser events.
                || !(img.naturalWidth > 0)
                || !(img.naturalHeight > 0)
            ) return;
            clearCandidateTimer();
            clearRetryTimer();
            candidateVersion += 1;
            img.onload = null;
            img.onerror = null;
            retryAttempt = 0;
            delete img.dataset.cardArtRetryAttempt;
            img.classList.remove('card-art-retrying', 'card-art-unavailable', 'placeholder-art');
            const settledVersion = candidateVersion;
            const settled = () => ownsImage()
                && candidateVersion === settledVersion
                && candidateSourceIsCurrent(sourceUrl);
            const settle = async (decoded = null) => {
                // Held until the paint's bitmap-tier raster has read it too:
                // let go between the two, the decode was evicted by the next
                // card's and redone on the main thread.
                let release = decoded?.release;
                try {
                    if (!settled()) return;
                    // On the bitmap tier a decoded picture is rastered once,
                    // there (bitmapTierRasterFor); the art stays behind its
                    // well meanwhile.
                    let locked = false;
                    const cardElement = !directArtOnly && img.isConnected ? img.closest?.('.card') : null;
                    if (cardElement && decoded?.pixels && samplerHasBitmapTier()) {
                        locked = await lockCardRasterAtBitmapTier(cardElement, 'initial');
                        if (!locked && !settled()) return;
                    }
                    delete img.dataset.cardArtLoadPending;
                    if (locked) return;
                    // A hover or drag that came while the picture decoded has
                    // already locked the final paint.
                    if (img.dataset.cardArtPaintLock) return;
                    // Once the card is measurable, materialize its declared
                    // interaction reserve here so the first visible frame is
                    // already the immutable final paint rather than direct
                    // pixels that sharpen or swap on a later sampler task.
                    if (cardElement && img.isConnected) {
                        locked = lockCardRaster(cardElement, 'initial', decoded?.pixels || null);
                        const paint = locked && decoded?.pixels
                            ? cardElement.querySelector('.card-image > canvas.card-art-paint')
                            : null;
                        if (release && paint && lockedRasterBitmapUpgradeOwed(paint)) {
                            const heldRelease = release;
                            release = null;
                            refreshLockedCardRaster(paint).catch(() => false).finally(heldRelease);
                        }
                    }
                    if (!directArtOnly && !locked && ownsImage()) {
                        // The decoded source could not be materialized
                        // synchronously (usually because final geometry is
                        // not measurable yet). Start the one async sampler
                        // authority only now; beginning it at URL assignment
                        // would duplicate all work on the normal successful
                        // initial-lock path.
                        void enhanceCardArt(img, sourceUrl, artProfile);
                    }
                    if (directArtOnly && ownsImage()) revealSettledCardArt(img);
                } finally {
                    release?.();
                }
            };
            /* THE FIRST DRAW READS A PICTURE ALREADY DECODED (replay 01a175f7,
               26 October 2026): drawn in this event, the picture decoded on
               the main thread, 150-230 ms a card on a slow PC (2 of the
               2.6-3.2 s a deck builder page took to turn). The art waits
               behind its well, as for a download, until the sampler has it
               decoded off the main thread (raster-sampler.ts decodePicture). */
            const decodePicture = window.MoonfallRasterSampler?.decodePicture;
            if (directArtOnly || typeof decodePicture !== 'function') {
                void settle();
                return;
            }
            let decoding;
            try {
                decoding = Promise.resolve(decodePicture(img));
            } catch (_) {
                decoding = Promise.resolve(null);
            }
            decoding.then(settle, () => settle());
        };

        const setCandidateUrl = (value, assetId = '') => {
            const url = sanitizeDirectMediaUrl(value);
            if (!url || url === CARD_IMAGE_PLACEHOLDER || attemptedUrls.has(url)) return false;
            attemptedUrls.add(url);
            img.classList.remove(
                'placeholder-art',
                'card-art-unavailable',
                'card-art-retrying',
            );
            if (assetId) img.dataset.assetId = assetId;
            else img.removeAttribute('data-asset-id');
            clearCandidateTimer();
            const version = ++candidateVersion;
            img.onload = (event) => completeCandidate(version, url, event);
            img.onerror = () => failCandidate(version, url);
            // The window grows with every attempt that outlasted the last
            // one, so an image the first window could not hold still gets
            // an attempt long enough to finish.
            candidateTimer = setTimeout(
                () => failCandidate(version, url),
                imageAttemptDeadlineMs(retryAttempt),
            );
            if (directArtOnly) {
                const directProfile = cardArtProfile(artProfile);
                img.classList.add('card-art-paint');
                img.dataset.cardArtSource = url;
                img.dataset.cardArtFit = directProfile.fit;
                img.dataset.cardArtQuality = 'direct';
            } else {
                const pendingProfile = cardArtProfile(artProfile);
                img.classList.add('card-art-paint');
                img.dataset.cardArtSource = url;
                img.dataset.cardArtFit = pendingProfile.fit;
                img.dataset.cardArtQuality = 'pending';
                img.dataset.cardArtLoadPending = 'true';
                ensureCardArtResizeObserver(img);
            }
            useSharedArtRequestMode(img, url);
            img.setAttribute('src', url);
            if (version === candidateVersion) applyArtMaterialize(img);
            return true;
        };

        const advance = async () => {
            if (!ownsImage()) return;
            noteConnection();
            const version = ++requestVersion;
            while (ownsImage() && version === requestVersion && directIndex < directUrls.length) {
                if (setCandidateUrl(directUrls[directIndex++])) return;
            }

            const media = MoonfallCloud?.media;
            while (ownsImage() && version === requestVersion && assetIndex < assets.length) {
                const asset = assets[assetIndex];
                if (!asset.peekChecked) {
                    asset.peekChecked = true;
                    let cachedUrl = null;
                    try {
                        cachedUrl = media?.peekUrl?.(asset.id);
                    } catch (_err) {
                        // A cache read is only an optimization; the authoritative lookup follows.
                    }
                    if (setCandidateUrl(cachedUrl, asset.id)) return;
                }

                if (typeof media?.getUrl !== 'function') {
                    roundRetryable = true;
                    assetIndex += 1;
                    continue;
                }

                const resolvingIndex = assetIndex;
                const outcome = await resolveMediaUrl(media, asset.id);
                if (!ownsImage() || version !== requestVersion) return;
                assetIndex = resolvingIndex + 1;
                if (outcome.retryable) roundRetryable = true;
                if (setCandidateUrl(outcome.url, asset.id)) return;
            }

            if (!ownsImage() || version !== requestVersion) return;
            if (roundRetryable) scheduleRetry();
            else showUnavailableArt();
        };

        const controller = { loaderId, dispose, scheduleRetry };
        cardArtLoaderControllers.set(img, controller);
        // Observe from the resolver stage, not only after a URL exists. A card
        // removed while Convex is offline must dispose its retry timer and
        // lifecycle listeners without waiting for the next backoff tick.
        ensureCardArtResizeObserver(img);
        resetRound();
        void advance();
    }

    /**
     * Load one known card-art URL through the same retry, lifecycle, decode and
     * raster path as a full card face. Static consumers (banners, Foundry art
     * choices, Collection details) must use this instead of assigning a URL and
     * treating the first network error as a permanent missing asset.
     */
    /*
     * ONE REQUEST PER PICTURE. The card's own <img> used to load its art
     * without CORS while the preloader and the raster sampler asked for the
     * same URL with `crossOrigin="anonymous"`; Chromium keys those apart, so a
     * first visit downloaded every card and portrait two or three times in
     * parallel (measured 25 September 2026 on the tutorial board). The art
     * hosts serve CORS headers — the sampler already depends on them — so the
     * element joins the same mode. Local assets use no CORS, as their warm
     * does. Resolve the mode for each concrete candidate immediately before
     * src: a retry or reused image can cross from one origin to another.
     */
    function useSharedArtRequestMode(img, sourceUrl) {
        if (!img || img.tagName !== 'IMG') return;
        const crossOrigin = LoadScheduler.requestMode(sourceUrl).crossOrigin;
        if (img.crossOrigin !== crossOrigin) img.crossOrigin = crossOrigin;
    }

    function loadCardArtImage(img, sourceUrl, artProfile = {}, options = {}) {
        const directUrl = sanitizeDirectMediaUrl(sourceUrl);
        loadCardImageWithFallbacks(img, {
            directUrls: directUrl ? [directUrl] : [],
            assetIds: [],
        }, cardArtProfile(artProfile), {
            directArtOnly: options.directArtOnly === true,
        });
        return pendingCardArtReadiness.get(img)?.promise || Promise.resolve(false);
    }

    function disposeCardArtImage(paint) {
        if (!paint) return;
        cardArtLoaderControllers.get(paint)?.dispose?.({ cancelSource: true });
        stopCardArtResizeObservation(paint);
        untrackCardArtOwner(paint);
        if (paint.tagName === 'IMG') paint.removeAttribute('src');
        delete paint.dataset.cardArtDirectOnly;
        delete paint.dataset.artMaterializeBound;

        // Disposal is a presentation-ownership boundary, not merely a retry
        // cancellation. Invalidate every asynchronous raster path that can
        // otherwise keep a hidden chooser/banner/card alive or repaint it
        // after its surface has closed. WeakMap deletion deliberately makes
        // in-flight ownsGeneration/ownsRefresh guards fail.
        cardArtEnhancementGenerations.delete(paint);
        pendingCardArtTasks.delete(paint);
        settleCardArtReadiness(paint, false);
        pendingCardArtReadiness.delete(paint);
        lockedRasterRefreshGenerations.set(
            paint,
            (lockedRasterRefreshGenerations.get(paint) || 0) + 1,
        );
        lockedRasterRefreshTasks.delete(paint);
        pendingLockedRasterRefreshTargets.delete(paint);
        lockedRasterAuthorities.delete(paint);
        transientLockedRasterAuthorities.delete(paint);
        releaseRefitSource(paint);
    }

    function ensureSummonStagingContainer() {
        if (summonStagingRoot) return summonStagingRoot;
        if (typeof document === 'undefined') return null;

        const container = document.createElement('div');
        container.id = 'stellar-summon-staging';
        container.style.position = 'absolute';
        container.style.width = '1px';
        container.style.height = '1px';
        container.style.overflow = 'hidden';
        container.style.pointerEvents = 'none';
        container.style.opacity = '0';
        container.style.left = '-9999px';
        container.style.top = '0';
        document.body.appendChild(container);
        summonStagingRoot = container;
        return summonStagingRoot;
    }

    function createStellarVideoElement(src, className, {
        loop = false,
        muted = true,
        preload = 'metadata',
        fallbackSrc = '',
    } = {}) {
        const video = document.createElement('video');
        video.className = className;
        video.src = src;
        video.loop = loop;
        video.muted = muted;
        video.preload = preload;
        video.dataset.mediaReady = 'false';
        // Visibility follows this, never the pointer. See cards.css.
        video.dataset.playing = 'false';
        if (fallbackSrc && fallbackSrc !== src) video.dataset.stellarFallbackSrc = fallbackSrc;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('aria-hidden', 'true');
        video.tabIndex = -1;
        try {
            video.disablePictureInPicture = true;
        } catch (_) { }
        return video;
    }

    function tryStellarMp4Fallback(video) {
        const fallbackSrc = String(video?.dataset?.stellarFallbackSrc || '');
        if (!video || !fallbackSrc || video.dataset.stellarFallbackUsed === 'true') return false;
        video.dataset.stellarFallbackUsed = 'true';
        video.dataset.stellarFallbackLoading = 'true';
        const resume = video._stellarFallbackResume;
        if (typeof resume === 'function') {
            let resumed = false;
            const resumeOnce = () => {
                if (resumed) return;
                resumed = true;
                video.removeEventListener('loadeddata', resumeOnce);
                video.removeEventListener('canplay', resumeOnce);
                video.dataset.stellarFallbackLoading = 'false';
                resume();
            };
            video.addEventListener('loadeddata', resumeOnce);
            video.addEventListener('canplay', resumeOnce);
        }
        video.src = fallbackSrc;
        try { video.load(); } catch (_) { }
        return true;
    }

    function bindStellarVideoReadiness(video) {
        if (!video || video.dataset.mediaReadyBound === 'true') return;
        video.dataset.mediaReadyBound = 'true';
        video.dataset.mediaReady = 'false';

        const markReady = () => {
            video.dataset.mediaReady = 'true';
            if (video.dataset.stellarFallbackUsed === 'true') {
                video.dataset.stellarFallbackLoading = 'false';
            }
        };

        const resetReady = () => {
            video.dataset.mediaReady = 'false';
        };

        const handleError = () => {
            resetReady();
            if (tryStellarMp4Fallback(video)) return;
            if (video.dataset.stellarFallbackLoading === 'true') {
                video.dataset.stellarFallbackLoading = 'false';
                if (typeof video._stellarFallbackFailure === 'function') {
                    video._stellarFallbackFailure(video.error || new Error('stellar_media_fallback_failed'));
                }
            }
        };

        // `playing` means frames are actually being rendered — the honest signal
        // for "this layer is animating". `waiting` is deliberately NOT bound: a
        // stall holds the last frame, and dropping the layer there would flash
        // the still art mid-animation.
        const markPlaying = () => { video.dataset.playing = 'true'; };
        const markStopped = () => { video.dataset.playing = 'false'; };

        video.addEventListener('loadeddata', markReady);
        video.addEventListener('canplay', markReady);
        video.addEventListener('playing', markReady);
        video.addEventListener('playing', markPlaying);
        video.addEventListener('pause', markStopped);
        video.addEventListener('ended', markStopped);
        video.addEventListener('loadstart', resetReady);
        video.addEventListener('error', handleError);
        video.addEventListener('error', markStopped);
        video.addEventListener('emptied', resetReady);
        video.addEventListener('emptied', markStopped);
    }

    function getStellarVideos(cardElement) {
        if (!cardElement) return { loopVideo: null, summonVideo: null, castVideo: null };
        return {
            loopVideo: cardElement.querySelector('video.stellar-loop-video'),
            summonVideo: cardElement.querySelector('video.stellar-summon-video'),
            castVideo: cardElement.querySelector('video.stellar-cast-video'),
        };
    }

    function fadeMediaVolume(media, targetVolume, durationMs, onComplete) {
        if (!media || typeof media.volume !== 'number') {
            if (typeof onComplete === 'function') onComplete();
            return;
        }
        const startVolume = media.volume;
        const safeDuration = Math.max(0, Number(durationMs) || 0);
        const steps = Math.max(1, Math.round(safeDuration / 50));
        let step = 0;

        if (media._volumeFadeTimer) {
            clearInterval(media._volumeFadeTimer);
            media._volumeFadeTimer = null;
        }

        if (safeDuration === 0 || steps === 1) {
            media.volume = targetVolume;
            if (typeof onComplete === 'function') onComplete();
            return;
        }

        media._volumeFadeTimer = setInterval(() => {
            step += 1;
            const progress = Math.min(1, step / steps);
            media.volume = startVolume + (targetVolume - startVolume) * progress;
            if (progress >= 1) {
                clearInterval(media._volumeFadeTimer);
                media._volumeFadeTimer = null;
                if (typeof onComplete === 'function') onComplete();
            }
        }, safeDuration / steps);
    }

    function prefersReducedMotion() {
        try {
            return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
        } catch (_) {
            return false;
        }
    }

    /**
     * Is a one-shot cinematic REALLY holding this card's art well?
     *
     * The two `*-playing` classes used to be trusted on sight, so any path that
     * set one and never cleared it left the card unable to resume its resting
     * loop for the rest of the match — a staged summon clip that a board rebuild
     * DETACHES is paused by the UA and fires neither `ended` nor `error`.
     * A missing, detached or finished layer is stale state and is repaired here.
     * A present-but-not-yet-started one is NOT: `startSummonPlaybackForEntry`
     * adds the class a tick before `play()` resolves, and stripping it there
     * would cancel the cinematic it is announcing.
     */
    function cinematicOwnsCard(cardElement) {
        let owned = false;
        [
            ['stellar-summon-playing', 'video.stellar-summon-video'],
            ['stellar-cast-playing', 'video.stellar-cast-video'],
        ].forEach(([className, selector]) => {
            if (!cardElement.classList.contains(className)) return;
            const video = cardElement.querySelector(selector);
            if (video && video.isConnected && !video.ended) {
                owned = true;
                return;
            }
            cardElement.classList.remove(className);
        });
        return owned;
    }

    function stopLoopVideo(loopVideo) {
        if (!loopVideo) return;
        loopVideo.dataset.playing = 'false';
        try { loopVideo.pause(); } catch (_) { }
        try { loopVideo.currentTime = 0; } catch (_) { }
        loopVideo.muted = true;
        loopVideo.volume = STELLAR_LOOP_VOLUME;
        loopVideo._stellarFallbackResume = null;
    }

    /** Release the card from its summon cinematic and restart its resting loop. */
    function finishStellarSummon(summonVideo) {
        summonVideo._stellarFallbackResume = null;
        summonVideo._stellarFallbackFailure = null;
        const instanceId = summonVideo.dataset.instanceId || '';
        if (instanceId) {
            activeSummonVideos.delete(instanceId);
        }
        const cardElement = summonVideo.closest('.card');
        if (!cardElement) return;
        cardElement.classList.remove('stellar-summon-playing');
        // Unconditional: a Stellar card animates at rest, and after the drag
        // that summoned it the pointer has long since left. Gating this on
        // hover is what stopped the passive clip for good.
        playLoopVideo(cardElement, { restart: false });
    }

    function bindSummonVideoEvents(summonVideo) {
        if (!summonVideo || summonVideo.dataset.summonBound === 'true') return;
        summonVideo.dataset.summonBound = 'true';
        bindCinematicLoopHandover(summonVideo);
        summonVideo.addEventListener('ended', () => finishStellarSummon(summonVideo));
        /* Symmetry with bindCastVideoEvents. A summon clip that ERRORS used to
           bind nothing, so `stellar-summon-playing` stuck for the rest of the
           match and that Character never animated again — Spells recovered,
           Characters did not. The fallback guard matches the cast path: while an
           MP4 fallback is still loading the error is that swap, not the end. */
        summonVideo.addEventListener('error', () => {
            if (summonVideo.dataset.stellarFallbackLoading !== 'true') {
                finishStellarSummon(summonVideo);
            }
        });
    }

    function finishStellarSpellCast(cardElement) {
        if (!cardElement || !cardElement.classList.contains('stellar-cast-playing')) return false;
        cardElement.classList.remove('stellar-cast-playing');
        playLoopVideo(cardElement, { restart: false });
        return true;
    }

    function bindCastVideoEvents(castVideo) {
        if (!castVideo || castVideo.dataset.castBound === 'true') return;
        castVideo.dataset.castBound = 'true';
        bindCinematicLoopHandover(castVideo);
        const finish = () => {
            finishStellarSpellCast(castVideo.closest('.card'));
        };
        castVideo.addEventListener('ended', finish);
        castVideo.addEventListener('error', () => {
            if (castVideo.dataset.stellarFallbackLoading !== 'true') finish();
        });
    }

    function findCardElementByInstanceId(instanceId) {
        if (!instanceId || typeof document === 'undefined') return null;
        // Preview clones (tap-inspection popup, catalog hovers) share the board
        // card's instanceId but are throwaway media surfaces: staged summon
        // videos must never resolve to them (see attachStagedSummonVideo).
        const selectors = [
            `.card[data-instance-id="${instanceId}"][data-zone="player-board"]`,
            `.card[data-instance-id="${instanceId}"][data-zone="opponent-board"]`,
            `.card[data-instance-id="${instanceId}"]:not([data-stellar-preview])`,
        ];
        for (const selector of selectors) {
            const match = document.querySelector(selector);
            if (match) return match;
        }
        return null;
    }

    function attachSummonVideoToCard(cardElement, summonVideo) {
        if (!cardElement || !summonVideo) return;
        const imageElement = cardElement.querySelector('.card-image');
        if (!imageElement) return;

        const existing = imageElement.querySelector('video.stellar-summon-video');
        if (existing && existing !== summonVideo) {
            existing.remove();
        }

        if (!summonVideo.classList.contains('stellar-summon-video')) {
            summonVideo.classList.add('stellar-summon-video');
        }
        if (summonVideo.parentElement !== imageElement) {
            imageElement.appendChild(summonVideo);
        }
    }

    /**
     * Hand a Stellar card back to its resting animation.
     *
     * This is THE re-arm path — losing the pointer, a summon clip ending, a cast
     * clip ending, a blocked cinematic — so it has to be the most robust play in
     * the file. A rejected `play()` used to just warn here, which meant a card
     * whose media was a moment from ready stayed dark for the rest of the
     * session; under parallel browser load that reproduced the original defect's
     * exact signature (`paused: true`, `opacity: 0`), and a slow phone is the
     * same machine. So a refusal arms a one-shot readiness retry.
     */
    function playLoopVideo(cardElement, { restart = true } = {}) {
        if (!cardElement) return;
        if (cinematicOwnsCard(cardElement)) return;
        const { loopVideo } = getStellarVideos(cardElement);
        if (!loopVideo) return;
        /* An indefinitely looping clip that starts by itself is what WCAG 2.2
           SC 2.2.2 governs. The one-shot summon/cast cinematics are deliberately
           NOT suppressed — they are bounded and they are gameplay feedback. */
        if (prefersReducedMotion()) {
            stopLoopVideo(loopVideo);
            return;
        }

        loopVideo.muted = true;
        loopVideo.volume = 0;
        loopVideo.loop = true;
        if (restart) {
            try { loopVideo.currentTime = 0; } catch (_) { }
        }
        loopVideo._stellarFallbackResume = () => playLoopVideo(cardElement, { restart: true });

        const armReadinessRetry = () => {
            if (loopVideo._stellarLoopRetryBound) return;
            loopVideo._stellarLoopRetryBound = true;
            const retry = () => {
                loopVideo.removeEventListener('canplay', retry);
                loopVideo.removeEventListener('loadeddata', retry);
                loopVideo._stellarLoopRetryBound = false;
                if (!cardElement.isConnected || !loopVideo.isConnected) return;
                playLoopVideo(cardElement, { restart: false });
            };
            loopVideo.addEventListener('canplay', retry);
            loopVideo.addEventListener('loadeddata', retry);
        };

        const handleRefusal = (err) => {
            const name = err && err.name;
            /* Only a DECODE problem justifies the MP4 fallback: swapping `src`
               discards the WebM for the session and re-downloads a clip already
               fetched under preload:'auto', once per card. An AbortError just
               means something interrupted the play (a `pause()` from
               suspendLoopForCinematic, or a `load()`), and a NotAllowedError is
               an autoplay policy refusal — neither says the file is unplayable.
               The readiness retry is safe in both cases: playLoopVideo returns
               early while a cinematic owns the card, so a retry cannot cut in. */
            if (name !== 'AbortError' && name !== 'NotAllowedError'
                && tryStellarMp4Fallback(loopVideo)) return;
            armReadinessRetry();
            if (name === 'AbortError') return;
            console.warn('Stellar loop video play blocked:', err);
        };

        try {
            const playPromise = loopVideo.play();
            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(handleRefusal);
            }
        } catch (err) {
            handleRefusal(err);
        }
    }

    /**
     * A one-shot cinematic owns the art well, so the passive loop underneath is
     * invisible for its whole duration — decoding it is pure waste, and on iOS,
     * where concurrent video decoders are scarce, it is worse than waste: a
     * second stream starves the first, which then reports `paused === false`
     * while its frames never advance. Measured on Playwright WebKit, the loop
     * came back from a summon frozen on one frame in exactly that state.
     * One decoder at a time. `playLoopVideo` resumes it when the clip ends.
     */
    function suspendLoopForCinematic(cardElement) {
        const { loopVideo } = getStellarVideos(cardElement);
        if (!loopVideo) return;
        loopVideo.dataset.playing = 'false';
        try { loopVideo.pause(); } catch (_) { }
    }

    /**
     * Hand the art well between the passive loop and a one-shot cinematic
     * without ever showing the static still in between.
     *
     * Measured before this existed: one blank frame per edge in Chromium and
     * two in WebKit — the outgoing layer hid before the incoming one had
     * rendered, so the card flashed its still art mid-ceremony. The rule is
     * simply that the incoming layer must be LIVE before the outgoing one goes:
     *   • entering — suspend the loop when the cinematic actually reports
     *     `playing`, not when we ask it to play;
     *   • leaving — restart the loop a fraction of a second BEFORE the
     *     cinematic ends. It runs unseen underneath (the cinematic sits a layer
     *     above) just long enough to reach its own `playing`.
     * The overlap is bounded to that lead, so the one-decoder-at-a-time rule
     * that keeps WebKit from starving the loop still holds for the whole clip.
     */
    const STELLAR_LOOP_HANDOVER_LEAD_S = 0.4;

    function bindCinematicLoopHandover(cinematicVideo) {
        cinematicVideo.addEventListener('playing', () => {
            cinematicVideo._stellarLoopPrimed = false;
            const cardElement = cinematicVideo.closest('.card');
            if (cardElement) suspendLoopForCinematic(cardElement);
        });

        cinematicVideo.addEventListener('timeupdate', () => {
            if (cinematicVideo._stellarLoopPrimed) return;
            const duration = Number(cinematicVideo.duration);
            if (!Number.isFinite(duration) || duration <= 0) return;
            if (duration - cinematicVideo.currentTime > STELLAR_LOOP_HANDOVER_LEAD_S) return;
            const cardElement = cinematicVideo.closest('.card');
            if (!cardElement) return;
            cinematicVideo._stellarLoopPrimed = true;
            const { loopVideo } = getStellarVideos(cardElement);
            if (!loopVideo) return;
            loopVideo.muted = true;
            loopVideo.volume = 0;
            loopVideo.loop = true;
            try {
                const started = loopVideo.play();
                if (started && typeof started.catch === 'function') started.catch(() => { });
            } catch (_) { }
        });
    }

    function startSummonPlaybackForEntry(cardElement, entry) {
        if (!entry || entry.started || !entry.video) return;
        const instanceId = entry.instanceId || entry.video.dataset.instanceId || '';
        if (instanceId && stellarSummonPlayed.has(instanceId)) return;

        entry.started = true;
        const summonVideo = entry.video;
        if (cardElement) {
            // The loop keeps running until the cinematic's own `playing` fires
            // (bindCinematicLoopHandover) so the swap never shows the still.
            cardElement.classList.add('stellar-summon-playing');
        }

        if (Soundtrack && typeof Soundtrack.duckVolume === 'function') {
            Soundtrack.duckVolume(STELLAR_SUMMON_DUCK_FACTOR, STELLAR_SUMMON_DUCK_MS);
        }

        const markPlayed = () => {
            if (instanceId) {
                stellarSummonPending.delete(instanceId);
                stellarSummonPlayed.add(instanceId);
            }
        };

        const markFailed = (err) => {
            if (instanceId) {
                stellarSummonPending.delete(instanceId);
                activeSummonVideos.delete(instanceId);
            }
            entry.started = false;
            if (cardElement) {
                cardElement.classList.remove('stellar-summon-playing');
                // The cinematic did not play. That is reported below, not
                // papered over — but the card still owes the player its resting
                // animation, so hand it back.
                playLoopVideo(cardElement, { restart: false });
            }
            if (err && err.name === 'AbortError') return;
            console.warn('Stellar summon video blocked:', err);
        };

        startSummonVideoPlayback(summonVideo, {
            restart: true,
            fadeIn: true,
            onStarted: markPlayed,
            onError: markFailed,
        });
    }

    function startSummonPlaybackWhenPresented(cardElement, entry) {
        if (!cardElement || !entry || entry.started || !entry.video) return;
        if (!cardElement.classList.contains('card-summon-art-pending')) {
            startSummonPlaybackForEntry(cardElement, entry);
            return;
        }
        if (entry.presentationPromise) return;

        const waitForPresentation = Board?.waitForCardPresentation;
        if (typeof waitForPresentation !== 'function') return;
        let presentation;
        try {
            presentation = waitForPresentation(entry.instanceId);
        } catch (_) {
            return;
        }
        if (!presentation || typeof presentation.then !== 'function') {
            startSummonPlaybackForEntry(cardElement, entry);
            return;
        }

        const pending = Promise.resolve(presentation)
            .then((presentedCard) => {
                if (entry.presentationPromise !== pending) return;
                entry.presentationPromise = null;
                if (activeSummonVideos.get(entry.instanceId) !== entry || entry.started) return;
                const liveCard = presentedCard?.isConnected
                    ? presentedCard
                    : findCardElementByInstanceId(entry.instanceId);
                if (!liveCard || liveCard.classList.contains('card-summon-art-pending')) return;
                attachSummonVideoToCard(liveCard, entry.video);
                entry.attachedTo = liveCard;
                startSummonPlaybackForEntry(liveCard, entry);
            })
            .catch(() => {
                if (entry.presentationPromise === pending) {
                    entry.presentationPromise = null;
                }
            });
        entry.presentationPromise = pending;
    }

    function startSummonVideoPlayback(summonVideo, { restart = true, fadeIn = true, onStarted, onError } = {}) {
        if (!summonVideo) return null;
        const sfxLevel = sfxEffectiveVolume();
        const muted = sfxLevel <= 0;
        const targetVolume = STELLAR_SUMMON_VOLUME * sfxLevel;
        summonVideo.muted = muted;
        summonVideo.loop = false;
        if (restart) {
            try { summonVideo.currentTime = 0; } catch (_) { }
        }
        if (fadeIn && !muted) {
            summonVideo.volume = 0;
        } else {
            summonVideo.volume = targetVolume;
        }
        summonVideo._stellarFallbackResume = () => startSummonVideoPlayback(summonVideo, {
            restart: true,
            fadeIn,
            onStarted,
            onError,
        });
        summonVideo._stellarFallbackFailure = onError;

        try {
            const playPromise = summonVideo.play();
            if (playPromise && typeof playPromise.then === 'function') {
                playPromise
                    .then(() => {
                        if (fadeIn && !muted) {
                            fadeMediaVolume(summonVideo, targetVolume, STELLAR_AUDIO_FADE_MS);
                        }
                        if (typeof onStarted === 'function') onStarted();
                    })
                    .catch(err => {
                        if (err && err.name === 'AbortError' && summonVideo.dataset.stellarFallbackUsed === 'true') return;
                        if (!muted && err && err.name === 'NotAllowedError') {
                            retrySummonPlaybackMuted(summonVideo, { onStarted, onError });
                            return;
                        }
                        if (tryStellarMp4Fallback(summonVideo)) return;
                        if (typeof onError === 'function') onError(err);
                    });
                return playPromise;
            }
            if (fadeIn && !muted) {
                fadeMediaVolume(summonVideo, targetVolume, STELLAR_AUDIO_FADE_MS);
            }
            if (typeof onStarted === 'function') onStarted();
        } catch (err) {
            if (err && err.name === 'AbortError' && summonVideo.dataset.stellarFallbackUsed === 'true') return null;
            if (!muted && err && err.name === 'NotAllowedError') {
                retrySummonPlaybackMuted(summonVideo, { onStarted, onError });
            } else if (tryStellarMp4Fallback(summonVideo)) {
                return null;
            } else if (typeof onError === 'function') {
                onError(err);
            }
        }
        return null;
    }

    // iOS/Safari reject unmuted autoplay outside a direct gesture with a
    // NotAllowedError. Retry the summon video exactly once with muted = true so
    // the summon VISUAL survives — only the audio is silently dropped. A second
    // denial (or any other error) falls through to the normal failure path; the
    // `!muted` guard on the callers makes this a single retry, never a loop.
    function retrySummonPlaybackMuted(summonVideo, { onStarted, onError } = {}) {
        summonVideo.muted = true;
        summonVideo.volume = 0;
        summonVideo._stellarFallbackResume = () => retrySummonPlaybackMuted(
            summonVideo,
            { onStarted, onError },
        );
        summonVideo._stellarFallbackFailure = onError;
        try {
            const retryPromise = summonVideo.play();
            if (retryPromise && typeof retryPromise.then === 'function') {
                retryPromise
                    .then(() => { if (typeof onStarted === 'function') onStarted(); })
                    .catch(err => {
                        if (err && err.name === 'AbortError' && summonVideo.dataset.stellarFallbackUsed === 'true') return;
                        if (tryStellarMp4Fallback(summonVideo)) return;
                        if (typeof onError === 'function') onError(err);
                    });
                return;
            }
            if (typeof onStarted === 'function') onStarted();
        } catch (err) {
            if (tryStellarMp4Fallback(summonVideo)) return;
            if (typeof onError === 'function') onError(err);
        }
    }

    function attachStagedSummonVideo(cardElement) {
        const instanceId = cardElement?.dataset?.instanceId || '';
        if (!instanceId) return false;
        // Never reparent the in-flight cinematic into a throwaway preview
        // clone — it would be destroyed with the popup (see setStellarHover).
        if (cardElement?.dataset?.stellarPreview) return false;
        const zone = cardElement?.dataset?.zone || '';
        if (zone === 'player-hand' || zone === 'opponent-hand') return false;
        const entry = activeSummonVideos.get(instanceId);
        if (!entry || !entry.video) return false;

        attachSummonVideoToCard(cardElement, entry.video);
        entry.attachedTo = cardElement;
        if (!entry.started && !stellarSummonPlayed.has(instanceId)) {
            startSummonPlaybackWhenPresented(cardElement, entry);
        } else if (!entry.video.paused && !entry.video.ended) {
            cardElement.classList.add('stellar-summon-playing');
        }
        return true;
    }

    function attachStagedSummonVideos() {
        activeSummonVideos.forEach((entry, instanceId) => {
            const cardElement = findCardElementByInstanceId(instanceId);
            if (!cardElement) return;
            if (cardElement.dataset?.stellarPreview) return;
            const zone = cardElement.dataset?.zone || '';
            if (zone === 'player-hand' || zone === 'opponent-hand') return;
            if (entry.attachedTo === cardElement) return;
            attachSummonVideoToCard(cardElement, entry.video);
            entry.attachedTo = cardElement;
            if (!entry.started && !stellarSummonPlayed.has(instanceId)) {
                startSummonPlaybackWhenPresented(cardElement, entry);
            } else if (!entry.video.paused && !entry.video.ended) {
                cardElement.classList.add('stellar-summon-playing');
            }
        });
    }

    function attachStellarVideo(cardElement, imageElement, card) {
        if (!cardElement || !imageElement) return;
        const isTestStellar = typeof cardElement.id === 'string' && cardElement.id.startsWith('stellar-');
        if (!isStellarCard(card) && !isTestStellar) return;

        cardElement.classList.add('stellar-card');
        cardElement.dataset.stellar = 'true';
        const loopVideoSrc = resolveStellarLoopVideoSource(card, isTestStellar);
        const summonVideoSrc = resolveStellarSummonVideoSource(card);
        const castVideoSrc = resolveStellarCastVideoSource(card);
        const loopFallbackSrc = resolveMp4Fallback(loopVideoSrc, card?.stellarVideoUrl);
        const summonFallbackSrc = resolveMp4Fallback(summonVideoSrc, card?.stellarSummonVideoUrl);
        const castFallbackSrc = resolveMp4Fallback(castVideoSrc, card?.stellarCastVideoUrl);
        if (loopVideoSrc) cardElement.dataset.stellarLoopVideo = loopVideoSrc;
        if (summonVideoSrc) cardElement.dataset.stellarSummonVideo = summonVideoSrc;
        if (castVideoSrc) cardElement.dataset.stellarCastVideo = castVideoSrc;
        if (loopFallbackSrc) cardElement.dataset.stellarLoopVideoFallback = loopFallbackSrc;
        if (summonFallbackSrc) cardElement.dataset.stellarSummonVideoFallback = summonFallbackSrc;
        if (castFallbackSrc) cardElement.dataset.stellarCastVideoFallback = castFallbackSrc;

        if (loopVideoSrc && !imageElement.querySelector('video.stellar-loop-video')) {
            const loopVideo = createStellarVideoElement(loopVideoSrc, 'stellar-video stellar-loop-video', {
                loop: true,
                muted: true,
                preload: 'auto',
                fallbackSrc: loopFallbackSrc,
            });
            bindStellarVideoReadiness(loopVideo);
            imageElement.appendChild(loopVideo);
            try { loopVideo.load(); } catch (_) { }
        }

        if (summonVideoSrc && !imageElement.querySelector('video.stellar-summon-video')) {
            const summonVideo = createStellarVideoElement(summonVideoSrc, 'stellar-video stellar-summon-video', {
                loop: false,
                muted: true,
                preload: 'auto',
                fallbackSrc: summonFallbackSrc,
            });
            summonVideo.dataset.instanceId = cardElement.dataset.instanceId || '';
            bindStellarVideoReadiness(summonVideo);
            bindSummonVideoEvents(summonVideo);
            imageElement.appendChild(summonVideo);
            try { summonVideo.load(); } catch (_) { }
        }

        if (castVideoSrc && !imageElement.querySelector('video.stellar-cast-video')) {
            const castVideo = createStellarVideoElement(castVideoSrc, 'stellar-video stellar-cast-video', {
                loop: false,
                muted: true,
                preload: 'auto',
                fallbackSrc: castFallbackSrc,
            });
            castVideo.dataset.instanceId = cardElement.dataset.instanceId || '';
            bindStellarVideoReadiness(castVideo);
            bindCastVideoEvents(castVideo);
            imageElement.appendChild(castVideo);
            try { castVideo.load(); } catch (_) { }
        }
    }

    function setStellarHover(cardElement, isHovered) {
        if (!cardElement || !cardElement.classList.contains('stellar-card')) return;

        const instanceId = cardElement.dataset.instanceId || '';
        const previewMode = cardElement.dataset.stellarPreview || '';
        const isCollectionPreview = previewMode === 'collection';

        // Preview clones (tap-inspection popup, catalog hovers) share the board
        // card's instanceId but must NEVER adopt the live staged summon video:
        // attachStagedSummonVideo would REPARENT the in-flight cinematic into
        // the throwaway preview and destroy it with the popup.
        if (instanceId && !previewMode && activeSummonVideos.has(instanceId)) {
            attachStagedSummonVideo(cardElement);
        }
        const { loopVideo, summonVideo, castVideo } = getStellarVideos(cardElement);
        const actionVideo = castVideo || summonVideo;
        const entry = instanceId ? activeSummonVideos.get(instanceId) : null;
        cardElement.classList.toggle('stellar-hover', isHovered);
        if (isHovered) {
            if (isCollectionPreview) {
                if (loopVideo) {
                    if (actionVideo) {
                        try { actionVideo.pause(); } catch (_) { }
                        try { actionVideo.currentTime = 0; } catch (_) { }
                        actionVideo.muted = true;
                        actionVideo.volume = STELLAR_SUMMON_VOLUME;
                    }
                    cardElement.classList.remove('stellar-summon-playing');
                    playLoopVideo(cardElement, { restart: true });
                    return;
                }

                if (summonVideo && !castVideo) {
                    cardElement.classList.add('stellar-summon-playing');
                    startSummonVideoPlayback(summonVideo, { restart: true, fadeIn: true });
                    return;
                }

                return;
            }

            if (entry && !entry.started && !stellarSummonPlayed.has(instanceId)) {
                startSummonPlaybackWhenPresented(cardElement, entry);
                return;
            }

            if (cinematicOwnsCard(cardElement)) {
                if (summonVideo) {
                    const sfxLevel = sfxEffectiveVolume();
                    summonVideo.muted = sfxLevel <= 0;
                    summonVideo.volume = STELLAR_SUMMON_VOLUME * sfxLevel;
                }
                return;
            }

            playLoopVideo(cardElement, { restart: false });
            return;
        }

        if (isCollectionPreview) {
            if (actionVideo) {
                try { actionVideo.pause(); } catch (_) { }
                try { actionVideo.currentTime = 0; } catch (_) { }
                actionVideo.muted = true;
                actionVideo.volume = STELLAR_SUMMON_VOLUME;
            }
            cardElement.classList.remove('stellar-summon-playing');
            /* Same rule as every other surface: losing the pointer hands the
               card back to its resting animation, it does not switch it off.
               `data-stellar-preview` is NOT a throwaway-clone marker —
               card-packs.js stamps it on the revealed fan card, which lives for
               the whole pack modal, so stopping here left a just-opened Stellar
               dark after one hover ("two Stellars, only one animates"). A
               genuine popup clone is removed in the same task and the UA
               releases a detached element's decoder anyway. */
            playLoopVideo(cardElement, { restart: false });
            return;
        }

        // A LIVE cinematic keeps the card; a stale class is repaired and the
        // card is handed back, so a detached clip cannot strand it all match.
        if (cinematicOwnsCard(cardElement)) return;

        // NOT stopLoopVideo(). A Stellar card animates at rest, so losing the
        // pointer hands it back to its passive loop rather than switching it
        // off — the whole defect was that this seam meant "stop animating".
        playLoopVideo(cardElement, { restart: false });
    }

    function queueStellarSummonPlayback(
        cardInstanceId,
        isStellar,
        summonVideoUrl = '',
        summonVideoFallbackUrl = '',
    ) {
        if (!cardInstanceId || !isStellar || !summonVideoUrl) return;
        if (stellarSummonPlayed.has(cardInstanceId) || stellarSummonPending.has(cardInstanceId)) return;

        let cardElement = findCardElementByInstanceId(cardInstanceId);
        summonVideoUrl = cardElement?.dataset?.stellarSummonVideo || summonVideoUrl;
        summonVideoFallbackUrl = cardElement?.dataset?.stellarSummonVideoFallback
            || summonVideoFallbackUrl;
        const zone = cardElement?.dataset?.zone || '';
        if (zone === 'player-hand' || zone === 'opponent-hand') {
            cardElement = null;
        }
        let summonVideo = cardElement ? cardElement.querySelector('video.stellar-summon-video') : null;

        if (!summonVideo) {
            summonVideo = createStellarVideoElement(summonVideoUrl, 'stellar-video stellar-summon-video', {
                loop: false,
                muted: true,
                preload: 'auto',
                fallbackSrc: summonVideoFallbackUrl,
            });
        }

        summonVideo.dataset.instanceId = cardInstanceId;
        bindStellarVideoReadiness(summonVideo);
        bindSummonVideoEvents(summonVideo);

        if (cardElement) {
            cardElement.dataset.stellarSummonVideo = summonVideoUrl;
            if (summonVideoFallbackUrl) {
                cardElement.dataset.stellarSummonVideoFallback = summonVideoFallbackUrl;
            }
            attachSummonVideoToCard(cardElement, summonVideo);
        } else {
            const stagingRoot = ensureSummonStagingContainer();
            if (stagingRoot && summonVideo.parentElement !== stagingRoot) {
                stagingRoot.appendChild(summonVideo);
            }
        }

        activeSummonVideos.set(cardInstanceId, {
            video: summonVideo,
            attachedTo: cardElement || null,
            instanceId: cardInstanceId,
            started: false,
        });

        stellarSummonPending.add(cardInstanceId);
        if (cardElement) {
            startSummonPlaybackWhenPresented(cardElement, activeSummonVideos.get(cardInstanceId));
        }
    }

    function boundedSetAdd(set, value) {
        set.add(value);
        if (set.size <= MAX_STELLAR_CAST_TRACKING) return;
        set.delete(set.values().next().value);
    }

    function normalizeCastActionId(value) {
        const actionId = typeof value === 'string' ? value.trim() : '';
        return actionId && actionId.length <= 128 ? actionId : '';
    }

    function isStellarSpellCastSource(value) {
        if (!value) return false;
        if (value.nodeType === 1) {
            return value.dataset?.type === 'Spell'
                && value.dataset?.stellar === 'true'
                && Boolean(value.dataset?.stellarCastVideo);
        }
        return value.type === 'Spell'
            && isStellarCard(value)
            && Boolean(resolveStellarCastVideoSource(value));
    }

    function shouldDeferSpellCastSound(value) {
        return isStellarSpellCastSource(value);
    }

    /**
     * Remember an accepted Stellar Spell action so its synchronized cast clip
     * replaces the later generic story-resolution sound exactly once.
     */
    function registerStellarSpellCast(data) {
        const actionId = normalizeCastActionId(data?.actionId);
        if (!actionId || !isStellarSpellCastSource(data?.card)) return false;
        stellarCastSoundSuppressions.set(actionId, data?.cardInstanceId || data.card?.instanceId || '');
        if (stellarCastSoundSuppressions.size > MAX_STELLAR_CAST_TRACKING) {
            stellarCastSoundSuppressions.delete(stellarCastSoundSuppressions.keys().next().value);
        }
        return true;
    }

    function findStellarSpellCastSoundSuppression(data) {
        const actionId = normalizeCastActionId(data?.actionId);
        if (actionId && stellarCastSoundSuppressions.has(actionId)) return actionId;
        const instanceId = typeof (data?.cardInstanceId || data?.sourceCardId) === 'string'
            ? (data.cardInstanceId || data.sourceCardId)
            : '';
        if (!instanceId) return null;
        for (const [trackedActionId, trackedInstanceId] of stellarCastSoundSuppressions) {
            if (trackedInstanceId === instanceId) return trackedActionId;
        }
        return null;
    }

    function hasStellarSpellCastSoundSuppression(data) {
        if (!data) return stellarCastSoundSuppressions.size > 0;
        return findStellarSpellCastSoundSuppression(data) !== null;
    }

    function consumeStellarSpellCastSoundSuppression(data) {
        const trackedActionId = findStellarSpellCastSoundSuppression(data);
        return trackedActionId ? stellarCastSoundSuppressions.delete(trackedActionId) : false;
    }

    function rejectStellarSpellCast(data) {
        const actionId = normalizeCastActionId(typeof data === 'string' ? data : data?.actionId);
        if (actionId) stellarCastSoundSuppressions.delete(actionId);
    }

    function resetStellarSpellCastTracking() {
        stellarCastPlayed.clear();
        stellarCastSoundSuppressions.clear();
    }

    /** Play the Spell action clip only after the server broadcasts acceptance. */
    function playStellarSpellCast(cardElement, actionId) {
        const normalizedActionId = normalizeCastActionId(actionId);
        if (!normalizedActionId || !isStellarSpellCastSource(cardElement)) return false;
        if (stellarCastPlayed.has(normalizedActionId)) return false;
        const { castVideo } = getStellarVideos(cardElement);
        if (!castVideo) return false;

        boundedSetAdd(stellarCastPlayed, normalizedActionId);
        cardElement.classList.add('stellar-cast-playing');
        bindCastVideoEvents(castVideo);

        if (Soundtrack?.duckVolume) {
            Soundtrack.duckVolume(STELLAR_SUMMON_DUCK_FACTOR, STELLAR_CAST_DUCK_MS);
        }

        startSummonVideoPlayback(castVideo, {
            restart: true,
            fadeIn: false,
            onError: (error) => {
                finishStellarSpellCast(cardElement);
                if (error?.name !== 'AbortError') {
                    console.warn('Stellar cast video blocked:', error);
                }
            },
        });
        return true;
    }

    function extractStellarSummonMap(cards) {
        const map = new Map();
        if (!Array.isArray(cards)) return map;
        cards.forEach(card => {
            if (!card || !card.instanceId) return;
            if (!isStellarCard(card)) return;
            const summonVideoUrl = resolveStellarSummonVideoSource(card);
            if (!summonVideoUrl) return;
            map.set(card.instanceId, {
                source: summonVideoUrl,
                fallback: resolveMp4Fallback(summonVideoUrl, card.stellarSummonVideoUrl),
            });
        });
        return map;
    }

    function syncStellarBoardSummons(gameState) {
        if (!gameState) return;
        const nextPlayer = extractStellarSummonMap(gameState.player?.summonedCards);
        const nextOpponent = extractStellarSummonMap(gameState.opponent?.summonedCards);

        if (!stellarBoardSnapshot) {
            stellarBoardSnapshot = { player: nextPlayer, opponent: nextOpponent };
            return;
        }

        const prevPlayer = stellarBoardSnapshot.player || new Map();
        const prevOpponent = stellarBoardSnapshot.opponent || new Map();

        nextPlayer.forEach((summonMedia, id) => {
            if (!prevPlayer.has(id)) {
                queueStellarSummonPlayback(id, true, summonMedia.source, summonMedia.fallback);
            }
        });
        nextOpponent.forEach((summonMedia, id) => {
            if (!prevOpponent.has(id)) {
                queueStellarSummonPlayback(id, true, summonMedia.source, summonMedia.fallback);
            }
        });

        stellarBoardSnapshot = { player: nextPlayer, opponent: nextOpponent };
    }
    // Debug helper function to prevent variable redeclaration
    function logDebugInfo(elementId, compareId) {
        if (!DEBUG_ENABLED) return;

        const element = document.getElementById(elementId);
        const compareElement = compareId ? document.getElementById(compareId) : null;

        if (!element) return;

        const elementRect = element.getBoundingClientRect();
        const compareRect = compareElement ? compareElement.getBoundingClientRect() : null;

        console.log(`\n🔍 DEBUG INFO FOR ${elementId}:`);
        console.log('Element rect:', elementRect);
        if (compareRect) {
            console.log('Comparison with', compareId, 'rect:', compareRect);
            console.log('Difference (bottom):', compareRect.bottom - elementRect.bottom);
        }

        console.log('Computed styles:');
        const styles = window.getComputedStyle(element);
        ['position', 'bottom', 'top', 'left', 'right', 'display', 'height', 'width', 'overflow', 'z-index'].forEach(prop => {
            console.log(`- ${prop}: ${styles[prop]}`);
        });
    }

    /**
     * Create a card element with proper styling and content
     * @param {Object} card - Card data object
     * @param {String} uniqueId - Unique ID for the card element
     * @param {Object} [options] - Surface-specific rendering and ownership options
     * @param {boolean} [options.touchPreview=true] - Whether the card owns generic touch inspection
     * @returns {HTMLElement} - The created card element
     */
    function createCardElement(card, uniqueId, options = {}) {
        const imageCandidates = resolveCardImageCandidates(card);
        const rasterReserveScale = normalizeRasterReserveScale(options.rasterReserveScale);
        const ownsTouchPreview = options.touchPreview !== false;
        if (imageCandidates.directUrls.length === 0 && imageCandidates.assetIds.length === 0) {
            console.warn(`⚠️ No picture defined for card: ${card.name} (id: ${card.id})`);
        }

        // Create the main card element
        const cardElement = document.createElement('div');
        cardElement.dataset.cardRasterReserveScale = `${rasterReserveScale}`;
        if (options.directArtOnly === true) {
            cardElement.dataset.cardDirectArtOnly = 'true';
        }
        // Base classes
        cardElement.className = 'card card-front';
        // Add subtype class for different front frames
        if (card.type === 'Spell') {
            cardElement.classList.add('spell-card');
        } else if (card.type === 'Character') {
            cardElement.classList.add('character-card');
        }
        // The shimmer is an explicit Moon Fuse interim state, never a generic
        // network/error fallback. A finished direct URL may arrive through
        // either imageUrl or picture, so decide from the sanitized candidates
        // rather than from the truthiness of one raw field.
        const showMoonFuseLoadingPlaceholder = isMoonFuseCardInProgress(card)
            && !hasImmediateMoonFuseArt(card, imageCandidates);
        if (showMoonFuseLoadingPlaceholder) {
            cardElement.classList.add('moon-fuse-card-loading');
        }

        // Store card data as attributes for easy access in event handlers
        cardElement.id = uniqueId; // Unique DOM id for styling / lookup
        cardElement.dataset.instanceId = card.instanceId; // Use real instanceId for game logic
        // Store unique instance ID for gameplay actions
        cardElement.dataset.cardId = card.instanceId; // Use instanceId so targeting actions reference unique card copies
        cardElement.dataset.type = card.type;
        if (card.type === 'Spell') {
            cardElement.dataset.targetingType = card.targetingType;
        }
        // Store mana cost for quick access when evaluating drag eligibility
        cardElement.dataset.manaCost = card.manaCost;
        if (card.type === 'Character') {
            // Characters might have specific states like 'awakened' later, store if necessary
            // For now, just ensuring type is there is enough.
        }

        // Create cost element
        const costElement = document.createElement('div');
        costElement.className = 'card-cost';
        costElement.textContent = card.manaCost;

        // Create name element
        const nameElement = document.createElement('div');
        nameElement.className = 'card-name';
        nameElement.textContent = card.name;

        // Size the title to the width it actually occupies. See fitCardTitle:
        // the estimate (or this card's remembered fit) lands the first painted
        // frame, measurement corrects it once the card is in the document.
        const titleMemoryKey = titleFitMemoryKey(cardElement, card.name);
        applyEstimatedTitleRatio(nameElement, titleMemoryKey);
        scheduleTitleFit(nameElement, titleMemoryKey);



        // Create image container - USING BACKGROUND IMAGE APPROACH
        const imageElement = document.createElement('div');
        imageElement.className = 'card-image';
        if (showMoonFuseLoadingPlaceholder) {
            imageElement.classList.add('card-image-loading-placeholder');
            imageElement.setAttribute('aria-busy', 'true');
        }

        // Apply direct styling to ensure image visibility
        // Card anatomy must follow the same local unit as the outer frame.
        // This remains exact at the default 1vmin token, the phone px floor,
        // and high-resolution Collection counter-scaling.
        // The floor for the art window, in the axis it floors. It was
        // `var(--card-width)` — 12.29 units against a height of 12.321 — a
        // value from the OTHER axis that happens to sit 0.25% below the design.
        imageElement.style.minHeight = 'calc(var(--card-height) * 0.555)';
        imageElement.style.display = 'flex';
        imageElement.style.justifyContent = 'center';
        imageElement.style.alignItems = 'center';
        imageElement.style.position = 'relative';
        imageElement.style.overflow = 'visible';
        imageElement.style.zIndex = '1'; // Ensure it's below the card front
        if (showMoonFuseLoadingPlaceholder) {
            imageElement.style.overflow = 'hidden';
        }

        // Create an actual image element instead of using background image
        const img = document.createElement('img');
        img.className = 'card-art-paint';
        img.dataset.cardArtRasterOversample = `${rasterReserveScale}`;
        img.style.height = 'auto';
        img.style.objectFit = 'cover';
        img.style.zIndex = '1';

        const syncCardImagePresentation = () => {
            const currentSrc = typeof img.currentSrc === 'string' && img.currentSrc
                ? img.currentSrc
                : img.getAttribute('src') || '';
            if (!currentSrc) {
                img.classList.remove('character-portrait');
                return;
            }

            imageElement.style.backgroundImage = 'none';
            if (img.classList.contains('placeholder-art')) {
                img.classList.remove('character-portrait');
                return;
            }
            if (card.type === 'Character') {
                img.classList.add('character-portrait');
            } else {
                img.classList.remove('character-portrait');
            }
        };

        img.addEventListener('load', syncCardImagePresentation);
        imageElement.appendChild(img);

        if (showMoonFuseLoadingPlaceholder) {
            useSharedArtRequestMode(img, CARD_IMAGE_PLACEHOLDER);
            img.src = CARD_IMAGE_PLACEHOLDER;
            img.classList.add('placeholder-art', 'moon-fuse-placeholder-art');
        } else {
            loadCardImageWithFallbacks(img, imageCandidates, cardArtProfile({
                // One edge-to-edge crop is shared by Character/Spell and
                // normal/Stellar cards. The direct source, sampled bitmap, and
                // moving Stellar frame therefore have identical composition;
                // no surface can introduce letterbox seams or a hover jump.
                fit: 'cover',
                oversample: rasterReserveScale,
            }), {
                directArtOnly: options.directArtOnly === true,
            });
        }

        attachStellarVideo(cardElement, imageElement, card);
        autoPlayStellarVideo(cardElement);

        // Card description
        const descElement = document.createElement('div');
        descElement.className = 'card-desc';

        // Add description text and implement dynamic font scaling
        if (card.description && descElement) {
            descElement.textContent = card.description;
        } else if (descElement) {
            descElement.textContent = ''; // Clear description if none exists
        }

        // Assemble card components
        cardElement.appendChild(costElement);
        cardElement.appendChild(nameElement);
        cardElement.appendChild(imageElement);
        cardElement.appendChild(descElement);

        if (rasterReserveScale > 1) {
            // Touch: tap enlarges; a second tap acts only where the surface's own
            // click does (a catalog's) — never in battle, where cards are played by
            // dragging. The guard above already admits the
            // synthetic (untrusted) mouseenter this dispatches while rejecting the
            // compatibility one a real tap produces.
            if (ownsTouchPreview) {
                TouchPreview?.attach?.(cardElement);
            }
            cardElement.addEventListener('mouseenter', () => lockCardRaster(cardElement, 'hover'));
            cardElement.addEventListener('focusin', () => lockCardRaster(cardElement, 'hover'));
            cardElement.addEventListener('mouseleave', () => releaseHoverRasterAuthority(cardElement));
            cardElement.addEventListener('focusout', (event) => {
                if (!cardElement.contains(event.relatedTarget)) {
                    releaseHoverRasterAuthority(cardElement);
                }
            });
        }

        // --- Hint container (for hover hints; content set dynamically) ---
        const hintElement = document.createElement('div');
        hintElement.className = 'card-hint';
        cardElement.appendChild(hintElement);

        // Apply dynamic font scaling now that elements are attached
        if (card.description) {
            fitCardDescription(cardElement, options);
        }

        // --- Status Display ---
        if (card.status && card.status.trim() !== '') {
            const statusElement = document.createElement('div');
            statusElement.className = 'card-status';
            /* Cut by the SHARED rule (status-segments.js), not a local one.
               This split on `;` alone while the summoner rail had already been
               taught that the storyteller also separates with a PIPE, so the
               same string produced a clean summoner plate and a card chip whose
               first glyph was a raw `|`. */
            const parts = splitStatusSegments(card.status);
            /* Shared with the reaction layer (status-segments.js). Inline, the
               marker test ran before the period test, so `**Shielded**.` kept
               its asterisks and the player read them. */
            const cleanedParts = parts.map(cleanStatusToken).filter(Boolean);

            // Build status display using safe DOM methods (XSS prevention)
            cleanedParts.forEach((name, index) => {
                const span = document.createElement('span');
                span.setAttribute('data-name', name);
                // Shared with the summoner rail and the storyboard prose so one
                // effect keeps one colour, counter or no counter.
                const color = (StoryFeed && typeof StoryFeed.getStatusColor === 'function')
                    ? StoryFeed.getStatusColor(name)
                    : '';
                if (color) {
                    span.style.color = color;
                }
                span.textContent = name; // Safe: uses textContent instead of innerHTML
                // Held back on the RESTING card only. cards.css reveals these
                // on every enlarged/side-rail presentation, so nothing the
                // server sent becomes unreachable — it costs one hover.
                if (index >= STATUS_PARTS_ON_RESTING_CARD) {
                    span.classList.add('card-status-overflow');
                }
                statusElement.appendChild(span);
            });
            /* NO `<br>` BETWEEN THE SPANS. The plate is a FLEX CONTAINER in
               both of its presentations (cards.css) and lays the spans out on
               its own — a `row` at rest, a `column` once enlarged — while
               Chromium and WebKit made each `<br>` its OWN anonymous flex item
               carrying the container `gap` and Firefox did not. The identical
               two-status card measured 28px of plate in Chromium and 21px in
               Firefox, exactly the inconsistency "cards must look identical
               everywhere" forbids, and it is why three different measurements
               of how much of the description the plate used to eat disagreed.

               THE DIRECTION MATTERS AND THIS COMMENT USED TO GET IT WRONG. It
               said "column" full stop, which was true until 8fa5abfd held the
               resting plate to one line. A row's horizontal axis is
               `justify-content`, not `align-items`, so the centring quietly
               moved to a property nobody had set and every chip packed left —
               "caRD status fields on desktop uncentered". Anyone reading this
               to work out why `text-align: center` was not centring anything
               would have been sent the wrong way. */
            if (cleanedParts.length > STATUS_PARTS_ON_RESTING_CARD) {
                const more = document.createElement('span');
                more.className = 'card-status-more';
                more.textContent = `+${cleanedParts.length - STATUS_PARTS_ON_RESTING_CARD}`;
                statusElement.appendChild(more);
            }
            cardElement.appendChild(statusElement);

            // DEBUG: Log status element position when card is hovered or enlarged.
            // The layout reads live BEHIND the flag: getBoundingClientRect and
            // getComputedStyle force a style+layout pass on the same hover path
            // that runs the zoom animation — production hovers must pay nothing.
            // Touch: tap enlarges; a second tap acts only where the surface's own
            // click does (a catalog's) — never in battle, where cards are played by
            // dragging. The guard above already admits the
            // synthetic (untrusted) mouseenter this dispatches while rejecting the
            // compatibility one a real tap produces.
            if (ownsTouchPreview) {
                TouchPreview?.attach?.(cardElement);
            }
            cardElement.addEventListener('mouseenter', (event) => {
                if (event?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
                if (!window.CLIENT_DEBUG_MODE || !statusElement) return;
                const rect = statusElement.getBoundingClientRect();
                const comp = window.getComputedStyle(statusElement);
                console.log('[CARD-STATUS DEBUG] Enter card', card.name, {
                    hoverActive: cardElement.classList.contains('hover-active'),
                    opacity: comp.opacity,
                    position: comp.position,
                    left: comp.left,
                    top: comp.top,
                    bottom: comp.bottom,
                    right: comp.right,
                    transform: comp.transform,
                    rect
                });
            });
        }

        // Return the completed card element
        return cardElement;
    }

    /* Every field createCardElement above actually PAINTS, in a fixed order.
       Two cards with the same signature produce byte-identical markup, so a
       surface that already holds a painted element can keep it instead of
       rebuilding one — which is what renderBoard does with established board
       Characters (see board.js). The list mirrors the server's client card
       projection (game/utils/clientCardProjection.js) plus the Moon Fuse forge
       fields that decide the interim placeholder; `canAttack` and `revealed`
       are deliberately absent because nothing in the card's face reads them.
       `stellar` is the legacy spelling `isStellarCard` still accepts — it is
       NOT on the wire, so only a walk of what the face READS can catch it.
       tests/unit/ui/boardCardReuse.test.js runs createCardElement over a
       proxied card and fails if it touches a field that is missing here — a
       missing field would freeze stale pixels on a card whose data moved on. */
    const CARD_RENDER_SIGNATURE_FIELDS = [
        'instanceId',
        'id',
        'name',
        'description',
        'manaCost',
        'type',
        'targetingType',
        'status',
        'picture',
        'imageUrl',
        'isStellar',
        'stellar',
        'stellarVideoUrl',
        'stellarVideoWebmUrl',
        'stellarSummonVideoUrl',
        'stellarSummonVideoWebmUrl',
        'stellarCastVideoUrl',
        'stellarCastVideoWebmUrl',
        'forgeStatus',
        'collection',
        'origin',
    ];

    /**
     * A comparable identity for the card face `createCardElement(card, _, options)`
     * would build. Returns null for a missing card so a caller can never treat
     * "nothing to compare" as "unchanged".
     */
    function cardRenderSignature(card, options = {}) {
        if (!card || typeof card !== 'object') return null;
        return JSON.stringify([
            ...CARD_RENDER_SIGNATURE_FIELDS.map((field) => {
                const value = card[field];
                return value === undefined || value === null ? null : String(value);
            }),
            normalizeRasterReserveScale(options.rasterReserveScale),
            options.directArtOnly === true,
        ]);
    }

    /**
     * Render a player's hand
     * @param {Array} cards - Array of card objects
     */
    // In-game cohort reveal (docs/asset-loading.md grace rule): a rebuilt
    // group of cards appears TOGETHER — still-loading members are held until
    // every member settles or the shared cap releases them — so a deal never
    // trickles in one by one. Already-settled members (transferred paints)
    // are never held or re-hidden. Mirrors --card-art-grace.
    const CARD_COHORT_REVEAL_CAP_MS = 2500;

    function holdCardCohortUntilSettled(cardElements, { capMs = CARD_COHORT_REVEAL_CAP_MS } = {}) {
        const held = (cardElements || []).filter((cardElement) => (
            cardElement?.querySelector?.('.card-image > img.card-art-loading')
            /* Drawn cards have their own arrival choreography — hidden while
               their clone flies, revealed as it lands. Adding the art hold on
               top made the card land, vanish, and pop back when the group's
               art settled. The art-well swap stays gated per asset-loading
               law 2; only the ELEMENT hold is skipped. */
            && cardElement.dataset.fxDrawArrival !== '1'
        ));
        if (held.length < 2) return;
        held.forEach((cardElement) => cardElement.classList.add('card-cohort-hold'));
        const release = () => held.forEach((cardElement) => (
            cardElement.classList.remove('card-cohort-hold')
        ));
        const capTimer = setTimeout(release, capMs);
        const settlements = held.map((cardElement) => {
            const img = cardElement.querySelector('.card-image > img.card-art-paint');
            const readiness = img ? pendingCardArtReadiness.get(img) : null;
            const artSettled = readiness?.promise
                ? Promise.resolve(readiness.promise).catch(() => false)
                : Promise.resolve(true);
            // A hand card is revealed with its rest view (card-rest-view.js).
            return artSettled.then((settled) => CardRestView.whenSettled(cardElement, capMs).then(() => settled));
        });
        Promise.all(settlements).then(() => {
            clearTimeout(capTimer);
            release();
        });
    }

    /* AFFORDABILITY, as a function of current state rather than a side effect of
       rendering. It used to be decided once per card while the hand was being
       built, so anything that removed the class left it gone until the next
       server update rebuilt the hand — and a late or dropped update left the
       glow describing mana the player no longer had. Both surfaced as
       "I have summonable cards but they don't have the green hue".
       Locked by tests/unit/ui/cards.legalPlayRefresh.test.js. */
    function isCardLegalToPlay(card, state) {
        if (!card || !state || !state.isYourTurn || state.state === 'mulligan') return false;
        const mana = state.player && typeof state.player.mana !== 'undefined' ? state.player.mana : 0;
        const boardUsed = state.player && Array.isArray(state.player.summonedCards)
            ? state.player.summonedCards.length
            : 0;
        const cost = Number(card.manaCost);
        if (!Number.isFinite(cost) || mana < cost) return false;
        return card.type === 'Character' ? boardUsed < 5 : true;
    }

    function readGameStateForHighlights() {
        try {
            return (typeof GameState !== 'undefined' && GameState.get) ? GameState.get() : null;
        } catch (err) {
            return null;
        }
    }

    /* Re-derive the glow for every card currently in the hand. Cheap (a class
       toggle per card), idempotent, and safe to call from anywhere that may
       have changed mana, board occupancy, or the DOM. */
    function refreshLegalPlayHighlights() {
        const state = readGameStateForHighlights();
        const hand = state && state.player && Array.isArray(state.player.hand)
            ? state.player.hand
            : [];
        const byInstance = new Map(hand.map((card) => [String(card.instanceId), card]));
        document.querySelectorAll('#player-hand .card').forEach((node) => {
            const el = /** @type {HTMLElement} */ (node);
            const card = byInstance.get(String(el.dataset.instanceId || ''));
            el.classList.toggle('legal-play', isCardLegalToPlay(card, state));
        });
    }

    /* THE HAND CLOSES AND OPENS LIKE A HAND.
       Every snapshot rebuilds the fan, and a card leaving it (played, summoned)
       or joining it (drawn) re-seats its neighbours: each one takes a new place
       AND a new fan angle. Rebuilt in place, the survivors jumped there in one
       frame. Now each survivor starts exactly where, and at the angle, it was
       painted a moment ago and glides into its new seat.

       Measured from the LAYOUT seat (offset box: the fan pivots about each
       card's bottom centre) and the fan angle, never from the painted box,
       which rotation distorts. Played through the individual `translate` and
       `rotate` properties, which compose with the card's own transform stack
       (fan, hover, tilt) instead of replacing it. Presentation only. */
    const HAND_REFLOW_MS = 300;
    const HAND_REFLOW_EASING = 'cubic-bezier(0.22, 0.8, 0.28, 1)';

    function handSeatOf(cardElement) {
        if (!cardElement?.isConnected || cardElement.style.display === 'none') return null;
        if (cardElement.style.visibility === 'hidden' || cardElement.classList.contains('dragging')) return null;
        if (cardElement.classList.contains('hover-active')) return null;
        let angle = 0;
        try {
            const transform = getComputedStyle(cardElement).transform;
            const Matrix = window.DOMMatrixReadOnly || window.DOMMatrix;
            if (transform && transform !== 'none' && typeof Matrix === 'function') {
                const matrix = new Matrix(transform);
                angle = Math.atan2(matrix.b, matrix.a) * 180 / Math.PI;
            }
        } catch (_) { angle = 0; }
        return {
            x: cardElement.offsetLeft + cardElement.offsetWidth / 2,
            y: cardElement.offsetTop + cardElement.offsetHeight,
            angle: Number.isFinite(angle) ? angle : 0,
        };
    }

    function captureHandSeats(cardElements) {
        const seats = new Map();
        cardElements.forEach((cardElement) => {
            const id = cardElement?.dataset?.instanceId;
            const seat = id ? handSeatOf(cardElement) : null;
            if (!seat) return;
            // A card the hand holds for a deal is painted at its glide's first frame.
            const held = heldGlideOffsets.get(handHold?.glides.get(cardElement));
            seats.set(String(id), held
                ? { x: seat.x + held.dx, y: seat.y + held.dy, angle: seat.angle + held.turn }
                : seat);
        });
        return seats;
    }

    function prefersReducedHandMotion() {
        try {
            return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
        } catch (_) {
            return false;
        }
    }

    /* The hand hover engine caches every card's box shortly after a rebuild —
       mid-glide, now. It re-measures when told the glide has ended. */
    let handGlideSettleTimer = 0;
    function announceHandSettledAfter(ms) {
        clearTimeout(handGlideSettleTimer);
        handGlideSettleTimer = setTimeout(() => {
            window.dispatchEvent(new CustomEvent('moonfall:hand-reflowed'));
        }, ms);
    }

    /* A GLIDE STARTS ON THE FRAME THAT PAINTS IT (round 17, 28 September
       2026). WebKit starts an animation's clock when it is created; Chrome and
       Firefox start it at the frame that first paints it. The snapshot that
       re-seats the hand keeps the main thread for another 100-230 ms on the
       meter's WebKit phone seat, so the first frame showed each glide 115-187
       ms in, most of the way along its ease-out: the kept cards jumped
       (opponent-turn 15 morphs, summon 5 more than main). Each glide is held
       at its first frame and its clock started in the next animation frame,
       the one that paints it; one cancelled meanwhile stays cancelled.
       `onStarted` runs there too, so what waits for the landing counts from
       the start. Contract: tests/unit/ui/cards.glideStartsOnItsFirstFrame.test.js. */
    function startGlidesOnTheirFirstFrame(animations, onStarted) {
        const held = animations.filter((animation) => {
            try {
                animation.pause();
                return true;
            } catch (_) {
                return false; // it runs as created
            }
        });
        const start = () => {
            held.forEach((animation) => {
                try {
                    if (animation.playState === 'paused') animation.play();
                } catch (_) { /* gone with its card */ }
            });
            onStarted?.();
        };
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(start);
        else start();
    }

    /* THE HAND HOLDS STILL WHILE A DRAW WAITS FOR ITS PICTURE (round 17
       follow-up, 28 September 2026). A drawn card waits up to the battle's
       arrival wait for a picture already on the wire (renderPlayerHand, "THE
       FACE FLIES READY"). The kept cards used to glide apart at once, so on a
       slow link the drawn card's seat stood open as an empty gap for up to
       2 s: a hole in the hand, which reads as stuck, and a glide seconds
       apart from the card that finally flew into it (the lead: keep the
       seat-opening glide together with the card's entry). While a deal waits,
       every re-seat glide is held at its first frame, the card painted where
       it was, and plays in the frame the deal launches, with the flight: the
       seat opens as the card arrives. A snapshot meanwhile re-seats from where
       each card is painted and keeps holding; a card the player plays or
       brings back meanwhile (reflowHand) releases the hold, since the hand
       answers the player at once; the deal's cap still launches it, and no
       hold outlives its deal. Contract: tests/unit/ui/cards.handHoldsForTheDeal.test.js. */
    let handHold = null; // { deal, clock, glides: Map<card, Animation>, safety }
    const heldGlideOffsets = new WeakMap(); // a held glide -> its first frame { dx, dy, turn }
    let dealCount = 0;
    let handHoldWaivedFor = null; // the deal clock the player's own action released

    function holdHandForDeal(deal, clock) {
        if (!handHold) handHold = { deal, clock, glides: new Map(), safety: 0 };
        handHold.deal = deal;
        handHold.clock = clock;
        clearTimeout(handHold.safety);
        handHold.safety = setTimeout(() => releaseHandHold(), ARRIVAL_ART_WAIT_CAP_MS + 500);
    }

    /** End the hold. Its glides play now, or with `collect` are handed back unplayed. */
    function releaseHandHold({ collect = false, cancel = false } = {}) {
        const hold = handHold;
        handHold = null;
        if (!hold) return [];
        clearTimeout(hold.safety);
        const glides = [];
        hold.glides.forEach((glide, cardElement) => {
            if (glide.playState === 'idle') return;
            // A card that left the hand takes its glide with it.
            if (cancel || !cardElement.isConnected) {
                try { glide.cancel(); } catch (_) { /* gone */ }
                return;
            }
            glides.push(glide);
        });
        if (collect) return glides;
        glides.forEach((glide) => {
            try {
                if (glide.playState === 'paused') glide.play();
            } catch (_) { /* gone with its card */ }
        });
        if (glides.length) announceHandSettledAfter(HAND_REFLOW_MS + 30);
        return [];
    }

    /** The player took a card from a hand held for a deal: it answers them now, for the rest of that wait. */
    function releaseHandForPlayer() {
        if (!handHold) return;
        handHoldWaivedFor = handHold.clock;
        releaseHandHold();
    }

    /** Glide every card that changed seat since `before` into its new seat. */
    function glideHandFrom(before, { holdForDeal = false } = {}) {
        if (!before || !before.size || prefersReducedHandMotion()) {
            if (!holdForDeal) releaseHandHold();
            return;
        }
        const held = handHold?.glides || null;
        const glides = [];
        document.querySelectorAll('#player-hand .card[data-instance-id]').forEach((node) => {
            const cardElement = /** @type {HTMLElement} */ (node);
            const from = before.get(String(cardElement.dataset.instanceId));
            // With no measured start, a glide the hand holds for it plays when the hold ends.
            if (!from || typeof cardElement.animate !== 'function') return;
            const to = handSeatOf(cardElement);
            if (!to) return;
            const dx = from.x - to.x;
            const dy = from.y - to.y;
            const turn = from.angle - to.angle;
            // This glide starts where a held one paints the card: it replaces it.
            const stale = held?.get(cardElement);
            if (stale) {
                try { stale.cancel(); } catch (_) { /* gone */ }
                held.delete(cardElement);
            }
            if (Math.hypot(dx, dy) < 0.75 && Math.abs(turn) < 0.25) return;
            try {
                const glide = cardElement.animate([
                    { translate: `${dx.toFixed(2)}px ${dy.toFixed(2)}px`, rotate: `${turn.toFixed(2)}deg` },
                    { translate: '0px 0px', rotate: '0deg' },
                ], { duration: HAND_REFLOW_MS, easing: HAND_REFLOW_EASING });
                glides.push({ cardElement, glide, offset: { dx, dy, turn } });
            } catch (_) { /* an engine without individual transforms simply re-seats */ }
        });
        if (holdForDeal && handHold) {
            glides.forEach(({ cardElement, glide, offset }) => {
                try { glide.pause(); } catch (_) { /* it runs as created */ }
                heldGlideOffsets.set(glide, offset);
                handHold.glides.set(cardElement, glide);
            });
            return;
        }
        // What a hold still kept goes with this re-seat, on its first frame.
        const moves = [...releaseHandHold({ collect: true }), ...glides.map(({ glide }) => glide)];
        if (moves.length) {
            startGlidesOnTheirFirstFrame(moves, () => announceHandSettledAfter(HAND_REFLOW_MS + 30));
        }
    }

    /**
     * Change the hand's layout with `mutate` and let the cards glide to their
     * new seats. Used when a played card leaves the hand before any snapshot
     * says so, and when a refused one comes back. The player's own action
     * releases a hand held for a deal, for the rest of that deal's wait.
     */
    function reflowHand(mutate) {
        const hand = document.getElementById('player-hand');
        const before = hand ? captureHandSeats([...hand.querySelectorAll('.card[data-instance-id]')]) : null;
        if (handHold) handHoldWaivedFor = handHold.clock;
        mutate();
        glideHandFrom(before);
    }

    /* THE ENEMY FAN CLOSES AND OPENS LIKE A HAND TOO (wave F, 28 September
       2026). The fan is a centred flex row: a back added at the tail moved
       every kept back half a card to the left in one frame, and one taken
       away moved them half a card to the right (the meter's live Chrome
       match: the opponent's backs JUMPED 33 px at the turn-start draw, while
       the player's hand beside it glided).
       The row re-centres as ONE piece (every kept card moves by the same
       half-card), so the ROW glides: #opponent-hand starts where its cards
       were painted (`translate`) and eases to rest on the player's hand's
       clock, carrying the arriving back with it (EnemyFan measures the
       arrival from where the card is painted, so it still leaves the pile).
       Measured from a kept card's LAYOUT seat (offset box), so the fan's
       rotation, the hover dip and EnemyFan's own motion never enter it. The
       backs' own `translate`/`rotate` stay EnemyFan's (arrival,
       shoulder-aside) and nothing is written on a node: a back still carries
       nothing but its seat. A change while the row glides starts from where
       the row is painted, never from its layout. Presentation only. */
    let opponentRowGlide = null;

    /** The row's glide offset as painted right now (0 when it is at rest). */
    function opponentRowPaintedOffset(hand) {
        if (!opponentRowGlide || opponentRowGlide.hand !== hand) return { x: 0, y: 0 };
        try {
            const raw = String(getComputedStyle(hand).translate || '').trim();
            if (!raw || raw === 'none') return { x: 0, y: 0 };
            const [x = 0, y = 0] = raw.split(/\s+/).map((value) => Number.parseFloat(value) || 0);
            return { x, y };
        } catch (_) {
            return { x: 0, y: 0 };
        }
    }

    /** Where the row is painted now, read from one card it keeps. */
    function opponentRowAnchor(hand, kept) {
        const anchor = /** @type {HTMLElement | undefined} */ (
            Array.from(hand.children).find((child) => kept.has(child))
        );
        if (!anchor || anchor.style.display === 'none') return null;
        const offset = opponentRowPaintedOffset(hand);
        return { el: anchor, x: anchor.offsetLeft + offset.x, y: anchor.offsetTop + offset.y };
    }

    function glideOpponentRowFrom(hand, before) {
        if (!before?.el?.isConnected || before.el.parentElement !== hand) return;
        const dx = before.x - before.el.offsetLeft;
        const dy = before.y - before.el.offsetTop;
        const running = opponentRowGlide;
        opponentRowGlide = null;
        if (running) {
            try { running.animation.cancel(); } catch (_) { /* already gone */ }
        }
        if (Math.hypot(dx, dy) < 0.75 || prefersReducedHandMotion() || typeof hand.animate !== 'function') return;
        try {
            const animation = hand.animate([
                { translate: `${dx.toFixed(2)}px ${dy.toFixed(2)}px` },
                { translate: '0px 0px' },
            ], { duration: HAND_REFLOW_MS, easing: HAND_REFLOW_EASING });
            const glide = { hand, animation };
            opponentRowGlide = glide;
            Promise.resolve(animation.finished).then(() => {
                if (opponentRowGlide === glide) opponentRowGlide = null;
            }, () => { /* cancelled by the next change */ });
            startGlidesOnTheirFirstFrame([animation]);
        } catch (_) { /* an engine without individual transforms simply re-seats */ }
    }

    /* THE BATTLE'S ARRIVAL WAIT (round 17, 28 September 2026). A card that
       arrives with a motion (a draw from the deck, an enemy summon's traveler,
       a card that lands from outside the decks) waits up to this long for its
       picture before the motion plays, and plays it regardless at the cap.
       The owner, after a drawn card and enemy summons arrived without art:
       "the animations are not played without they are being loaded like it
       just delays it a bit when it's displayed". Measured through the snappy
       meter's shaping proxy on a warm CDN connection, a 722 KB card picture
       arrives in 0.76 s on the EU phone 4G seat and 1.69 s on the far (Asia)
       phone seat (1.9 s with two more on the same link). Contracts:
       tests/unit/ui/cards.drawArtInsurance.test.js,
       tests/unit/ui/enemyReplay.test.js, tests/unit/ui/fxEntranceWaitsForArt.test.js. */
    const ARRIVAL_ART_WAIT_CAP_MS = 2000;

    const HAND_RASTER_RESERVE_SCALE = 3.8;

    /**
     * Is this hand card, from the previous render, exactly what `card` would
     * build, and at rest? Same face (cardRenderSignature), settled art, and
     * nothing in progress on it: not carried or being played (CardDrag hides
     * those, and a rebuild re-applies their looks), not still arriving from
     * the deck (the draw's own hold, flight and fade own it), not in a deal's
     * cohort hold, not hidden. Only such a card is kept by renderPlayerHand.
     */
    function isRestingHandCard(cardElement, card) {
        const signature = cardRenderSignature(card, { rasterReserveScale: HAND_RASTER_RESERVE_SCALE });
        if (!signature || cardElement?.dataset?.cardRenderSignature !== signature) return false;
        if (CardDrag?.isCarrying?.(card.instanceId, 'hand') || CardDrag?.isPlayPending?.(card.instanceId)) return false;
        if (cardElement.dataset.playPending || cardElement.dataset.fxDrawInFlight || cardElement.dataset.fxDrawWaitSince) {
            return false;
        }
        if (cardElement.style.display === 'none' || cardElement.style.visibility === 'hidden') return false;
        if (['dragging', 'fx-draw-hold', 'card-draw-in', 'card-cohort-hold']
            .some((name) => cardElement.classList.contains(name))) return false;
        const paint = cardElement.querySelector('.card-image > .card-art-paint');
        if (!paint || paint.classList.contains('card-art-loading')) return false;
        return paint.tagName === 'CANVAS'
            ? paint.width > 0 && paint.height > 0
            : isSettledCardArt(paint);
    }

    /**
     * Everything renderPlayerHand owns on a seated hand card, applied the same
     * way to a new card and to a kept one, so a kept card cannot drift from a
     * rebuilt one (the board's rule: board.js applyBoardCardRenderState). A
     * write that would not change anything is not made: a same-value write is
     * still a mutation every observer on the page wakes for.
     */
    function applyHandCardRenderState(cardElement, card, index) {
        const id = `player-hand-${index}`;
        if (cardElement.id !== id) cardElement.id = id;
        // --- Add green highlight for playable cards ---
        cardElement.classList.toggle('legal-play', isCardLegalToPlay(card, readGameStateForHighlights()));
        // Tag zone/owner for hint logic
        if (cardElement.dataset.zone !== 'player-hand') cardElement.dataset.zone = 'player-hand';
        if (cardElement.dataset.owner !== 'player') cardElement.dataset.owner = 'player';
        /* NO CLICK PLAYS A CARD (owner, "Moonfall Drag Play Brief",
           2026-09-24). Dragging is the only way to play: a Character is
           dragged onto the battlefield, a Spell onto its target or the
           battlefield (card-play.js, card-drag.js). A click or a tap only
           inspects, so no gesture on a card has two possible outcomes. */
        // Make sure the card is visibly interactive when it's your turn
        cardElement.classList.toggle(
            'interactive-card',
            typeof GameState !== 'undefined' && Boolean(GameState.get().isYourTurn),
        );
        // Touch tap inspection: hand hover is mousemove-driven
        // (card-hover.js), so use callback mode to open the shared popup
        // preview instead of synthetic mouseenter/mouseleave. A tap only
        // INSPECTS; the enlarged card names the gesture that plays it
        // ("Drag to Summon") beside it, and a drag that starts on the
        // enlargement plays this card (CardDrag). Attached on every render:
        // the listener is registered once, and a kept card's callbacks close
        // over this snapshot's card.
        TouchPreview?.attach?.(cardElement, {
            onPreview: () => {
                void showCardPreviewFor(cardElement, card).then((shown) => {
                    if (shown) showPreviewPlayHint(cardElement);
                });
            },
            onDismiss: () => clearCardPreview(),
        });
    }

    /** The Mulligan overlay's card for this instance, or null. */
    function mulliganCardFor(instanceId) {
        const container = document.getElementById('mulligan-cards');
        if (!container || instanceId === undefined || instanceId === null) return null;
        const wanted = String(instanceId);
        const candidates = /** @type {NodeListOf<HTMLElement>} */ (
            container.querySelectorAll('.mulligan-card-wrapper > .card[data-instance-id]')
        );
        for (const candidate of candidates) {
            if (candidate.dataset.instanceId === wanted) return candidate;
        }
        return null;
    }

    function renderPlayerHand(cards) {
        const playerHandElement = document.getElementById('player-hand');
        if (!playerHandElement) {
            console.error('Player hand element not found!');
            return;
        }
        // A resting hand card shows its art at its own size, not the 3.8x
        // hover reserve minified (card-rest-view.js). Idempotent.
        CardRestView.observe(playerHandElement);

        const previousCards = new Map(
            [...playerHandElement.querySelectorAll('.card[data-instance-id]')]
                .map((card) => [card.dataset.instanceId, card]),
        );
        const previousSeats = captureHandSeats([...previousCards.values()]);

        // Draw-in: cards NEW since the previous snapshot fade in staggered.
        // The CSS keeps every live card transform fixed to avoid raster shimmer.
        // Suppressed on first hydration (no baseline), during mulligan, and
        // after resetHandTracking() (new game).
        const previousHandIds = lastHandIds;
        lastHandIds = new Set(cards.map((c) => c.instanceId));
        const inMulligan = (typeof GameState !== 'undefined' && GameState.get)
            ? GameState.get().state === 'mulligan'
            : false;
        let drawStagger = 0;

        /* Stale draw clones die WITH the seats they were flying to. A
           re-render mid-flight used to leave them airborne beside their
           rebuilt, fully visible cards — the "partially ghosted" slide.
           A DRAW STILL IN THE AIR KEEPS FLYING (26 September 2026, owner:
           "u might have made card draw too quick"). With the deck's faces
           warm, a deal launches within a frame of its snapshot, so a follow-up
           snapshot now lands mid-flight: cancelling the traveller cut the
           380 ms beat short and the rebuilt card stood in its seat at once.
           A card that is still arriving (FX.drawCard marks it until its
           reveal) and is still in the hand keeps its traveller; its rebuilt
           seat takes the arrival over below, hidden until the traveller
           lands, so there is never a visible copy beside it. */
        const nextHandIds = new Set(cards.map((c) => String(c.instanceId)));
        const stillArriving = new Set([...previousCards]
            .filter(([id, el]) => nextHandIds.has(id) && el.dataset.fxDrawInFlight === '1')
            .map(([id]) => id));
        FX?.cancelDrawTravels?.({ keep: stillArriving });

        /* A SNAPSHOT RECONCILES THE HAND; IT DOES NOT REBUILD IT (round 14,
           26 September 2026: board-page-return 15 pop-ins on the phones,
           hand-hover-snapshot 13 on desktop). Every snapshot used to wipe the
           hand and build every card again, so each card the player was
           looking at left the page and a copy appeared in its place — its
           hover handed over, its art re-transferred — even when nothing about
           it had changed. As on the board (board.js renderBoard), a card that
           paints exactly the same face, has settled art and is at rest
           (isRestingHandCard) is kept: the same node, never re-inserted
           unless its place in the hand changed (re-inserting restarts its CSS
           animations). What this render owns on it is re-derived
           (applyHandCardRenderState). Everything else is built as before.
           Contract: tests/unit/ui/cards.handReuse.test.js. */
        const keptCards = new Map(); // index -> the kept node
        const claimedCards = new Set();
        cards.forEach((card, index) => {
            const previousCard = previousCards.get(String(card.instanceId));
            if (
                previousCard
                && !claimedCards.has(previousCard) // a duplicated id seats a new node
                && isRestingHandCard(previousCard, card)
            ) {
                claimedCards.add(previousCard);
                keptCards.set(index, previousCard);
            }
        });
        /* Everything that is not kept leaves first, as the old wipe did: a
           rebuilt card's predecessor is detached before its successor is
           measured (transferSettledRasterPaint reads both boxes). */
        [...playerHandElement.children].forEach((child) => {
            if (!claimedCards.has(child)) child.remove();
        });
        const placeHandCard = (cardElement, index) => {
            const anchor = playerHandElement.children[index] || null;
            if (anchor !== cardElement) playerHandElement.insertBefore(cardElement, anchor);
        };

        const cohortElements = [];
        const drawCohort = [];
        const adoptedArrivals = [];
        // Add each card to the player's hand
        cards.forEach((card, index) => {
            const keptCard = keptCards.get(index);
            if (keptCard) {
                applyHandCardRenderState(keptCard, card, index);
                placeHandCard(keptCard, index);
                return;
            }
            const cardElement = createCardElement(card, `player-hand-${index}`, {
                rasterReserveScale: HAND_RASTER_RESERVE_SCALE,
            });
            const signature = cardRenderSignature(card, { rasterReserveScale: HAND_RASTER_RESERVE_SCALE });
            if (signature) cardElement.dataset.cardRenderSignature = signature;
            const previousCard = previousCards.get(String(card.instanceId));
            if (previousCard) {
                transferSettledRasterPaint(previousCard, cardElement);
                CardRestView.adopt(previousCard, cardElement);
                transferCardPresentation(previousCard, cardElement);
            } else {
                /* THE KEPT HAND ARRIVES WITH THE ART THE MULLIGAN ALREADY
                   SHOWED (round 15, 28 September 2026, WebKit phone seat).
                   During the Mulligan the hand is shown in its overlay, not in
                   #player-hand, so after a keep every kept card started its art
                   again: on WebKit a data-URL <img> still marked
                   card-art-loading, swapped for a canvas by a later resize (a
                   pop-in). The same card's settled canvas is still in the
                   (hidden) overlay: it is the paint source, exactly as a
                   previous hand card is. Only the paint: its title fit and
                   Stellar loop belong to the Mulligan's surface.
                   Contract: tests/unit/ui/cards.keptHandFromMulligan.test.js. */
                const mulliganCard = mulliganCardFor(card.instanceId);
                if (mulliganCard) transferSettledRasterPaint(mulliganCard, cardElement);
            }
            const drawnNow = Boolean(previousHandIds && !inMulligan && !previousHandIds.has(card.instanceId));
            /* A DRAW STILL WAITING FOR ITS FACE KEEPS WAITING (26 September
               2026, WebKit phone revisit: the turn-start draw stood on its
               seat with its art loading for 595 ms). A snapshot landing inside
               the deal's art wait rebuilt the card as an established one — no
               hold, and not in the cohort — so it showed its loading well.
               The rebuilt card inherits the arrival AND the wait's clock, so
               the cap still counts from the draw. A card whose deal has
               launched is no longer waiting and is never flown twice. */
            const carriedWaitSince = Number(previousCard?.dataset.fxDrawWaitSince);
            const drawStillWaiting = !drawnNow && Number.isFinite(carriedWaitSince);
            if (!drawnNow && stillArriving.has(String(card.instanceId))) {
                /* Mid-flight: the traveller is kept (above) and FX.drawCard
                   reveals whichever seat holds this card when it lands. Held,
                   never flown again, never faded in a second time. */
                cardElement.classList.add('fx-draw-hold');
                cardElement.style.visibility = 'hidden';
                cardElement.dataset.fxDrawArrival = '1';
                cardElement.dataset.fxDrawInFlight = '1';
                adoptedArrivals.push(cardElement);
            } else if (drawnNow || drawStillWaiting) {
                cardElement.classList.add('card-draw-in');
                cardElement.style.setProperty('--draw-delay', `${drawStagger * 70}ms`);
                /* The card also TRAVELS, from the deck it was drawn from. The
                   fade alone had no source: a card simply materialised in the
                   fan. FX.drawCard flies a cardback clone from the deck to this
                   seat and reveals the live card as it lands — the live card is
                   never transformed, so its raster is untouched. */
                /* A drawn card's arrival is owned by its flight: the cohort
                   art hold must not re-hide it after the clone lands. */
                cardElement.dataset.fxDrawArrival = '1';
                drawCohort.push({
                    el: cardElement,
                    index: drawStagger,
                    since: drawStillWaiting ? carriedWaitSince : null,
                });
                drawStagger += 1;
                // One-shot, counted from the deal's launch (launchDeal below).
            }
            // The glow, the zone tags, the turn affordance and the touch inspection.
            applyHandCardRenderState(cardElement, card, index);
            placeHandCard(cardElement, index);
            /* THE HAND MIRROR of the board's established-card rule: the
               cohort hold exists so a FRESH deal appears together, but a
               mid-game snapshot rebuilds this hand too, and re-hiding a card
               the player was ALREADY seeing — because its raster transfer
               happened to fail — blanked established cards for up to 2.5s
               ("card description disappears for a second when other cards
               summoned"; reproduced as ~500ms of visibility:hidden on an
               established hand card). A card that existed in the previous
               render keeps its seat visible and lets its art well re-settle
               in place; only genuinely new arrivals join the cohort. */
            if (!previousCard) cohortElements.push(cardElement);

            /* A card still in the air — lifted, flying home, or held over its
               landing slot — must not reappear in its seat because a snapshot
               rebuilt the hand under it. CardDrag reveals it when it lands. */
            if (CardDrag?.isCarrying?.(card.instanceId, 'hand')) {
                cardElement.classList.add('dragging');
                cardElement.style.visibility = 'hidden';
            }
            /* A card already PLAYED from this hand — its action still with the
               server — has left the hand; a rebuild must not bring it back. */
            if (CardDrag?.isPlayPending?.(card.instanceId)) {
                cardElement.style.display = 'none';
                cardElement.dataset.playPending = '1';
            }
        });

        // The dealt hand appears together, never one card at a time.
        let dealWaiting = false;
        /* The deck-to-hand travel, but only when the FX layer is IDLE.
           A draw taking off while another beat owns the layer competes with it,
           and displaced both the enemy live-play glide's first moving frame and
           the summon landing motion — two pinned juice contracts. A draw is the
           quietest event on the board; it yields — it WAITS for the layer, it
           is never skipped (see DRAW_LAYER_WAIT_CAP_MS). */
        if (drawCohort.length) {
            const deckEl = document.getElementById('player-deck-wrapper')
                || document.getElementById('player-deck');
            /* THE FACE FLIES READY. Art used to finish loading after the card
               landed ("first drawn, and only later its art loads"), and the
               first fix — the cardback keeping the seat until the face
               settled — read as cards arriving back-side-up and was rejected
               outright. So the DEAL ITSELF now waits, bounded, for the drawn
               faces' art to settle BEFORE any flight launches: the flip at
               contact then shows a complete card. The wait keys off the
               pipeline's public card-art-loading signal; seats stay hidden
               behind the !important hold class (fx.css) so nothing fades in
               naked while waiting; and the cap guarantees a cold connection
               delays the deal by at most one beat rather than trapping it.
               The cap is the battle's arrival wait (ARRIVAL_ART_WAIT_CAP_MS):
               at 900 ms a picture still on the wire lost the race and the
               card flew with an empty well (owner, 28 September 2026). */
            const DRAW_ART_WAIT_CAP_MS = ARRIVAL_ART_WAIT_CAP_MS;
            const DRAW_ART_POLL_MS = 50;
            // A carried wait keeps its first clock (see drawStillWaiting).
            const now = Date.now();
            const waitedSince = Math.min(now, ...drawCohort.map(({ since }) => since ?? now));
            drawCohort.forEach(({ el }) => {
                el.classList.add('fx-draw-hold');
                el.dataset.fxDrawWaitSince = String(waitedSince);
            });
            const releaseWithoutFlight = (el) => {
                el.classList.remove('fx-draw-hold');
                el.style.visibility = '';
            };
            const artPending = () => drawCohort.some(({ el }) => (
                el.isConnected && (
                    el.querySelector('.card-image > img.card-art-loading')
                    // Its rest view too: it lands showing it (card-rest-view.js).
                    || CardRestView.pending(el)
                )
            ));
            /* THE DRAW WAITS FOR THE LAYER; IT IS NEVER SKIPPED (26 September
               2026, owner: "u might have made card draw too quick"). A busy
               layer used to release the card in place with no flight, and the
               CSS draw-in fade had already run out behind the hold, so the card
               simply appeared (measured: first visible frame at 93-100%
               opacity). The wait for the faces used to outlast the previous
               beat's debris by accident; with the deck's faces warm the deal
               launches in the next frame, and a Character summoned or a Spell
               cast just before End Turn (an instant turn under
               LOAD_TEST_MODE) still has its landing on the layer (debris
               1000 ms, halo/shockwave ~820 ms). So the deal waits for the
               layer on the draw's own clock, and flies at the cap regardless.
               Another deal's travellers never count as busy. */
            const DRAW_LAYER_WAIT_CAP_MS = 1200;
            const layerBusy = () => [...(document.getElementById('fx-layer')?.children || [])]
                .some((node) => !node.classList.contains('fx-draw-travel'));
            const deal = ++dealCount;
            const launchDeal = () => {
                requestAnimationFrame(() => {
                    drawCohort.forEach(({ el, index }) => {
                        if (!el.isConnected) return; // superseded by a re-render
                        // Launched: a rebuild from here on keeps it, never re-deals it.
                        delete el.dataset.fxDrawWaitSince;
                        try {
                            // No FX at all must not leave the seat hidden.
                            if (FX?.drawCard?.(deckEl, el, { index }) === undefined) releaseWithoutFlight(el);
                        } catch (_) { releaseWithoutFlight(el); }
                        /* The CSS draw-in fade is a released card's only beat
                           (a flight takes the class off itself). It must not
                           lapse during a long wait, and must not linger into
                           later interactions: one shot, from the launch. */
                        setTimeout(() => el.classList.remove('card-draw-in'), 770 + index * 70);
                    });
                    // The seat opens as the card flies in (holdHandForDeal).
                    if (handHold?.deal === deal) releaseHandHold();
                });
            };
            const dealReady = () => {
                const waited = Date.now() - waitedSince;
                const facesReady = !artPending() || waited >= DRAW_ART_WAIT_CAP_MS;
                const layerFree = !layerBusy() || waited >= DRAW_LAYER_WAIT_CAP_MS;
                return facesReady && layerFree;
            };
            const awaitFaces = () => {
                if (dealReady()) {
                    launchDeal();
                    return;
                }
                setTimeout(awaitFaces, DRAW_ART_POLL_MS);
            };
            // The hand waits with the deal, unless the player has already moved it.
            if (!dealReady() && handHoldWaivedFor !== waitedSince) {
                holdHandForDeal(deal, waitedSince);
                dealWaiting = true;
            }
            awaitFaces();
        }

        /* A KEPT HOVERED CARD'S HINT FOLLOWS THE SNAPSHOT (review, 27
           September 2026). A rebuilt hovered card is handed to its copy by
           card-hover.js, and that handoff re-derives the copy's hint; a kept
           card is never handed over. The hint reads the turn, the mana, the
           board count and a pending action, so the card that owns the hover
           or the pill re-derives it here: the pill leaves when the opponent's
           turn begins, appears when the player's turn begins under a resting
           pointer, and is re-placed beside a seat that moved. Every seat is
           placed by now and no glide has started, so it is measured where it
           rests. An unchanged hint is not rewritten and the pill never
           re-enters (updateCardHint, showSideHintFor). */
        keptCards.forEach((keptCard) => {
            if (keptCard.classList.contains('hover-active') || sideHintCard === keptCard) {
                updateCardHint(keptCard);
            }
        });
        holdCardCohortUntilSettled(cohortElements);
        glideHandFrom(previousSeats, { holdForDeal: dealWaiting });
        // A rebuilt seat that took an arrival over keeps its traveller aimed at it.
        adoptedArrivals.forEach((cardElement) => FX?.followDrawArrival?.(cardElement));

        // Add event listeners for drag operations
        addPlayerHandDragListeners();
    }

    function opponentCardbackDescriptor(hand) {
        return {
            kind: hand.dataset.cardbackArtKind || 'custom-pending',
            slug: hand.dataset.cardbackArtSlug || 'default',
            url: hand.dataset.cardbackArtUrl || '',
        };
    }

    // back -> the cardback it is dressed in (opponentBackStyleKey), so a back
    // renderOpponentHand keeps is restyled only when the cardback changed.
    const opponentBackStyles = new WeakMap();

    function opponentBackStyleKey(descriptor) {
        return descriptor.kind === 'custom-pending' ? 'none' : `url:${descriptor.url}`;
    }

    function setOpponentBackStyle(back, descriptor) {
        CardBackRaster?.clear?.(back);
        back.style.removeProperty('background-image');
        // That inline image was EnemyFan's sharpened texture (enemy-fan.js
        // applyTexture). Its marker goes with it, or the fan would believe the
        // back still wears the texture and never dress it again; a back used
        // to be thrown away on the next snapshot, now it is kept.
        delete back.dataset.fanTextureKey;
        back.style.setProperty(
            '--cardback-image',
            descriptor.kind === 'custom-pending'
                ? 'none'
                : `url(${JSON.stringify(descriptor.url)})`,
        );
        opponentBackStyles.set(back, opponentBackStyleKey(descriptor));
    }

    function applyOpponentCardbackDescriptor(hand, descriptor, { refreshFan = true } = {}) {
        if (!hand || hand.dataset.cardbackArtSlug !== descriptor.slug) return;
        hand.dataset.cardbackArtKind = descriptor.kind;
        hand.dataset.cardbackArtUrl = descriptor.url || '';
        hand.querySelectorAll(':scope > .card-back').forEach((back) => {
            setOpponentBackStyle(back, descriptor);
        });
        if (refreshFan) {
            EnemyFan?.present?.(hand, {
                cardbackUrl: descriptor.url || '',
                animate: false,
            });
        }
    }

    function ensureOpponentCardbackOwner(hand, selectedSlug) {
        const slug = typeof selectedSlug === 'string' && selectedSlug.trim()
            ? selectedSlug.trim()
            : 'default';
        if (hand.dataset.cardbackArtSlug === slug) return opponentCardbackDescriptor(hand);

        const initial = slug === 'default'
            ? { kind: 'default', slug, url: '/assets/images/cardback.webp' }
            : { kind: 'custom-pending', slug, url: '' };
        hand.dataset.cardbackArtSlug = slug;
        hand.dataset.cardbackArtKind = initial.kind;
        hand.dataset.cardbackArtUrl = initial.url;

        const observe = CardbackArt?.observe;
        if (typeof observe === 'function') {
            observe(hand, slug, (descriptor) => {
                hand.dataset.cardbackArtKind = descriptor.kind;
                hand.dataset.cardbackArtUrl = descriptor.url || '';
                Promise.resolve().then(() => {
                    applyOpponentCardbackDescriptor(hand, descriptor);
                });
            });
        }
        return initial;
    }

    /**
     * Render the opponent's hand
     * @param {Number} count - Number of cards in opponent's hand
     */
    /** The art grace (cards.css `--card-art-grace`), in milliseconds. */
    function cardArtGraceMs() {
        try {
            const raw = getComputedStyle(document.documentElement).getPropertyValue('--card-art-grace').trim();
            const value = parseFloat(raw);
            if (Number.isFinite(value)) return /ms$/.test(raw) ? value : value * 1000;
        } catch (_) { /* no layout */ }
        return 450;
    }

    /* AN ENEMY CARD STILL WAITING FOR ITS PICTURE STAYS IN THEIR HAND (round
       17, 28 September 2026). An enemy summon resolves at once, so the snapshot
       that seats it on the board lands a few frames after `summonStarted`, and
       its back used to leave the fan in that render while EnemyReplay's
       traveller was still waiting for its face: the card was nowhere (the
       owner, of the old 1080 ms hold: "it dissapears and then comes back in a
       second"). While EnemyReplay holds a summon (enemy-replay.js
       summonArtCapMs, capped), the fan keeps one back for it: the tail seat,
       since backs carry no identity, marked `data-held-departure` with the
       summon it waits for. The traveller's launch or the cap gives it back to
       the snapshot's count, which stays the authority for every other seat.
       Contract: tests/unit/ui/cards.opponentHandHoldsDeparture.test.js. */
    const heldOpponentDepartures = new Set();
    let lastOpponentHand = null; // the last render's { count, revealedCards }

    function holdOpponentDeparture(instanceId) {
        if (instanceId === undefined || instanceId === null) return;
        heldOpponentDepartures.add(String(instanceId));
    }

    /** Give a held back to the count; redraw at once unless the next render will. */
    function releaseOpponentDeparture(instanceId, { rerender = true } = {}) {
        if (!heldOpponentDepartures.delete(String(instanceId))) return false;
        if (rerender && lastOpponentHand) {
            void renderOpponentHand(lastOpponentHand.count, lastOpponentHand.revealedCards);
        }
        return true;
    }

    async function renderOpponentHand(count, revealedCards = []) {
        const opponentHandElement = document.getElementById('opponent-hand');
        if (!opponentHandElement) {
            console.error('Opponent hand element not found!');
            return;
        }
        lastOpponentHand = { count, revealedCards };
        const held = [...heldOpponentDepartures];
        // The seats the fan shows: the snapshot's cards, then the held backs.
        const shown = count + held.length;

        const previousCards = new Map(
            [...opponentHandElement.querySelectorAll('.card[data-instance-id]')]
                .map((card) => [card.dataset.instanceId, card]),
        );
        const previousRow = Array.from(opponentHandElement.children);

        // Reveal mechanic: cards the storyteller exposed render face-up at
        // their hand position until they leave the opponent's hand.
        const revealedByIndex = new Map();
        (Array.isArray(revealedCards) ? revealedCards : []).forEach(entry => {
            if (entry && Number.isInteger(entry.handIndex) && entry.card) {
                revealedByIndex.set(entry.handIndex, entry.card);
            }
        });

        let selectedCardback = 'default';
        try {
            const state = (typeof GameState !== 'undefined' && GameState.get) ? GameState.get() : null;
            selectedCardback = state?.opponent?.selectedCardback || 'default';
        } catch (e) {
            console.warn('[Cards] Failed to resolve opponent cardback:', e);
        }
        const cardbackDescriptor = ensureOpponentCardbackOwner(
            opponentHandElement,
            selectedCardback,
        );
        const cardbackUrl = cardbackDescriptor.url || '';

        /* A REVEALED CARD THE PLAYER IS LOOKING AT STAYS. Wiping the row
           rebuilt it on every snapshot: a hovered or tapped (enlarged) card
           collapsed and replayed its grow, and one whose art was still loading
           was re-hidden by the art grace. The same card, unchanged, at the
           same seat, with its art settled, is kept where it is; a changed one
           is rebuilt as before. Backs are kept by seat (below). */
        const revealedSignature = (card) => cardRenderSignature({ ...card }, { rasterReserveScale: 2.4 });
        const graceMs = cardArtGraceMs();
        const keptRevealed = new Map();
        revealedByIndex.forEach((card, index) => {
            const previous = /** @type {HTMLElement | undefined} */ (previousCards.get(String(card.instanceId)));
            const signature = revealedSignature(card);
            if (
                previous
                && signature
                && previous.dataset.cardRenderSignature === signature
                && previous.id === `opponent-hand-revealed-${index}`
                && !previous.querySelector('.card-image > img.card-art-loading')
            ) keptRevealed.set(index, previous);
        });
        /* THE BACK UNDER THE POINTER STAYS TOO. Backs carry no identity (the
           server nulls which card left), so they are kept by SEAT: the back at
           seat i stays the back at seat i, and only the tail grows or shrinks.
           A rebuilt back lost `:hover` until the browser's next hover update,
           so the one the pointer rested on jumped up 5px and lost its glow on
           every snapshot of the enemy's turn. A kept back also keeps what
           EnemyFan put on it: its texture, a running arrival, a departing fade
           (enemy-fan.js treats a node that still carries a class as the same
           node, still animating). */
        const keptBacks = new Map();
        for (let i = 0; i < shown; i++) {
            const previous = previousRow[i];
            if (!revealedByIndex.has(i) && previous?.classList.contains('card-back')) {
                keptBacks.set(i, previous);
            }
        }
        const kept = new Set([...keptRevealed.values(), ...keptBacks.values()]);
        // Where the kept cards are painted now, read only when the row is about
        // to change (a snapshot that moves nothing reads no layout).
        const reseated = shown !== previousRow.length || kept.size !== previousRow.length;
        const rowBefore = reseated ? opponentRowAnchor(opponentHandElement, kept) : null;
        Array.from(opponentHandElement.children).forEach((child) => {
            if (!kept.has(child)) child.remove();
        });

        // Create card elements for opponent cards (backs, or fronts if revealed)
        for (let i = 0; i < shown; i++) {
            let cardElement = keptRevealed.get(i) || keptBacks.get(i) || null;

            const revealedCard = cardElement || i >= count ? null : revealedByIndex.get(i);
            if (revealedCard) {
                try {
                    // Direct row child — it must occupy the EXACT slot a card
                    // back would (same nth-child fan styling), just face-up.
                    cardElement = createCardElement(
                        { ...revealedCard },
                        `opponent-hand-revealed-${i}`,
                        { rasterReserveScale: 2.4 },
                    );
                    const signature = revealedSignature(revealedCard);
                    if (signature) cardElement.dataset.cardRenderSignature = signature;
                    const previousCard = previousCards.get(String(revealedCard.instanceId));
                    /* The art grace's clock belongs to the REVEAL, not to this
                       node: a rebuild inherits what is left of it, so a card
                       already showing its frame over its well is not hidden
                       again for another grace on every snapshot. */
                    const revealedAt = Number(/** @type {HTMLElement | undefined} */ (previousCard)?.dataset.revealedAt) || Date.now();
                    cardElement.dataset.revealedAt = String(revealedAt);
                    if (previousCard) {
                        transferSettledRasterPaint(previousCard, cardElement);
                        transferCardPresentation(previousCard, cardElement);
                        const remaining = Math.max(0, graceMs - (Date.now() - revealedAt));
                        cardElement.style.setProperty('--card-art-grace', `${Math.round(remaining)}ms`);
                    }
                    cardElement.classList.add('revealed-in-hand');
                    if (CrispHover?.attach) {
                        // Shared crisp hover-zoom; the zoomed card slides fully
                        // into view (this row starts half off-screen).
                        CrispHover.attach(cardElement, cardElement, { maxScale: 2.4, sharpZoom: true });
                    }
                    // Touch inspection: CrispHover's trusted-mouseenter guard
                    // suppresses compatibility hover on touch, so
                    // TouchPreview's synthetic mode drives CrispHover after the
                    // tap resolves.
                    TouchPreview?.attach?.(cardElement);
                } catch (err) {
                    console.error('[Cards] Failed to render revealed opponent card:', err);
                    cardElement = null;
                }
            }

            if (!cardElement) {
                cardElement = document.createElement('div');
                cardElement.className = 'card card-back';

                setOpponentBackStyle(cardElement, cardbackDescriptor);
            } else if (
                keptBacks.get(i) === cardElement
                && opponentBackStyles.get(cardElement) !== opponentBackStyleKey(cardbackDescriptor)
            ) {
                setOpponentBackStyle(cardElement, cardbackDescriptor);
            }

            // Mark as opponent hand slot and store index (no content). Written
            // only when it differs: a kept card is not touched, because even a
            // same-value attribute write wakes every observer on the page.
            if (cardElement.dataset.zone !== 'opponent-hand') cardElement.dataset.zone = 'opponent-hand';
            if (cardElement.dataset.opponentIndex !== String(i)) cardElement.dataset.opponentIndex = String(i);
            // A held seat names the summon it waits for; a seat of the count names none.
            const heldFor = i >= count ? held[i - count] : undefined;
            if (heldFor !== undefined && cardElement.dataset.heldDeparture !== heldFor) {
                cardElement.dataset.heldDeparture = heldFor;
            } else if (heldFor === undefined && cardElement.dataset.heldDeparture !== undefined) {
                delete cardElement.dataset.heldDeparture;
            }

            // Native drag handlers removed — CardDrag.registerTarget in
            // addOpponentHandDropTargets() handles all drop logic via pointer events.

            // In order around a kept card, which is never moved.
            const anchor = opponentHandElement.children[i] || null;
            if (anchor !== cardElement) opponentHandElement.insertBefore(cardElement, anchor);
        }
        // A kept card past a shrunken count has no seat.
        while (opponentHandElement.children.length > shown) opponentHandElement.lastElementChild.remove();
        glideOpponentRowFrom(opponentHandElement, rowBefore);

        // Material, arrival stagger and idle life for the face-down fan. Purely
        // downstream: the count above is already authoritative, and a mulligan
        // reshuffle changes it without anyone having drawn.
        const inMulligan = (typeof GameState !== 'undefined' && GameState.get)
            ? GameState.get().state === 'mulligan'
            : false;
        EnemyFan?.present?.(opponentHandElement, {
            cardbackUrl,
            animate: !inMulligan,
        });

        // Add event listeners for drag targets
        addOpponentHandDropTargets();
    }



    /**
     * Add drag event listeners to player hand cards and drop listener for the hand area.
     */
    function addPlayerHandDragListeners() {
        const playerHandCards = document.querySelectorAll('#player-hand .card');
        const gameState = GameState.get();
        const hand = gameState?.player && Array.isArray(gameState.player.hand) ? gameState.player.hand : [];
        const byInstance = new Map(hand.map((c) => [String(c.instanceId), c]));
        CardPlay.registerZones();

        playerHandCards.forEach((node) => {
            const card = /** @type {HTMLElement} */ (node);
            // Clean up stale CardDrag listeners from previous renders
            CardDrag.cleanupDraggable(card);
            CardDrag.unregisterTarget(card);

            /* EVERY card in the hand can be picked up ("everything is
               draggable"). One that cannot be played right now still lifts,
               says why while it is held, and comes home — CardPlay decides the
               reason, CardDrag carries it. */
            const cardData = byInstance.get(String(card.dataset.instanceId || '')) || {
                instanceId: card.dataset.instanceId,
                cardId: card.dataset.cardId,
                type: card.dataset.type,
                targetingType: card.dataset.targetingType,
                manaCost: Number.parseInt(card.dataset.manaCost, 10) || 0,
            };
            // A kept card already has it: toggle writes only a change.
            card.classList.toggle('draggable', true);
            CardDrag.makeDraggable(card, CardPlay.handDragConfig(card, cardData));

            // Register every hand card as a drop target (a Spell may target one)
            CardDrag.registerTarget(card, {
                zone: 'player-hand-card',
                validate: (d) => {
                    if (!d) return false;
                    if (d.instanceId === card.dataset.instanceId) return false;
                    if (!GameState.get().isYourTurn) return false;
                    return true;
                },
                onDrop: (d) => {
                    const s = GameState.get();
                    if (s.actionInProgress) return;
                    return routeActionDrop(d, 'card', card.dataset.instanceId);
                }
            });
        });
    } // End of addPlayerHandDragListeners
    /**
     * Add drop targets to opponent hand for targeting spells or actions.
     */
    function addOpponentHandDropTargets() {
        const opponentHand = document.getElementById('opponent-hand');
        if (!opponentHand) return;

        // Register each opponent card-back as a drop target
        opponentHand.querySelectorAll('.card').forEach(card => {
            CardDrag.registerTarget(card, {
                zone: 'opponent-hand-card',
                validate: (d) => d && GameState.get().isYourTurn,
                onDrop: (d) => {
                    const s = GameState.get();
                    if (s.actionInProgress) return;
                    const targetId = (s.opponentId || 'opponent') + '_hand';
                    const targetIndex = Number.parseInt(card.dataset.opponentIndex || '', 10);
                    const hasTargetIndex = Number.isInteger(targetIndex) && targetIndex >= 0;
                    if (d.sourceZone === 'player-hand') {
                        const handCards = s.player?.hand || [];
                        const draggedCard = handCards.find(c => c.instanceId === d.instanceId);
                        if (draggedCard?.type === 'Spell' && draggedCard?.targetingType === 'Targetable' && hasTargetIndex) {
                            return routeActionDrop(d, 'opponentHandCard', targetId, targetIndex);
                        }
                    } else if (d.sourceZone && d.sourceZone.startsWith('player-board-slot') && hasTargetIndex) {
                        return routeActionDrop(d, 'opponentHandCard', targetId, targetIndex);
                    }
                    return undefined;
                }
            });
        });

        // Register the hand area itself
        CardDrag.registerTarget(opponentHand, {
            zone: 'opponent-hand-area',
            validate: (d) => d && GameState.get().isYourTurn,
            onDrop: (d) => {
                const s = GameState.get();
                if (s.actionInProgress) return;
                const targetId = (s.opponentId || 'opponent') + '_hand';
                return routeActionDrop(d, 'card', targetId);
            }
        });
    }

    // --- Hover scaling helpers ---
    function calculateHoverScale(cardElement, {
        baseScale = 3.8,
        minScale = 1.6,
        margin = 16,
        fitDisplayed = false
    } = {}) {
        if (!cardElement) return baseScale;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
        if (!viewportHeight || !viewportWidth) return baseScale;

        const rect = typeof cardElement.getBoundingClientRect === 'function'
            ? cardElement.getBoundingClientRect()
            : { width: 0, height: 0 };
        // fitDisplayed (opt-in, announce floats): a statically scaled ancestor
        // stage renders the card smaller than its layout box, and the
        // enlargement multiplies what the user SEES — fit against the BCR.
        // Everything else keeps layout-box semantics: hand/mulligan zoom math
        // and their control-stationarity contracts are calibrated to offsets.
        const baseWidth = (fitDisplayed && rect.width) || cardElement.offsetWidth || rect.width || 0;
        const baseHeight = (fitDisplayed && rect.height) || cardElement.offsetHeight || rect.height || 0;
        if (!baseWidth || !baseHeight) return baseScale;

        // The viewport is already in hand: this runs on every hover frame.
        const edge = margin * uiScaleOf(viewportWidth, viewportHeight);
        const availableHeight = viewportHeight - edge * 2;
        const availableWidth = viewportWidth - edge * 2;
        let scale = baseScale;
        const heightScale = availableHeight / baseHeight;
        const widthScale = availableWidth / baseWidth;

        if (Number.isFinite(heightScale)) scale = Math.min(scale, heightScale);
        if (Number.isFinite(widthScale)) scale = Math.min(scale, widthScale);

        const boundedMin = Math.min(minScale, baseScale);
        return Math.max(boundedMin, scale);
    }

    /** The card's row neighbours and where they are painted now (sub-pixel). */
    function rowNeighboursAt(cardElement) {
        const read = (el) => {
            if (!el || typeof el.getBoundingClientRect !== 'function') return null;
            const left = el.getBoundingClientRect().left;
            return Number.isFinite(left) ? { el, left } : null;
        };
        return {
            prev: read(cardElement.previousElementSibling),
            next: read(cardElement.nextElementSibling),
        };
    }

    /**
     * How much the row's footprint of `cardElement` grew (px, negative when it
     * shrank) since `neighbours` was read, from where its neighbours moved: in
     * a single-line flex row a footprint change F moves everything before the
     * card by the line's start shift and everything after it by that shift
     * plus F. 0 when nothing moved or the row's distribution is not one the
     * neighbours can tell apart.
     */
    function rowFootprintDrift(cardElement, neighbours) {
        const moved = (seen) => (seen?.el?.isConnected
            ? seen.el.getBoundingClientRect().left - seen.left
            : null);
        const before = moved(neighbours?.prev);
        const after = moved(neighbours?.next);
        let drift = 0;
        if (before !== null && after !== null) {
            drift = after - before;
        } else if (before !== null || after !== null) {
            let justify = '';
            try {
                justify = getComputedStyle(cardElement.parentElement).justifyContent || '';
            } catch (_) {
                justify = '';
            }
            if (justify === 'center' || justify === 'safe center') {
                drift = before !== null ? -2 * before : 2 * after;
            } else if (/(^|\s)(normal|flex-start|start|left)$/.test(justify) && after !== null) {
                drift = after;
            } else if (/(^|\s)(flex-end|end|right)$/.test(justify) && before !== null) {
                drift = -before;
            }
        }
        return Number.isFinite(drift) && Math.abs(drift) > 0.001 ? drift : 0;
    }

    function applyHoverTransform(cardElement, {
        baseScale = 3.8,
        minScale = 1.6,
        margin = 16,
        origin = 'bottom center',
        translateYPercent = -15,
        fitDisplayed = false,
        // CSS zoom re-resolves vmin-derived card sizing inside the zoomed
        // subtree, shrinking the layout box and eating most of a small
        // enlargement (a 1.6 zoom renders ~1.2 visible growth). Surfaces whose
        // raster is already reserved at the target scale (announce floats)
        // opt out and take the geometrically exact transform path instead.
        allowZoom = true
    } = {}) {
        if (!cardElement) return baseScale;
        const scale = calculateHoverScale(cardElement, { baseScale, minScale, margin, fitDisplayed });
        cardElement.style.transformOrigin = origin;
        const translate = Number.isFinite(translateYPercent) ? `${translateYPercent}%` : '-15%';
        const isHandFan = origin.includes('bottom');
        cardElement.classList.remove('safari-hand-zoom-hover');
        // Safari hand cards use physical zoom only after both their absolute-
        // child and margin behavior are measured. The two compensation formulas
        // preserve the exact resting footprint for scaled- and unscaled-margin
        // engines. Other engines retain their established zoom behavior.
        const useZoom = allowZoom
            && _useZoomHover
            && (!isHandFan || !_isSafari || _zoomScalesMargins !== null);
        if (useZoom) {
            const cs = getComputedStyle(cardElement);
            if (isHandFan) {
                // Hand cards: zoom + margin compensation to prevent siblings
                // from shifting. Margin-top anchors the bottom edge, and the
                // row's height is what the battle column shrinks it to, so
                // the board above moves with it: the used size, fractional —
                // whole pixels come back multiplied by the zoom
                // (used-box-size.js). offsetWidth's rounding (79 for 78.656
                // at 1400x640) moved every other hand card 0.47 px in the
                // meter's hand-hover step (wave F, 28 September 2026).
                const { width: w, height: h } = usedBoxSize(cardElement, cs);
                const neighbours = rowNeighboursAt(cardElement);
                const oldML = parseFloat(cs.marginLeft) || 0;
                const oldMR = parseFloat(cs.marginRight) || 0;
                const origH = w + oldML + oldMR;
                const marginsScale = _zoomScalesMargins !== false;
                const newMH = marginsScale
                    ? (origH / scale - w) / 2
                    : (origH - scale * w) / 2;
                const oldMT = parseFloat(cs.marginTop) || 0;
                const oldMB = parseFloat(cs.marginBottom) || 0;
                const origV = h + oldMT + oldMB;
                const newMT = marginsScale
                    ? origV / scale - h
                    : origV - scale * h;
                cardElement.style.zoom = scale;
                cardElement.style.margin = `${newMT}px ${newMH}px 0px ${newMH}px`;
                // The engine snaps the zoomed box and margins to its layout
                // grid (1/64 px in Chromium): the row can still be off by a
                // few hundredths, which flips a neighbour's whole-pixel offset
                // now and then. Whatever the row measurably lost or gained is
                // given back on the right margin, so the rest of the hand
                // stays exactly where it was.
                const drift = rowFootprintDrift(cardElement, neighbours);
                if (drift) {
                    cardElement.style.marginRight = `${newMH - (marginsScale ? drift / scale : drift)}px`;
                }
                if (_isSafari) cardElement.classList.add('safari-hand-zoom-hover');
            } else {
                // Collection/deck-builder previews (fixed position, no siblings).
                // Multiply existing CSS zoom by scale. Return 1 so callers
                // (positionHoverPreview) don't double-count — offsetWidth already
                // reflects the new zoom in Firefox.
                const existingZoom = parseFloat(cs.zoom) || 1;
                cardElement.style.zoom = existingZoom * scale;
            }
            cardElement.style.setProperty(
                '--card-hover-transform',
                `rotate(0deg) translateY(${translate})`
            );
            return isHandFan ? scale : 1;
        }
        // transform: scale() — functional fallback when standard zoom geometry
        // is unavailable or the Safari margin model is inconclusive.
        cardElement.style.setProperty(
            '--card-hover-transform',
            `scale(${scale}) rotate(0deg) translateY(${translate})`
        );
        return scale;
    }

    // --- Hover Hint helpers ---
    function ensureHintElement(cardElement) {
        let hint = cardElement.querySelector('.card-hint');
        if (!hint) {
            hint = document.createElement('div');
            hint.className = 'card-hint';
            cardElement.appendChild(hint);
        }
        return hint;
    }

    function computeCardHint(cardElement) {
        try {
            const state = (typeof GameState !== 'undefined' && typeof GameState.get === 'function') ? GameState.get() : null;
            if (!state) return '';
            if (state.actionInProgress) return '';
            if (state.state === 'mulligan') return '';

            const zone = cardElement.dataset.zone || '';
            const type = cardElement.dataset.type;
            const manaCost = parseInt(cardElement.dataset.manaCost || '0', 10) || 0;
            const isYourTurn = !!state.isYourTurn;
            const currentMana = (state.player && typeof state.player.mana !== 'undefined') ? state.player.mana : 0;

            // Helper to get translated hint text
            const t = (key, fallback) => {
                if (i18n && typeof i18n.t === 'function') {
                    const translated = i18n.t(key);
                    return translated !== key ? translated : fallback;
                }
                return fallback;
            };

            if (zone === 'player-hand') {
                if (!isYourTurn) return '';
                if (currentMana < manaCost) return '';
                /* Dragging is the only way to play (owner, 2026-09-24), on a
                   mouse and on a finger alike — the hint names the one gesture. */
                if (type === 'Character') {
                    const boardCount = (state.player && Array.isArray(state.player.summonedCards)) ? state.player.summonedCards.length : 0;
                    const BOARD_LIMIT = 5;
                    if (boardCount >= BOARD_LIMIT) return '';
                    return t('game.dragToSummon', 'Drag to Summon');
                }
                if (type === 'Spell') return t('game.dragToCast', 'Drag to Cast');
                return '';
            }

            if (zone === 'player-board') {
                if (!isYourTurn) return '';
                const id = cardElement.dataset.instanceId || cardElement.dataset.cardId;
                if (!id) return '';
                const canUse = (typeof GameState !== 'undefined' && typeof GameState.canUseCard === 'function' && GameState.canUseCard(id));
                // Every ready board Character opens the intent dialog now.
                if (canUse) return t('game.dragForIntent', 'Drag for Intent');
                return '';
            }

            return '';
        } catch (e) {
            return '';
        }
    }

    // --- Card title fitting -------------------------------------------------
    // "Maximum card title length is 20 characters" is an instruction the content
    // prompts give the storyteller, the player LLM and Moon Fuse — soft guidance
    // a model can miss, and one that translation cannot honour at all: a faithful
    // Spanish "Quetzalcoatl" is "Quetzalcóatl, la Serpiente Emplumada". The
    // renderer therefore measures the title instead of trusting the rule, and the
    // bar wraps to a second line (see .card-name in cards.css) so an over-budget
    // title costs a line rather than half its type size.
    //
    // Character count is the wrong axis for both jobs. A full-width CJK glyph is
    // ~1em against ~0.5em for Latin, so a 12-character Japanese title is nearly
    // twice the width of a 12-character English one.
    const TITLE_MAX_RATIO = 0.030;  // mirrors the .card-name rule in cards.css
    const TITLE_MIN_RATIO = 0.012;  // past this the bar's overflow clip is the net
    // A single line is kept while it stays at least this large — the old sizing
    // ladder's second-smallest rung. Only titles that would have dropped below
    // it (and clipped) take the second line, so the vast majority of cards look
    // exactly as they always have.
    const TITLE_SINGLE_LINE_MIN_RATIO = 0.018;
    // The bar is a fixed strip on the frame art; a third line would leave it.
    const TITLE_MAX_LINES = 2;
    // Bisection steps. Eight halvings of the 0.012-0.030 range settle to
    // ~0.00007, far finer than a pixel at any card size.
    const TITLE_FIT_STEPS = 8;
    // Content width of the title bar as a multiple of --card-height, from the
    // same .card-name rule: (0.61 - 2 * 0.032) * 12.29 / 22.2. Only used to pick
    // a close size before any layout exists; measurement is the authority.
    const TITLE_CONTENT_RATIO = 0.3023;
    const WIDE_GLYPH = new RegExp(
        '[\\u1100-\\u115F\\u2E80-\\u303E\\u3041-\\u33FF\\u3400-\\u4DBF\\u4E00-\\u9FFF'
        + '\\uA000-\\uA4CF\\uAC00-\\uD7A3\\uF900-\\uFAFF\\uFE30-\\uFE4F\\uFF00-\\uFF60\\uFFE0-\\uFFE6]',
    );
    const titleRatios = new WeakMap();

    // Average advance width per character, in ems. The bar is uppercased, and
    // uppercase Spectral measures ~0.68em per Latin character (17-character
    // sample: 11.63em of scalable width). A full-width CJK glyph is ~1em.
    const TITLE_LATIN_EM = 0.68;
    // How much headroom the estimate must show before the measured pass is
    // skipped entirely. The estimate is an average, so a short title can still
    // be a little wider than predicted; a quarter of the bar absorbs that.
    const TITLE_ESTIMATE_MARGIN = 1.25;

    function estimateTitleEms(text) {
        let ems = 0;
        for (const glyph of text) ems += WIDE_GLYPH.test(glyph) ? 1 : TITLE_LATIN_EM;
        return ems;
    }

    function estimateTitleRatio(text) {
        return text ? TITLE_CONTENT_RATIO / estimateTitleEms(text) : TITLE_MAX_RATIO;
    }

    function clampTitleRatio(ratio) {
        if (!Number.isFinite(ratio)) return TITLE_MAX_RATIO;
        return Math.min(TITLE_MAX_RATIO, Math.max(TITLE_MIN_RATIO, ratio));
    }

    function applyTitleRatio(nameElement, ratio) {
        nameElement.style.fontSize = `calc(var(--card-height) * ${ratio})`;
        titleRatios.set(nameElement, ratio);
    }

    /* TITLE FIT MEMORY, keyed like the description's (surface + card
       instance) plus the title text. The hand is rebuilt on every render
       (`renderPlayerHand`), and a new title opened at the character-count
       estimate, then moved to its measured fit a moment later: a long title
       (Worldtree Guardian: 0.0247 -> 0.0221 of the card height) visibly
       changed size on every enemy action, 2026-09-25. A card fitted on its
       surface before opens at that fit, as the description does.
       A card NEW to a surface opens at the fit its title had anywhere else:
       the bar is the same fraction of every card, so a fit carries across
       sizes to within rounding. A summoned card landed on the board at the
       character-count estimate, visible, and was only fitted a frame later
       ("Spectral Burrow", Firefox, 2026-09-26). */
    const titleFitMemory = new Map();
    const TITLE_FIT_MEMORY_CAP = 600;

    function titleFitMemoryKey(cardElement, text) {
        const key = descriptionFitMemoryKey(cardElement);
        return key && text ? `${key}|${text}` : null;
    }

    const titleTextMemoryKey = (text) => `any|${text}`;

    function rememberTitleFit(key, nameElement) {
        const ratio = titleRatios.get(nameElement);
        if (!key || !Number.isFinite(ratio)) return;
        const entry = { ratio, wrap: nameElement.classList.contains('card-name--wrap') };
        [key, titleTextMemoryKey(nameElement.textContent || '')].forEach((memoryKey) => {
            if (titleFitMemory.has(memoryKey)) titleFitMemory.delete(memoryKey);
            titleFitMemory.set(memoryKey, entry);
        });
        while (titleFitMemory.size > TITLE_FIT_MEMORY_CAP) {
            titleFitMemory.delete(titleFitMemory.keys().next().value);
        }
    }

    function applyEstimatedTitleRatio(nameElement, memoryKey = null) {
        const text = nameElement.textContent || '';
        if (!text || CardDescEngraving.isTitleEngraved(nameElement)) return;
        const remembered = memoryKey ? titleFitMemory.get(memoryKey) : null;
        if (remembered) {
            nameElement.classList.toggle('card-name--wrap', remembered.wrap);
            applyTitleRatio(nameElement, remembered.ratio);
            return;
        }
        const elsewhere = titleFitMemory.get(titleTextMemoryKey(text));
        if (elsewhere) {
            // Fitted on a card of another size: a hair smaller, so rounding
            // here cannot overflow the bar before this card's own fit.
            nameElement.classList.toggle('card-name--wrap', elsewhere.wrap);
            applyTitleRatio(nameElement, elsewhere.ratio * TITLE_LINE_SHARE);
            return;
        }
        applyTitleRatio(nameElement, clampTitleRatio(estimateTitleRatio(text)));
    }

    // Sub-pixel width of the rendered text against the element's content box.
    // Returns null until the element has been laid out, so the caller retries
    // rather than baking in a measurement taken from a zero-sized box.
    function measureTitle(nameElement) {
        const box = nameElement.getBoundingClientRect();
        if (!(box.width > 0) || !(nameElement.clientHeight > 0)) return null;
        const style = getComputedStyle(nameElement);
        const available = box.width
            - (parseFloat(style.paddingLeft) || 0)
            - (parseFloat(style.paddingRight) || 0);
        const range = document.createRange();
        range.selectNodeContents(nameElement);
        const textBox = range.getBoundingClientRect();
        return {
            available,
            // Vertical fit is line boxes against the content box, computed from
            // resolved style. The two obvious alternatives both lie at title
            // sizes: scrollHeight/clientHeight are rounded to whole pixels and
            // the bar centres its content, so they report a phantom pixel of
            // overflow on text that fits (Firefox: 10 against 9 for an 8.93px
            // block in a 9.24px box); and a Range's per-line client rects are
            // clamped to a 5px minimum height in Chromium, which at a 3.4px
            // title font is nearly 1.5x the real line box.
            availableHeight: box.height
                - (parseFloat(style.paddingTop) || 0)
                - (parseFloat(style.paddingBottom) || 0),
            lineHeight: parseFloat(style.lineHeight) || 0,
            textWidth: textBox.width,
            // One rect per line box. Shrinking the type REDUCES this, so a
            // three-line title means the size is still too large — the bar is
            // tall enough to swallow three small lines, which is why height
            // alone is not a sufficient test.
            lines: range.getClientRects().length,
            // .card-name sets letter-spacing in PIXELS, so this slice of the
            // width does not shrink with the type. Solving for the font size
            // without subtracting it overshoots by ~3px on a 20-character
            // title — enough to clip at hand size, and only at hand size,
            // which is exactly the resolution-dependent behaviour §3.10 bans.
            fixedWidth: (parseFloat(style.letterSpacing) || 0) * (nameElement.textContent || '').length,
        };
    }

    /* A SINGLE LINE FITS WITH A HAIRLINE TO SPARE, never over the edge. The
       one-line bar is `nowrap` under `text-overflow: ellipsis`, and ANY
       overflow paints an ellipsis: the half pixel of slack this used to allow
       showed "THUNDER CALLE…" at rest at every card size (Chrome and WebKit,
       2026-09-26), and more letters went as the card was drawn larger. The
       hairline is solveTitleRatio's, so a copy of the card drawn at another
       size keeps every letter as well. A second line wraps inside the bar
       instead, and keeps the half-pixel slack against rounding. */
    const TITLE_LINE_SHARE = 0.995;

    function titleLineFits(measured) {
        return measured.textWidth <= measured.available * TITLE_LINE_SHARE;
    }

    function titleFitsBar(nameElement) {
        const measured = measureTitle(nameElement);
        if (!measured) return false;
        const across = nameElement.classList.contains('card-name--wrap')
            ? measured.textWidth <= measured.available + 0.5
            : titleLineFits(measured);
        return across
            && measured.lines <= TITLE_MAX_LINES
            && measured.lines * measured.lineHeight <= measured.availableHeight + 0.5;
    }

    // Two phases. Phase one keeps the historical single-line title, sized to the
    // width it actually occupies. Only when that would fall below the legibility
    // floor does phase two take the second line, stepping down because line
    // count is quantised and a width ratio would overshoot it.
    // Returns false if the element was not laid out yet.
    function fitCardTitle(nameElement) {
        if (!nameElement || !nameElement.textContent) return true;
        // Engraved = frozen: an enlarged card keeps its resting title.
        if (CardDescEngraving.isTitleEngraved(nameElement)) return true;
        // Nothing is changed before the bar can be measured: sizing it first
        // left a title in a hidden container at full size, overflowing.
        if (!(nameElement.getBoundingClientRect().width > 0) || !(nameElement.clientHeight > 0)) return false;

        nameElement.classList.remove('card-name--wrap');
        applyTitleRatio(nameElement, TITLE_MAX_RATIO);
        const atMax = measureTitle(nameElement);
        if (!atMax) return false;

        // The overwhelming majority of titles fit at the authored size. Leaving
        // here keeps them at ONE forced layout each: every card in a hand, board
        // and catalogue page runs this, and the search below costs a layout per
        // probe, which is enough main-thread time to stall a turn animation.
        if (titleLineFits(atMax)) return true;

        // Solve for the size whose text just fills the bar. Only the part of
        // the width that scales with the type takes part in the ratio; the
        // letter-spacing slice is constant and is subtracted from both sides.
        const singleLine = solveTitleRatio(atMax, TITLE_MAX_RATIO, 1);

        if (singleLine >= TITLE_SINGLE_LINE_MIN_RATIO) {
            applyTitleRatio(nameElement, Math.min(TITLE_MAX_RATIO, singleLine));
            // A solve that lands just above the floor can still be clipped once
            // it settles onto it. That title has not earned its single line;
            // fall through and let it wrap rather than leaving it cut off.
            if (settleTitle(nameElement, TITLE_SINGLE_LINE_MIN_RATIO)) return true;
        }

        nameElement.classList.add('card-name--wrap');
        // A second line roughly halves the width each line must carry. Wrapping
        // is never perfectly efficient, so solve for it and then settle.
        applyTitleRatio(nameElement, clampTitleRatio(solveTitleRatio(atMax, TITLE_MAX_RATIO, 2)));
        settleTitle(nameElement, TITLE_MIN_RATIO);
        return true;
    }

    // Largest ratio at which the text fits across `lines`, from a measurement
    // taken at `atRatio`. Exact for one line; for two it is a starting point
    // that settleTitle() walks down, since wrapping is quantised.
    function solveTitleRatio(measured, atRatio, lines) {
        const scalable = measured.textWidth - measured.fixedWidth;
        const room = measured.available * lines - measured.fixedWidth;
        // Nothing measurable to shrink (whitespace-only title): leave it alone
        // at full size. No room at all: nothing to do but go to the floor.
        if (!(scalable > 0)) return TITLE_MAX_RATIO;
        if (!(room > 0)) return TITLE_MIN_RATIO;
        // 0.995 leaves a hairline for hinting, which is not perfectly linear.
        return Math.min(TITLE_MAX_RATIO, atRatio * (room / scalable) * 0.995);
    }

    // Largest size at or above `floor` that fits, by bisection. Fitting is
    // monotonic in type size — a smaller face is never wider, never uses more
    // line boxes, and therefore is never taller — so bisection is sound and
    // reaches the whole range. A proportional walk is not enough: pulling a
    // three-word title from three line boxes back to two can need a 40% cut,
    // which a bounded stride cannot cover without dozens of layout passes.
    // Returns whether the title ended up inside the bar.
    function settleTitle(nameElement, floor) {
        if (titleFitsBar(nameElement)) return true;
        let hi = titleRatios.get(nameElement);
        applyTitleRatio(nameElement, floor);
        // Even the floor cannot hold it; the caller decides what to do next.
        if (!titleFitsBar(nameElement)) return false;
        let lo = floor;
        for (let step = 0; step < TITLE_FIT_STEPS; step += 1) {
            const mid = (lo + hi) / 2;
            applyTitleRatio(nameElement, mid);
            if (titleFitsBar(nameElement)) lo = mid; else hi = mid;
        }
        applyTitleRatio(nameElement, lo);
        return true;
    }

    function scheduleTitleFit(nameElement, memoryKey = null) {
        // A title the estimate clears with room to spare needs no measurement.
        // Every card in a hand, a board and a catalogue page runs this, and a
        // measured pass costs a forced layout each — the sizing this replaced
        // cost none, and a battle turn should not get slower to fit a long
        // title that most cards do not have.
        if (estimateTitleRatio(nameElement.textContent || '')
            >= TITLE_MAX_RATIO * TITLE_ESTIMATE_MARGIN) {
            return;
        }
        const generation = (titleFitGenerations.get(nameElement) || 0) + 1;
        titleFitGenerations.set(nameElement, generation);
        const isCurrent = () => titleFitGenerations.get(nameElement) === generation;
        const attempt = (count) => {
            if (!isCurrent()) return;
            if (fitCardTitle(nameElement)) {
                standInTitles.delete(nameElement);
                // An engraved title wears another card's fit (an adopted popup).
                if (!CardDescEngraving.isTitleEngraved(nameElement)) rememberTitleFit(memoryKey, nameElement);
                return;
            }
            if (count >= 4) return;
            setTimeout(() => attempt(count + 1), 16);
        };
        // Opened on a stand-in size (applyEstimatedTitleRatio): a seat may
        // run this fit before its first frame (fitTitleBeforeFirstFrame). A
        // title fitted there keeps that fit: the timer below would measure it
        // again mid-landing, under another scale, and move it by a hair.
        let fittedBeforeFrame = false;
        if (memoryKey && titleFitMemory.has(memoryKey)) {
            standInTitles.delete(nameElement);
        } else {
            standInTitles.set(nameElement, () => {
                attempt(4);
                fittedBeforeFrame = !standInTitles.has(nameElement);
            });
        }
        // An engraving that arrives before this first pass runs it first.
        const firstFit = () => attempt(4);
        CardDescEngraving.setPendingFit(nameElement, firstFit);
        setTimeout(() => {
            CardDescEngraving.clearPendingFit(nameElement, firstFit);
            if (!fittedBeforeFrame) attempt(0);
        }, 0);
        // A late webfont swap changes every advance width, so re-fit once the
        // real face is in. All cards share the one already-settled promise.
        if (document.fonts && document.fonts.status !== 'loaded') {
            document.fonts.ready.then(() => {
                if (!isCurrent()) return;
                applyEstimatedTitleRatio(nameElement, memoryKey);
                attempt(0);
            }).catch(() => { /* fitting is presentation-only */ });
        }
    }

    /* A TITLE IS FITTED BEFORE THE FIRST FRAME THAT SHOWS ITS CARD (round 17,
       28 September 2026). A card new to a surface opens its title on a
       stand-in size (its fit elsewhere, a hair smaller, or the estimate) and
       is measured on a timer. A card seated and shown in one task (a warm
       summon whose answer reaches a phone after its drag clone already hangs
       over the slot) could be painted before that timer ran, and its title
       grew on the board a frame or three after it landed: 0.5-2.1% on the
       snappy meter's summon step and in a probe with a late answer, on main
       and on the branch. A seat asks for the fit in the next animation frame,
       which runs before that frame is painted and after the rest of the
       seating task (one layout for all of it). That fit is the title's: its
       timer does not measure it again (a landing scales the card from its
       first frame, and a fit read under another scale differs by a hair).
       Contract: tests/unit/ui/cards.titleFitsBeforeFirstFrame.test.js. */
    const standInTitles = new WeakMap(); // .card-name -> its fit, before its first frame
    const titlesBeforeFrame = new Set();
    let titleFrameRequested = false;

    function fitTitleBeforeFirstFrame(cardElement) {
        const nameElement = cardElement?.querySelector?.(':scope > .card-name');
        if (!nameElement || !standInTitles.has(nameElement)) return;
        if (typeof requestAnimationFrame !== 'function') return; // its timer fits it
        titlesBeforeFrame.add(nameElement);
        if (titleFrameRequested) return;
        titleFrameRequested = true;
        requestAnimationFrame(() => {
            titleFrameRequested = false;
            const names = [...titlesBeforeFrame];
            titlesBeforeFrame.clear();
            names.forEach((name) => {
                const fit = standInTitles.get(name);
                // One pass: a title not laid out yet is left to its timer.
                if (fit && name.isConnected) fit();
            });
        });
    }

    // Helper to dynamically fit description text

    /* FIT MEMORY, keyed by card instance + surface. An enemy summon rebuilds
       BOTH boards from scratch (board.js innerHTML = ''), so every description
       element is new: the fitter restarted at max font — overflow-clipped in a
       small battle card, i.e. INVISIBLE for the frames until the async pass
       walked it back down ("card descriptions disappearing for a second on
       mobile when a card is summoned") — and every guard keyed on the ELEMENT
       (the width high-water) was structurally unable to fire. The fitted size
       must survive the element. Bounded so a long session cannot grow it. */
    const descriptionFitMemory = new Map();
    const DESCRIPTION_FIT_MEMORY_CAP = 600;

    function descriptionFitMemoryKey(cardElement) {
        const instance = cardElement?.dataset?.instanceId
            || cardElement?.dataset?.cardId
            || '';
        if (!instance) return null;
        // Same instance, different surface (hand vs board vs preview) = a
        // different box; the trailing index is per-render noise.
        const surface = String(cardElement.id || '').replace(/-?\d+$/, '');
        return `${surface}|${instance}`;
    }

    function rememberDescriptionFit(key, size, width) {
        if (!key || !Number.isFinite(size)) return;
        if (descriptionFitMemory.has(key)) descriptionFitMemory.delete(key);
        descriptionFitMemory.set(key, { size, width });
        if (descriptionFitMemory.size > DESCRIPTION_FIT_MEMORY_CAP) {
            descriptionFitMemory.delete(descriptionFitMemory.keys().next().value);
        }
    }

    /* ------------------------------------------------------------------
       DESCRIPTION TRACE — development-only, opt-in, off by default.

       "when summoning a card, the other cards on its board lose the card
       description" has been reported thirteen times and survived five rounds
       of work, and no harness on this host has reproduced it: a real-backend
       WebKit run at the owner's MEASURED page box (844x280 @3x, insets
       47/47/20) keeps every description intact through the landing, on both a
       scale-invariant DOM oracle and the composited video frames.

       That leaves the device itself as the only place the mechanism can be
       observed, so this records enough to settle it from DOM state alone:
       every sizing DECISION the fitter makes (with the reason it took that
       branch), plus a bounded per-frame sample of what the board's
       descriptions actually look like across a summon.

       It answers the one question the code cannot currently answer: is the
       fitter shrinking/holding wrongly, or is it innocent and the loss is
       below the DOM entirely? On this host every card sits at the fitter's
       MAX size with scrollHeight === clientHeight, i.e. the shrink loop never
       runs at this page box at all — so if the owner's device shows the same,
       the fitter is exonerated and the cause is compositor-level.

       Enable on the device: append `?descTrace=1` to the URL, or run
       `localStorage.setItem('mfDescTrace','1')` and reload.
       Read back:            `copy(UI.Cards.descriptionTrace())`
       Costs nothing when off — every hook is behind `descTraceOn()`.
       ------------------------------------------------------------------ */
    const DESC_TRACE_CAP = 4000;
    let descTraceEnabled = null;
    const descTraceLog = [];
    let descTraceFrameStop = 0;

    function descTraceOn() {
        if (descTraceEnabled === null) {
            descTraceEnabled = false;
            try {
                descTraceEnabled = new URLSearchParams(window.location.search).get('descTrace') === '1'
                    || window.localStorage?.getItem('mfDescTrace') === '1';
            } catch (_) { /* private mode / no storage: stay off */ }
        }
        return descTraceEnabled;
    }

    function descTracePush(entry) {
        descTraceLog.push({ t: Math.round(performance.now()), ...entry });
        if (descTraceLog.length > DESC_TRACE_CAP) descTraceLog.shift();
    }

    /** What a description element actually looks like right now. */
    function descTraceSample(descElement) {
        const cs = getComputedStyle(descElement);
        const box = descElement.getBoundingClientRect();
        let inside = 0;
        let total = 0;
        try {
            const range = document.createRange();
            range.selectNodeContents(descElement);
            for (const r of range.getClientRects()) {
                if (!(r.width > 0) || !(r.height > 0)) continue;
                total += r.width * r.height;
                const w = Math.min(r.right, box.right) - Math.max(r.left, box.left);
                const h = Math.min(r.bottom, box.bottom) - Math.max(r.top, box.top);
                if (w > 0 && h > 0) inside += w * h;
            }
            range.detach?.();
        } catch (_) { /* geometry is best-effort */ }
        return {
            inline: descElement.style.fontSize || '',
            px: Number.parseFloat(cs.fontSize) || 0,
            opacity: cs.opacity,
            visibility: cs.visibility,
            cw: descElement.clientWidth,
            ch: descElement.clientHeight,
            sh: descElement.scrollHeight,
            chars: (descElement.textContent || '').trim().length,
            // 1.0 = every line box lands inside its own clip box.
            contain: total > 0 ? Number((inside / total).toFixed(3)) : 1,
            onFxLayer: Boolean(descElement.closest('#fx-layer, .fx-glide')),
        };
    }

    /**
     * Sample every board description once per frame for a bounded window.
     * Called at the start of a summon landing so the whole ~1s the owner
     * describes is covered, and self-terminating so it can never become a
     * permanent per-frame cost even if left enabled.
     */
    function beginDescriptionTrace(label, durationMs = 2000) {
        if (!descTraceOn()) return;
        const alreadyRunning = descTraceFrameStop > performance.now();
        descTraceFrameStop = performance.now() + durationMs;
        descTracePush({ event: 'trace-window', label });
        if (alreadyRunning) return;
        const tick = () => {
            if (performance.now() > descTraceFrameStop) return;
            const cards = {};
            for (const card of document.querySelectorAll('#player-board .card, #opponent-board .card')) {
                const id = card.getAttribute('data-instance-id');
                const desc = card.querySelector('.card-desc');
                if (!id || !desc) continue;
                cards[id] = { ...descTraceSample(desc), cls: card.className };
            }
            descTracePush({ event: 'frame', cards });
            requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    }

    /**
     * One sizing decision by the fitter, with the branch it took and the
     * geometry it produced. A description that reads as blank on the device
     * while this reports "measured, settled at max, fully contained"
     * exonerates the fitter and puts the loss below the DOM.
     */
    function recordDescriptionFit(cardElement, descElement, info) {
        if (!descTraceOn()) return;
        const { heldResting, settled, remembered } = info;
        descTracePush({
            event: 'fit',
            id: cardElement?.dataset?.instanceId || cardElement?.dataset?.cardId || '',
            branch: heldResting ? 'held-resting' : 'measured',
            immediate: info.immediate,
            settled: Number(settled.toFixed(3)),
            max: info.maxFontSize,
            min: info.minFontSize,
            hitFloor: !heldResting && settled <= info.minFontSize,
            remembered: remembered ? { size: remembered.size, width: remembered.width } : null,
            seenWidth: Number(descElement.dataset.mfFitWidth) || 0,
            after: descTraceSample(descElement),
        });
    }

    /** The recorded trace, newest last. Safe to call when tracing is off. */
    function descriptionTrace() {
        return {
            enabled: descTraceOn(),
            note: 'fitter decisions + per-frame board description state around each summon',
            entries: descTraceLog.slice(),
        };
    }

    /* ENGRAVED = FROZEN. An enlarged card's description keeps the size and
       the lines of its resting card (card-desc-engraving.js); re-fitting it at
       the enlarged size is exactly the re-wrap the engraving prevents. Any fit
       still scheduled on it is void too (its passes check `isCurrentFit`). */
    function descriptionIsFrozen(descElement) {
        if (!CardDescEngraving.isEngraved(descElement)) return false;
        descriptionFitGenerations.set(descElement, (descriptionFitGenerations.get(descElement) || 0) + 1);
        CardDescEngraving.clearPendingFit(descElement);
        return true;
    }

    function fitCardDescription(cardElement, options = {}) {
        const descElement = cardElement.querySelector('.card-desc');
        // Need text content to fit. Warning: this reads from DOM, so ensure textContent is set.
        if (!descElement || !descElement.textContent.trim()) return;
        if (descriptionIsFrozen(descElement)) return;

        const fitGeneration = (descriptionFitGenerations.get(descElement) || 0) + 1;
        descriptionFitGenerations.set(descElement, fitGeneration);
        // An engraving taken after this fit began freezes it as well.
        const isCurrentFit = () => descriptionFitGenerations.get(descElement) === fitGeneration
            && !CardDescEngraving.isEngraved(descElement);

        const fitConfig = options.descriptionFit || {};

        // Every bound is expressed in card units, so an enlarged surface scales
        // its text by scaling --card-unit — the same token that sizes the frame.
        // There is deliberately no per-caller multiplier: the one that used to
        // exist only compensated for surfaces that resized the frame without
        // the unit, and it double-applies the moment those surfaces are correct.
        const maxFontSize = Number.isFinite(Number(fitConfig.maxFontSize)) ? Number(fitConfig.maxFontSize) : 0.75;
        const minFontSize = Number.isFinite(Number(fitConfig.minFontSize)) ? Number(fitConfig.minFontSize) : 0.25;
        const step = Number.isFinite(Number(fitConfig.step)) ? Number(fitConfig.step) : 0.03;

        const unitSize = (size) => `calc(${size} * var(--card-unit))`;

        /* Open at the REMEMBERED size when this instance has fitted on this
           surface before — a rebuilt card then paints correctly on its first
           frame instead of flashing an overflow-clipped max-font pass. The
           remembered width also re-seeds the shrunken-box guard, which
           otherwise resets with the element. */
        const memoryKey = descriptionFitMemoryKey(cardElement);
        const remembered = memoryKey ? descriptionFitMemory.get(memoryKey) : null;
        const restingSize = remembered
            ? Math.min(maxFontSize, Math.max(minFontSize, remembered.size))
            : maxFontSize;
        if (remembered?.width > 0 && !descElement.dataset.mfFitWidth) {
            descElement.dataset.mfFitWidth = String(remembered.width);
        }

// Apply an immediate size from the same token as every card dimension.
        descElement.style.fontSize = unitSize(restingSize);

        /* A box that cannot fit even ONE line at the smallest allowed size is
           not a tight box — it is an UNSETTLED one. Summoning re-renders the
           board while cards are still animating into place, and measuring
           mid-flight let the shrink loop run all the way down to minFontSize on
           every card at once: the descriptions visibly collapsed and then came
           back a frame later. ("when sumoning cards on mobile for a brief
           moment all summoned cards lose their text description.")
           Treat that exactly like the clientHeight <= 0 case the fitter already
           knew about — hold the readable size and measure again. */
        function boxIsMeasurable() {
            /* A card IN FLIGHT is never a valid ruler. The enemy-summon replay
               flies a clone on the FX layer; that clone has a real height, so
               the unsettled-box guard below passes, and the shrink loop then
               measures a box that is still travelling and scaling. It runs all
               the way to minFontSize and the description reads as blank for the
               split second the card is moving — reported repeatedly as the
               enemy summon blanking the card text. Hold the readable size until
               it lands. */
            if (descElement.closest('#fx-layer, .fx-glide')) return false;
            const height = descElement.clientHeight;
            if (height <= 0) return false;

            /* A box that has just SHRUNK is not a ruler either.
               The two guards above catch a box with no height and a card flying
               on the FX layer, but a card can also be measured while its own
               box is momentarily narrow — mid-transition, mid-relayout, or
               during the frame a board re-render hands it a new slot. The
               shrink loop then fits text to a width that is about to grow, runs
               down toward minFontSize, and the description reads as gone until
               the next fit puts it back.
               So remember the widest this element has genuinely been and refuse
               to measure while it is well under that. It is the same reasoning
               as the height guard, applied to the other axis, and it costs one
               number per element. A card that legitimately gets narrower (a
               responsive re-layout) settles at the new width and the high-water
               mark follows it down on the next settled measurement. */
            const width = descElement.clientWidth;
            /* A zero width is NOT treated as unmeasurable the way a zero height
               is. jsdom reports clientWidth as 0 for every element, and several
               fitter suites drive real fits with only the height mocked, so
               rejecting on width would disable the fitter wherever layout
               metrics are absent rather than merely transient. Height already
               covers the genuinely unrendered case; width only ever rejects a
               box that HAS a width and is far below the widest it has had. */
            if (width > 0) {
                const seen = Number(descElement.dataset.mfFitWidth) || 0;
                if (seen > 0 && width < seen * 0.85) return false;
                if (width > seen) descElement.dataset.mfFitWidth = String(width);
            }
            const line = descElement.getBoundingClientRect
                ? Number.parseFloat(getComputedStyle(descElement).lineHeight)
                : NaN;
            if (!Number.isFinite(line) || line <= 0) return true;
            return height >= line;
        }

        function adjustFontSize() {
            if (!isCurrentFit()) return;
            let currentSize = maxFontSize;
            const setSize = (size) => {
                descElement.style.fontSize = unitSize(size);
            };

            setSize(currentSize);

            /* THE OVERFLOW TEST HAS TO MEAN THE SAME THING AT EVERY SCALE.
               `scrollHeight` and `clientHeight` are INTEGERS — the engine rounds
               both — while everything they measure is a multiple of
               `--card-unit`. So the same card, with the same text, in the same
               box shape, can round differently on a small surface than on a
               large one, and a fraction of a pixel of overflow then costs the
               smaller card a whole `step` rung the larger one never pays.
               Measured on the Collection in Chrome and Firefox: the
               description's ink was 0.21499 of card height on the resting card
               and 0.20386 in its own 2x hover popup — the same words 5.2%
               smaller relative to the card, purely because the popup had more
               pixels to round with, and the text visibly re-fitted as the popup
               opened.
               A tolerance proportional to the box makes the comparison
               `content > 1.02 x box` in card units, which is scale-invariant,
               with a 1px floor so a small box still clears integer rounding.
               The cost is at most a 2% overspill into a box that already clips;
               the cost of not having it is a visible size change on hover. */
            const overflowSlack = () => Math.max(1, descElement.clientHeight * 0.02);

            /* `boxIsMeasurable()` is side-effecting (it advances the width
               high-water mark), so it is called exactly ONCE per pass. The
               branch is recorded by flagging the HELD path below rather than
               by hoisting the call or flagging inside the if, because
               tests/unit/ui/cardDescriptionFitStability.test.js asserts the
               literal `if (boxIsMeasurable()) { while (…` shape to prove the
               shrink loop can only run on a measurable box. */
            let heldResting = false;
            if (boxIsMeasurable()) {
                while (descElement.scrollHeight > descElement.clientHeight + overflowSlack()
                    && currentSize > minFontSize) {
                    currentSize -= step;
                    currentSize = Math.max(currentSize, minFontSize);
                    setSize(currentSize);
                }
                // The slack is for rounding, never for letters (descriptionInkEscapes).
                while (currentSize > minFontSize && descriptionInkEscapes(descElement)) {
                    currentSize = Math.max(currentSize - step, minFontSize);
                    setSize(currentSize);
                }
                rememberDescriptionFit(
                    memoryKey,
                    currentSize,
                    Number(descElement.dataset.mfFitWidth) || descElement.clientWidth || 0,
                );
            } else {
                /* Hold the last KNOWN-GOOD size, not max: holding max on a
                   long description overflows the hidden centred box — the
                   other way the text reads as vanished. */
                heldResting = true;
                setSize(restingSize);
            }
            descElement.style.removeProperty('line-height');

            // Which branch ran, and what the element looked like afterwards.
            recordDescriptionFit(cardElement, descElement, {
                heldResting,
                immediate: options.immediate === true,
                settled: heldResting ? restingSize : currentSize,
                maxFontSize,
                minFontSize,
                remembered,
            });
        }

        function scheduleAdjust(attempt = 0) {
            if (!isCurrentFit()) return;
            adjustFontSize();
            if (!boxIsMeasurable() && attempt < 4) {
                setTimeout(() => scheduleAdjust(attempt + 1), 16);
            }
        }

        // Run by an engraving that arrives before the scheduled first pass.
        function flushFirstFit() {
            adjustFontSize();
        }

        CardDescEngraving.clearPendingFit(descElement);
        if (options.immediate === true) {
            adjustFontSize();
            return;
        }
        CardDescEngraving.setPendingFit(descElement, flushFirstFit);
        setTimeout(() => {
            CardDescEngraving.clearPendingFit(descElement, flushFirstFit);
            scheduleAdjust(0);
        }, 0);
    }

    function updateCardHint(cardElement) {
        const hintEl = ensureHintElement(cardElement);
        const text = computeCardHint(cardElement);
        // Written only when it changes: a snapshot re-derives a kept hovered
        // card's hint (renderPlayerHand), and a same-value write is still a
        // mutation every observer on the page wakes for.
        if (hintEl.textContent !== text) hintEl.textContent = text;
        hintEl.classList.toggle('visible-hint', Boolean(text));
        syncSideHint(cardElement, text);
    }

    // --- Side Hint (off-card display) ---
    // The hint text is rendered next to the card via a single floating element
    // appended to <body>. This escapes the card's overflow:hidden so the hint
    // can sit beside the enlarged card without overlapping its art.
    let sideHintCard = null;
    let sideHintReflowRaf = 0;

    function ensureSideHintElement() {
        let el = document.getElementById('card-side-hint');
        if (el && el.isConnected) return el;
        el = document.createElement('div');
        el.id = 'card-side-hint';
        el.setAttribute('aria-hidden', 'true');
        document.body.appendChild(el);
        return el;
    }

    function shouldDisplaySideHint(cardElement) {
        if (!cardElement || !cardElement.isConnected) return false;
        if (cardElement.classList.contains('hover-active')) return true;
        // The touch inspection popup's card (showPreviewPlayHint) keeps its
        // pill for as long as it is enlarged, through resizes and scrolls.
        if (typeof cardElement.closest === 'function' && cardElement.closest('#card-hover-preview')) return true;
        if (cardElement.classList.contains('dragging')) return false;
        const zone = cardElement.dataset && cardElement.dataset.zone;
        // Board cards reveal on plain :hover (no hover-active class). The
        // SLOT's hover counts too: board hover is the slot's (board.js binds
        // it there, game-board.css enlarges `.card-slot:hover .card`), and a
        // card rebuilt in place under a resting pointer is not `:hover`
        // itself until the browser's next hover update, while its slot never
        // stopped being. Asking only the card withdrew the hint from a card
        // the player was still reading.
        if (zone === 'player-board' || zone === 'opponent-board') {
            try {
                return cardElement.matches(':hover')
                    || Boolean(cardElement.closest('.card-slot')?.matches(':hover'));
            } catch (_) {
                return false;
            }
        }
        return false;
    }

    function getStableAnchor(cardElement) {
        // Board cards zoom up to ~4.8× on hover. For VERTICAL alignment, use
        // the fixed-size .card-slot so the hint doesn't bob as the card
        // enlarges. For HORIZONTAL placement, fall back to the enlarged card
        // bounds below so the hint always sits OUTSIDE the visible card art.
        if (!cardElement) return null;
        const zone = cardElement.dataset && cardElement.dataset.zone;
        if (zone === 'player-board' || zone === 'opponent-board') {
            const slot = typeof cardElement.closest === 'function'
                ? cardElement.closest('.card-slot')
                : null;
            if (slot) return slot;
        }
        return cardElement;
    }

    function positionSideHint(el, cardElement, restingRect = null) {
        if (!el || !cardElement) return;
        const verticalAnchor = getStableAnchor(cardElement) || cardElement;
        // The user-visible card is the (potentially zoomed) card element.
        // Horizontal placement MUST clear those bounds so the hint never sits
        // behind the enlarged card art. A card still moving into its pose is
        // measured where it comes to rest (cardRestingPoseRect).
        const visualRect = restingRect || cardRestingPoseRect(cardElement) || cardElement.getBoundingClientRect();
        const anchorRect = verticalAnchor === cardElement
            ? visualRect
            : verticalAnchor.getBoundingClientRect();
        const vw = window.innerWidth || document.documentElement.clientWidth || 0;
        const vh = window.innerHeight || document.documentElement.clientHeight || 0;
        // jsdom returns zeros — bail out in that case so tests don't move the element off-screen.
        if (vw <= 0 || vh <= 0 || (visualRect.width === 0 && visualRect.height === 0)) {
            el.style.left = '';
            el.style.top = '';
            el.classList.remove('side-hint-left', 'side-hint-right');
            return;
        }
        const PAD = Math.max(8, Math.round(vw * 0.008));
        const hintW = el.offsetWidth || 0;
        const hintH = el.offsetHeight || 0;
        let placeLeft = false;
        let leftPx = visualRect.right + PAD;
        if (leftPx + hintW + PAD > vw) {
            const leftCandidate = visualRect.left - hintW - PAD;
            if (leftCandidate >= PAD) {
                placeLeft = true;
                leftPx = leftCandidate;
            } else {
                // Neither side fits comfortably — clamp to the right edge.
                leftPx = Math.max(PAD, vw - hintW - PAD);
            }
        }
        // Vertical centering uses the stable slot anchor so the hint doesn't
        // drift up/down while the card animates its zoom.
        let topPx = anchorRect.top + anchorRect.height / 2 - hintH / 2;
        topPx = Math.max(PAD, Math.min(topPx, vh - hintH - PAD));
        el.style.left = `${Math.round(leftPx)}px`;
        el.style.top = `${Math.round(topPx)}px`;
        el.classList.toggle('side-hint-left', placeLeft);
        el.classList.toggle('side-hint-right', !placeLeft);
    }

    /* A HINT IS PLACED WHERE ITS CARD COMES TO REST, THEN SHOWN (flicker hunt,
       25 September 2026, the summon). A hand card's hover pose is a CSS
       transition (`#player-hand .card` transitions `transform` for 150 ms: the
       fan's rotation straightens and the card lifts). The pill was shown in
       the first frame of that motion and re-placed beside the moving card on
       every frame, so the visible pill slid by up to (-33, -29) px beside the
       card the player was pressing. It now waits out the card's own running
       pose transition, re-checking because each pointer move retargets it
       (the lean), bounded by SIDE_HINT_POSE_WAIT_CAP_MS, and appears once.
       A card at rest shows it at once, as before. A board card's zoom is a
       script animation this cannot see, so board.js asks for the hint only
       once that zoom has landed (tests/unit/ui/boardSideHintAfterZoom.test.js):
       it is at rest too. Contract:
       tests/unit/ui/cards.sideHintPlacedBeforeShown.test.js. */
    /* ...AND IT IS SHOWN IN THE FIRST FRAME (round 14, 26 September 2026).
       Waiting out the pose removed the slide but made the pill a pop-in: it
       arrived 167-256 ms after the card enlarged, on every hover, and the
       hover step finished later than the baseline (Firefox 357 -> 464 ms).
       The pose the card comes to rest in is known the moment its transition
       starts — it is the transition's own end value — so the pill is placed
       beside it and shown at once (cardRestingPoseRect). Only when that pose
       cannot be read does it still wait, bounded, as above. */
    const SIDE_HINT_POSE_PROPERTIES = new Set(['transform', 'translate', 'rotate', 'scale', 'zoom']);
    const SIDE_HINT_POSE_WAIT_CAP_MS = 300;

    /* The lean — rotateX/rotateY, the tilt that follows the pointer — is left
       out of the resting pose. It changes on every pointer move and the pill
       never chases it; and Chrome serializes a zoomed card's lengths into the
       transition's keyframes already zoomed (perspective(1400px) at zoom 3.8
       reads back as perspective(5320px)), so a lean re-applied through them is
       projected wrongly (measured 6 px off, 26 September 2026). With no
       rotation about X or Y the perspective does nothing, and Chrome, Firefox
       and WebKit measure the same box exactly. */
    function withoutPoseLean(transform) {
        return transform.replace(/\brotate([XY])\([^)]*\)/g, 'rotate$1(0deg)');
    }

    /**
     * Where a card still moving into its pose comes to rest, or null when it
     * is at rest or that pose cannot be read. The end value of each running
     * pose transition is applied for one synchronous measurement and removed
     * again in the same task: no frame ever shows it, and the card's own
     * transition is never touched (seeking it made WebKit start a second one).
     */
    function cardRestingPoseRect(cardElement) {
        if (typeof cardElement?.getAnimations !== 'function' || typeof cardElement.animate !== 'function') {
            return null;
        }
        let running;
        try {
            running = cardElement.getAnimations().filter((animation) => (
                animation?.playState === 'running'
                && SIDE_HINT_POSE_PROPERTIES.has(animation.transitionProperty)
            ));
        } catch (_) {
            return null;
        }
        if (!running.length) return null;
        const pose = {};
        for (const animation of running) {
            const property = animation.transitionProperty;
            let frames = null;
            try {
                frames = animation.effect?.getKeyframes?.();
            } catch (_) {
                frames = null;
            }
            const end = Array.isArray(frames) && frames.length ? frames[frames.length - 1]?.[property] : null;
            if (typeof end !== 'string' || !end) return null;
            pose[property] = property === 'transform' ? withoutPoseLean(end) : end;
        }
        let probe = null;
        try {
            probe = cardElement.animate([pose], { duration: 1, fill: 'forwards' });
            probe.finish?.();
            const rect = cardElement.getBoundingClientRect();
            if (!(rect?.width > 0) || !(rect?.height > 0)) return null;
            return {
                left: rect.left,
                top: rect.top,
                right: rect.right,
                bottom: rect.bottom,
                width: rect.width,
                height: rect.height,
            };
        } catch (_) {
            return null;
        } finally {
            try {
                probe?.cancel?.();
            } catch (_) { /* nothing was applied */ }
        }
    }
    let sideHintPending = null; // { cardElement, requestedAt, timer, frame }

    function sideHintNow() {
        return typeof performance !== 'undefined' && typeof performance.now === 'function'
            ? performance.now()
            : Date.now();
    }

    /** How long the card's own running pose transition still has to go (0: at rest). */
    function cardPoseSettlesInMs(cardElement) {
        let animations = [];
        try {
            // Flushes style, so a pose written in this same task is seen.
            animations = typeof cardElement?.getAnimations === 'function' ? cardElement.getAnimations() : [];
        } catch (_) {
            return 0;
        }
        return (animations || []).reduce((remaining, animation) => {
            if (animation?.playState !== 'running' || !SIDE_HINT_POSE_PROPERTIES.has(animation.transitionProperty)) {
                return remaining;
            }
            const end = Number(animation.effect?.getComputedTiming?.().endTime);
            const current = Number(animation.currentTime);
            return Number.isFinite(end) && Number.isFinite(current)
                ? Math.max(remaining, end - current)
                : remaining;
        }, 0);
    }

    function cancelSideHintReveal() {
        if (!sideHintPending) return;
        clearTimeout(sideHintPending.timer);
        if (sideHintPending.frame && typeof window.cancelAnimationFrame === 'function') {
            window.cancelAnimationFrame(sideHintPending.frame);
        }
        sideHintPending = null;
    }

    function trackSideHintCard(cardElement) {
        const zone = cardElement.dataset?.zone;
        // A board card's hint is asked for once its zoom has landed (board.js
        // hintPlacer), and a card whose pose was waited out is at rest: there
        // is nothing to follow, and re-placing the pill every frame beside a
        // box that no longer changes was only layout reads and style writes.
        // Resize and scroll still re-place it (scheduleSideHintReposition).
        if (zone === 'player-board' || zone === 'opponent-board') return;
        if (typeof cardElement.getAnimations === 'function') return;
        // No getAnimations: the hand card's pose transition cannot be seen,
        // so the pill follows it until it has comfortably settled.
        sideHintTrackEndAt = sideHintNow() + 350;
        maintainSideHintTracking();
    }

    function revealSideHint(el, cardElement, restingRect = null) {
        positionSideHint(el, cardElement, restingRect);
        el.classList.add('visible');
        trackSideHintCard(cardElement);
    }

    function showSideHintFor(cardElement, text) {
        const el = ensureSideHintElement();
        if (el.textContent !== text) el.textContent = text;
        const shownHere = sideHintCard === cardElement && el.classList.contains('visible');
        sideHintCard = cardElement;
        if (shownHere) {
            // Already standing beside this card (a refresh): re-placed as
            // before, and a board card's zoom is followed again.
            positionSideHint(el, cardElement);
            trackSideHintCard(cardElement);
            return;
        }
        if (sideHintPending?.cardElement === cardElement) return; // already waiting for it
        cancelSideHintReveal();
        const pending = {
            cardElement,
            requestedAt: sideHintNow(),
            timer: /** @type {ReturnType<typeof setTimeout> | null} */ (null),
            frame: 0,
        };
        sideHintPending = pending;
        const raf = typeof window.requestAnimationFrame === 'function'
            ? window.requestAnimationFrame.bind(window)
            : (cb) => setTimeout(cb, 16);
        const attempt = () => {
            if (sideHintPending !== pending) return;
            pending.frame = 0;
            if (sideHintCard !== cardElement || !shouldDisplaySideHint(cardElement)) {
                sideHintPending = null;
                hideSideHint(cardElement);
                return;
            }
            const remaining = cardPoseSettlesInMs(cardElement);
            const left = SIDE_HINT_POSE_WAIT_CAP_MS - (sideHintNow() - pending.requestedAt);
            const restingRect = remaining > 0 ? cardRestingPoseRect(cardElement) : null;
            if (restingRect) {
                // Beside where the card comes to rest, in this frame.
                sideHintPending = null;
                revealSideHint(el, cardElement, restingRect);
                return;
            }
            if (remaining > 0 && left > 0) {
                // Read the pose on the frame after the motion ends.
                pending.timer = setTimeout(() => {
                    pending.timer = null;
                    if (sideHintPending === pending) pending.frame = raf(attempt);
                }, Math.min(remaining, left));
                return;
            }
            sideHintPending = null;
            revealSideHint(el, cardElement);
        };
        attempt();
    }

    let sideHintTrackRaf = 0;
    let sideHintTrackEndAt = 0;
    function maintainSideHintTracking() {
        if (sideHintTrackRaf) return;
        const raf = (typeof window !== 'undefined' && window.requestAnimationFrame)
            ? window.requestAnimationFrame
            : (cb) => setTimeout(() => cb(Date.now()), 16);
        const tick = (now) => {
            sideHintTrackRaf = 0;
            if (!sideHintCard) return;
            const el = document.getElementById('card-side-hint');
            if (!el || !el.classList.contains('visible')) return;
            positionSideHint(el, sideHintCard);
            const nowMs = typeof now === 'number' ? now : Date.now();
            if (nowMs < sideHintTrackEndAt) {
                sideHintTrackRaf = raf(tick);
            }
        };
        sideHintTrackRaf = raf(tick);
    }

    function hideSideHint(cardElement, options) {
        // Scoped hide: when a card is supplied (e.g. a leave handler firing for
        // a specific card), only clear if that card owns the active side hint.
        // No argument means an unconditional hide.
        if (cardElement && sideHintCard && sideHintCard !== cardElement) return;
        // A hint still waiting for its card to come to rest is never shown.
        cancelSideHintReveal();
        const el = document.getElementById('card-side-hint');
        if (el) {
            // `instant` (a play was just dispatched) suppresses the CSS transition
            // so the pill vanishes in the same frame instead of trailing a ~180ms
            // shrink-fade beside the card — an ugly stale ghost after the click.
            // A normal hide keeps the transition and fades out in place. Either
            // way, leave left/top untouched: clearing them would snap the element
            // to the stylesheet default (off-screen top-left) and flash there;
            // showSideHintFor() repositions it before the next reveal.
            // The pill beside the enlarged popup is PART of it (cards.css
            // `.side-hint-over-preview`): it leaves in the popup's last frame
            // on every path, never with the 180 ms fade its `.visible` rule
            // stops suppressing the moment the class is removed.
            const instant = !!(options && options.instant) || el.classList.contains('side-hint-over-preview');
            if (instant) el.style.transition = 'none';
            el.classList.remove('visible', 'side-hint-left', 'side-hint-right', 'side-hint-over-preview');
            if (instant) {
                // Only the instant path may empty the pill: on a fading hide the
                // text must survive the ~180ms transition, or the content
                // collapses and an empty bordered box shrink-fades beside the
                // card. showSideHintFor() overwrites the text on every reveal.
                el.textContent = '';
                void el.offsetWidth;      // commit the no-transition off-state…
                el.style.transition = ''; // …then restore so the next reveal animates
            }
        }
        sideHintCard = null;
    }

    // Snap the floating "Drag to Summon/Cast" pill away the instant a card is
    // lifted. A normal hide fades it over ~200ms, leaving a glowing trace beside
    // a card that has already left. Hide UNCONDITIONALLY (not scoped to a card):
    // once a card is in the air the pill is stale no matter which node owns it,
    // and the owner is often a *different* node than the one lifted (the hand
    // can re-render between hover and press), which would make a scoped hide
    // silently no-op.
    function dismissCardHint() {
        hideSideHint(undefined, { instant: true });
    }

    /* THE ENLARGED CARD NAMES ITS GESTURE ON TOUCH TOO. A tap only inspects,
       and there is no longer a Summon/Cast control on the popup, so the pill
       that desktop hover shows ("Drag to Summon") sits beside the enlarged
       card instead. It is raised above the popup, which lives on the top
       layer, and it retires with the popup. */
    function showPreviewPlayHint(sourceElement) {
        const previewCard = document.querySelector('#card-hover-preview .card');
        const text = sourceElement ? computeCardHint(sourceElement) : '';
        if (!previewCard || !text) return;
        const el = ensureSideHintElement();
        el.classList.add('side-hint-over-preview');
        // Same z-index as the popup, so the later node paints on top.
        if (el.nextSibling) document.body.appendChild(el);
        showSideHintFor(previewCard, text);
    }

    function syncSideHint(cardElement, text) {
        // Clear stale ownership if the previously active card was detached
        // (e.g. hand re-render) without a matching hide call.
        if (sideHintCard && !sideHintCard.isConnected) {
            hideSideHint();
        }
        if (text && shouldDisplaySideHint(cardElement)) {
            showSideHintFor(cardElement, text);
            return;
        }
        if (sideHintCard === cardElement) {
            hideSideHint();
        }
    }

    function scheduleSideHintReposition() {
        if (sideHintReflowRaf) return;
        sideHintReflowRaf = requestAnimationFrame(() => {
            sideHintReflowRaf = 0;
            if (!sideHintCard) return;
            const el = document.getElementById('card-side-hint');
            if (!el || !el.classList.contains('visible')) return;
            if (!sideHintCard.isConnected || !shouldDisplaySideHint(sideHintCard)) {
                hideSideHint();
                return;
            }
            positionSideHint(el, sideHintCard);
        });
    }

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        window.addEventListener('resize', scheduleSideHintReposition, { passive: true });
        window.addEventListener('scroll', scheduleSideHintReposition, { passive: true, capture: true });
    }

    // The side hint must disappear the instant a drag starts — leaving it
    // hovering near the original card position while the user drags the card
    // somewhere else is confusing. Cover both drag systems:
    //   • HTML5 drag — fires 'dragstart' on the card.
    //   • CardDrag pointer drag — adds `body.card-dragging` while active.
    if (typeof document !== 'undefined') {
        document.addEventListener('dragstart', () => hideSideHint(), true);
        if (typeof MutationObserver === 'function' && document.body) {
            const dragObs = new MutationObserver(() => {
                if (document.body.classList.contains('card-dragging')) {
                    // The card the pill described has left: gone in the same
                    // frame, never a fading pill beside nothing.
                    dismissCardHint();
                }
            });
            dragObs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        }
    }

    /**
     * Start a Stellar card's resting animation as soon as it is in the DOM.
     *
     * A thin deferral over `playLoopVideo`, which owns the muting, the loop
     * flag, the MP4 fallback and the readiness retry — there is one
     * implementation of "get this loop running", not two that drift apart. The
     * rAF is the only thing this adds: a browser will not play a detached
     * element, and card faces are built before they are appended.
     */
    function autoPlayStellarVideo(cardElement) {
        if (!cardElement || !cardElement.classList.contains('stellar-card')) return;
        requestAnimationFrame(() => {
            if (!cardElement.isConnected) return;
            playLoopVideo(cardElement, { restart: false });
        });
    }

    /**
     * Toggle audio on a stellar card that's already auto-playing.
     * Loop audio is permanently muted — only an accepted action clip plays audio.
     */
    function setStellarAudioHover() {
        // Loop audio disabled; summon/cast audio is handled separately.
    }

    // Live pixel size of a resting card. A hidden probe measures the CSS vars
    // because deriving vmin in JS desyncs the moment --card-unit gains its
    // phone px floor (and text-parsing calc()/max() is a dead end).
    let cardPixelSizeCache = null;

    function getCardPixelSize() {
        if (cardPixelSizeCache) return cardPixelSizeCache;
        const probe = document.createElement('div');
        probe.style.cssText =
            'position:absolute;left:-9999px;top:0;visibility:hidden;pointer-events:none;'
            + 'width:var(--card-width);height:var(--card-height);';
        document.body.appendChild(probe);
        const rect = probe.getBoundingClientRect();
        probe.remove();
        cardPixelSizeCache = { width: rect.width, height: rect.height };
        return cardPixelSizeCache;
    }

    window.addEventListener('resize', () => { cardPixelSizeCache = null; });
    window.addEventListener('orientationchange', () => { cardPixelSizeCache = null; });

    /**
     * Resolve a card's art into an <img> — the single shared resolver used by
     * full card faces and by lightweight consumers (story-feed tiles) so art
     * lookup rules never fork: direct URL first, then Convex asset id.
     */
    function resolveCardArt(card, img, options = {}) {
        if (!img) return Promise.resolve(false);
        const rasterReserveScale = normalizeRasterReserveScale(options.rasterReserveScale);
        img.dataset.cardArtRasterOversample = `${rasterReserveScale}`;
        loadCardImageWithFallbacks(
            img,
            resolveCardImageCandidates(card || {}),
            cardArtProfile({
                fit: options.fit === 'contain' ? 'contain' : 'cover',
                oversample: rasterReserveScale,
            }),
            { directArtOnly: options.directArtOnly === true },
        );
        return pendingCardArtReadiness.get(img)?.promise || Promise.resolve(false);
    }

    /** Release every match-scoped media/animation identity before a fresh Game. */
    function resetMatchTracking() {
        resetHandTracking();
        CardbackArt?.clear?.(document.getElementById('opponent-hand'));
        EnemyFan?.reset?.();
        // renderOpponentHand keeps its backs from snapshot to snapshot, never
        // from match to match: the fan has just forgotten the flight and the
        // departure its classes on those backs belong to. Leaving through the
        // lobby does not clear the board nodes (game-end's cleanup does).
        document.getElementById('opponent-hand')?.replaceChildren();
        // The same for the player's hand: renderPlayerHand keeps an unchanged
        // card from snapshot to snapshot (isRestingHandCard), never into the
        // next match, whose deal is its own.
        document.getElementById('player-hand')?.replaceChildren();
        stellarSummonPlayed.clear();
        stellarSummonPending.clear();
        stellarBoardSnapshot = null;
        resetStellarSpellCastTracking();

        activeSummonVideos.forEach((entry) => {
            const video = entry?.video;
            if (!video) return;
            if (video._volumeFadeTimer) {
                clearInterval(video._volumeFadeTimer);
                video._volumeFadeTimer = null;
            }
            try { video.pause(); } catch (_) { /* no-op */ }
            try { video.removeAttribute('src'); } catch (_) { /* no-op */ }
            try { video.load?.(); } catch (_) { /* no-op */ }
            try { video.remove(); } catch (_) { /* no-op */ }
        });
        activeSummonVideos.clear();
        document.querySelectorAll('video.stellar-loop-video, video.stellar-summon-video, video.stellar-cast-video')
            .forEach((video) => {
                if (video._volumeFadeTimer) {
                    clearInterval(video._volumeFadeTimer);
                    video._volumeFadeTimer = null;
                }
                try { video.pause(); } catch (_) { /* no-op */ }
            });

        if (summonStagingRoot) {
            try { summonStagingRoot.remove(); } catch (_) { /* no-op */ }
            summonStagingRoot = null;
        } else {
            document.getElementById('stellar-summon-staging')?.remove();
        }
        clearHoverPreview();
        hideSideHint();
        if (sideHintReflowRaf) {
            window.cancelAnimationFrame?.(sideHintReflowRaf);
            sideHintReflowRaf = 0;
        }
        if (sideHintTrackRaf) {
            window.cancelAnimationFrame?.(sideHintTrackRaf);
            sideHintTrackRaf = 0;
        }
        sideHintTrackEndAt = 0;
    }

    // Export functions to Cards namespace
    Cards.createCardElement = createCardElement;
    Cards.cardRenderSignature = cardRenderSignature;
    Cards.resolveCardArt = resolveCardArt;
    Cards.loadCardArtImage = loadCardArtImage;
    Cards.disposeCardArtImage = disposeCardArtImage;
    Cards.enhanceCardArt = enhanceCardArt;
    Cards.refreshCardRaster = refreshCardRaster;
    Cards.lockCardRaster = lockCardRaster;
    Cards.prepareCardRasterForInteraction = prepareCardRasterForInteraction;
    Cards.transferSettledRasterPaint = transferSettledRasterPaint;
    Cards.transferCardPresentation = transferCardPresentation;
    Cards.fitTitleBeforeFirstFrame = fitTitleBeforeFirstFrame;
    /**
     * Is this connected card's settled paint already exactly what
     * `settleCardRasterForPresentation(card, _, { oversample })` would settle
     * (its geometry, its reserve and, on Chromium, the bitmap tier)? Synchronous,
     * so a caller holding a node for presentation can release it in the task
     * that inserted it.
     */
    Cards.isRasterPresentationCurrent = (cardElement, { oversample = 1 } = {}) => isCurrentLockedRasterGeometry(
        cardElement,
        { oversample: Number.isFinite(oversample) ? Math.max(1, oversample) : 1, forceCanvas: true },
    );
    Cards.settleCachedRasterPaint = settleCachedRasterPaint;
    Cards.restoreCardRasterReserve = restoreCardRasterReserve;
    Cards.settleCardRasterForPresentation = settleCardRasterForPresentation;
    Cards.settleCardTextForPresentation = settleCardTextForPresentation;
    Cards.settleCardFaceForPresentation = settleCardFaceForPresentation;
    Cards.copyCanvasPaints = copyCanvasPaints;
    Cards.getCardPixelSize = getCardPixelSize;
    Cards.renderPlayerHand = renderPlayerHand;
    Cards.ARRIVAL_ART_WAIT_CAP_MS = ARRIVAL_ART_WAIT_CAP_MS;
    Cards.holdOpponentDeparture = holdOpponentDeparture;
    Cards.releaseOpponentDeparture = releaseOpponentDeparture;
    Cards.refreshLegalPlayHighlights = refreshLegalPlayHighlights;
    Cards.holdCardCohortUntilSettled = holdCardCohortUntilSettled;
    Cards.resetHandTracking = resetHandTracking;
    Cards.resetMatchTracking = resetMatchTracking;
    Cards.renderOpponentHand = renderOpponentHand;
    Cards.addPlayerHandDragListeners = addPlayerHandDragListeners;
    Cards.addOpponentHandDropTargets = addOpponentHandDropTargets;
    Cards.calculateHoverScale = calculateHoverScale;
    Cards.applyHoverTransform = applyHoverTransform;
    Cards.updateCardHint = updateCardHint;
    Cards.hideSideHint = hideSideHint;
    Cards.setStellarHover = setStellarHover;
    Cards.setStellarAudioHover = setStellarAudioHover;
    Cards.autoPlayStellarVideo = autoPlayStellarVideo;
    Cards.attachStellarVideo = attachStellarVideo;
    Cards.queueStellarSummonPlayback = queueStellarSummonPlayback;
    Cards.resolveStellarSummonVideoSource = resolveStellarSummonVideoSource;
    Cards.playStellarSpellCast = playStellarSpellCast;
    Cards.shouldDeferSpellCastSound = shouldDeferSpellCastSound;
    Cards.registerStellarSpellCast = registerStellarSpellCast;
    Cards.hasStellarSpellCastSoundSuppression = hasStellarSpellCastSoundSuppression;
    Cards.consumeStellarSpellCastSoundSuppression = consumeStellarSpellCastSoundSuppression;
    Cards.rejectStellarSpellCast = rejectStellarSpellCast;
    Cards.resetStellarSpellCastTracking = resetStellarSpellCastTracking;
    Cards.attachStagedSummonVideos = attachStagedSummonVideos;
    Cards.syncStellarBoardSummons = syncStellarBoardSummons;
    Cards.attachStagedSummonVideos = attachStagedSummonVideos;
    Cards.fitCardDescription = fitCardDescription;
    // An enlarged card keeps its resting lines (card-desc-engraving.js).
    // Delegates, so browser specs reach them through the bridged namespace.
    Cards.engraveDescription = (card, options) => CardDescEngraving.engrave(card, options);
    Cards.releaseDescription = (card) => CardDescEngraving.release(card);
    Cards.adoptDescription = (card, source) => CardDescEngraving.adopt(card, source);
    Cards.descriptionLayoutOf = (card) => CardDescEngraving.layoutOf(card);
    Cards.isDescriptionEngraved = (card) => CardDescEngraving.isEngraved(card);
    // Opt-in description tracing (see the block above fitCardDescription).
    Cards.beginDescriptionTrace = beginDescriptionTrace;
    Cards.descriptionTrace = descriptionTrace;
    Cards.fitCardTitle = fitCardTitle;

    // --- Shared hover preview positioning ---
    // Used by Collection, Moon Exchange, Card Packs, Moon Dream.
    const HOVER_PREVIEW_MARGIN = 16;
    const HOVER_PREVIEW_PHONE_MARGIN = 4;
    const HOVER_PREVIEW_Z_INDEX = '2147483647';

    /**
     * The gutter an enlarged popup leaves around itself.
     *
     * A landscape phone page box is ~300px tall, so a 16px gutter on each side
     * costs 11% of the card the player asked to READ — measured, the enlarged
     * card reached only 84-86% of the page box. On a phone the card IS the
     * screen: take every pixel that still fits.
     */
    function hoverPreviewMargin() {
        try {
            if (window.matchMedia?.('(orientation: landscape) and (max-height: 560px)')?.matches) {
                return HOVER_PREVIEW_PHONE_MARGIN;
            }
        } catch (_) { /* no matchMedia: keep the desktop gutter */ }
        return HOVER_PREVIEW_MARGIN;
    }

    /**
     * How far a popup may grow before the viewport cap takes over.
     *
     * `calculateHoverScale` returns min(baseScale, heightScale, widthScale), so
     * whichever is smallest wins. On a desktop 3.5 is a deliberate ceiling. On a
     * landscape phone it is the BINDING term and it wastes the screen: measured
     * against the card's layout box, 3.5 filled only 84-86% of the page box
     * while the height cap would have allowed ~4.05. Raise the ceiling on phones
     * so the FIT is what limits the card, which is exactly "no padding as long
     * as it fits the screen". Overflow is impossible — the height and width caps
     * still apply, now with only a 4px gutter.
     */
    function hoverPreviewBaseScale() {
        try {
            if (window.matchMedia?.('(orientation: landscape) and (max-height: 560px)')?.matches) {
                return 6;
            }
        } catch (_) { /* no matchMedia: keep the desktop ceiling */ }
        return 3.5;
    }

    const HOVER_PREVIEW_OPTIONS = {
        get baseScale() { return hoverPreviewBaseScale(); },
        minScale: 1.6,
        get margin() { return hoverPreviewMargin(); },
        origin: 'center center',
        translateYPercent: 0,
    };

    function clampNum(value, min, max) {
        if (!Number.isFinite(value)) return min;
        if (!Number.isFinite(min)) return value;
        if (!Number.isFinite(max)) return value;
        if (min > max) return min;
        return Math.min(Math.max(value, min), max);
    }

    function positionHoverPreview(preview, anchorRect, scale) {
        if (!preview || !anchorRect) return;
        const vw = window.innerWidth || document.documentElement.clientWidth || 0;
        const vh = window.innerHeight || document.documentElement.clientHeight || 0;
        const m = hoverPreviewMargin() * uiScaleOf(vw, vh);

        const sw = Number(anchorRect.width) || 0;
        const sh = Number(anchorRect.height) || 0;
        const sourceCenterX = anchorRect.left + sw / 2;
        const sourceCenterY = anchorRect.top + sh / 2;

        const cardEl = preview.querySelector('.card');
        const cardBCR = cardEl ? cardEl.getBoundingClientRect() : null;
        const bcrValid = cardBCR && cardBCR.width > 0 && cardBCR.height > 0;
        const visualW = bcrValid ? cardBCR.width : (cardEl?.offsetWidth || sw);
        const visualH = bcrValid ? cardBCR.height : (cardEl?.offsetHeight || sh);

        const curLeft = parseFloat(preview.style.left) || 0;
        const curTop = parseFloat(preview.style.top) || 0;
        const centerOffsetX = bcrValid ? (cardBCR.left + cardBCR.width / 2 - curLeft) : visualW / 2;
        const centerOffsetY = bcrValid ? (cardBCR.top + cardBCR.height / 2 - curTop) : visualH / 2;

        let centerX = clampNum(sourceCenterX, m + visualW / 2, Math.max(m + visualW / 2, vw - m - visualW / 2));
        let centerY = clampNum(sourceCenterY, m + visualH / 2, Math.max(m + visualH / 2, vh - m - visualH / 2));


        /* The viewport is NOT the safe area: `viewport-fit=cover` runs the page
           under the Dynamic Island and the home indicator on purpose, so a
           viewport-clamped popup parks the card's edge under hardware. Re-clamp
           the card's visual rect into the safe box (a no-op wherever the insets
           are zero, which is every desktop). */
        const safeClamp = SafeArea?.clamp;
        if (typeof safeClamp === 'function') {
            // extra = 0: the cosmetic margin is already in the viewport clamp
            // above, and on a short page box adding it again can shrink the box
            // below the card's own height, which pins the card to the notch.
            const placed = safeClamp(centerX - visualW / 2, centerY - visualH / 2, visualW, visualH, 0);
            centerX = placed.left + visualW / 2;
            centerY = placed.top + visualH / 2;
        }

        preview.style.left = `${Math.round(centerX - centerOffsetX)}px`;
        preview.style.top = `${Math.round(centerY - centerOffsetY)}px`;
    }

    let hoverPreviewGeneration = 0;

    function clearHoverPreview() {
        hoverPreviewGeneration += 1;
        // A tear-down takes a preview handed to a neighbour on a glide too:
        // it gave the id up (hover-handoff.js, "ONE ELEMENT NAMES THE
        // PREVIEW"), so the lookup below finds only the one being built.
        HoverHandoff?.releaseHandedOff?.();
        // The battle popup's pill is part of it (cards.css
        // `.side-hint-over-preview`): whoever takes the popup down (game end,
        // the story feed, the Mulligan, the next popup) takes the pill with
        // it, in the same frame. A hand card's own hover pill is not touched.
        if (document.getElementById('card-side-hint')?.classList.contains('side-hint-over-preview')) {
            hideSideHint(undefined, { instant: true });
        }
        const existing = document.getElementById('card-hover-preview');
        if (!existing) return;
        const card = existing.querySelector('.card');
        if (card?.classList?.contains('stellar-card') && Cards?.setStellarHover) {
            Cards.setStellarHover(card, false);
        }
        // The battle popup's touch lean listens on the DOCUMENT (it cannot
        // hear its own finger); the listeners leave with the popup.
        if (card) {
            try { CardTilt?.unregister?.(card); } catch (_) { /* presentation only */ }
        }
        existing.remove();
    }

    function applyPreviewScale(cardEl, _variant, sourceRect, preview, options) {
        const opts = options || HOVER_PREVIEW_OPTIONS;
        const baseRect = cardEl?.getBoundingClientRect?.();
        const computed = cardEl ? getComputedStyle(cardEl) : null;
        let scale = opts.baseScale;
        if (calculateHoverScale) {
            scale = calculateHoverScale(cardEl, opts);
        }

        // Disable CSS transition so BCR returns the final size immediately
        // for accurate viewport clamping. Only affects cards using this function
        // (Moon Exchange, Card Packs, Moon Dream) — Collection/Deck Builder are unaffected.
        cardEl.style.transition = 'none';

        // Enlarged popup cards open directly beneath the pointer. Perspective
        // tilt would therefore activate on their first hit-tested frame and
        // distort the art box after its physical-pixel canvas was settled.
        // Popups are inspection surfaces: keep the whole card motionless while
        // ordinary in-place cards retain their interactive tilt treatment.
        cardEl.classList.add('card-tilt-disabled');
        cardEl.classList.remove('card-tilt-active');
        cardEl.style.setProperty('--card-tilt-x', '0.5');
        cardEl.style.setProperty('--card-tilt-y', '0.5');
        cardEl.style.setProperty('--card-tilt-rotate-x', '0deg');
        cardEl.style.setProperty('--card-tilt-rotate-y', '0deg');
        cardEl.style.setProperty('--card-tilt-opacity', '0');

        const growth = scale > 1 ? scale : 1;
        cardEl.style.setProperty('--card-frame-scale', `${growth}`);

        const declaredBoxMetric = (metric, outerMetric) => {
            let value = Number.parseFloat(computed?.[metric]);
            if (!(value > 0)) value = Math.max(0, Number(outerMetric || 0));
            return value;
        };
        // `--card-width` / `--card-height` own the CSS width properties, not a
        // transformed bounding rectangle. In particular, the BCR includes the
        // resting frame when an engine resolves the card as content-box; feeding
        // that outer value back into the variable grows the art window beyond
        // the 3.5x raster that was reserved for it.
        const declaredWidth = declaredBoxMetric('width', baseRect?.width);
        const declaredHeight = declaredBoxMetric('height', baseRect?.height);

        /* PHYSICAL enlargement on one path for normal and Stellar cards. The
           popup grows by being bigger, never by stretching a base-size raster.
           Scaling --card-unit with the box is essential: fonts, paddings,
           badges, art margins, and status anatomy all derive from it. A
           Stellar-only width/height branch left those parts at resting size
           inside a 3.5x frame and needed fragile vmin compensation rules. */
        if (declaredWidth > 0 && declaredHeight > 0) {
            cardEl.style.setProperty('--card-unit', `${(declaredWidth / 12.29) * growth}px`);
            cardEl.style.setProperty('--card-width', `${declaredWidth * growth}px`);
            cardEl.style.setProperty('--card-height', `${declaredHeight * growth}px`);
        }
        cardEl.style.removeProperty('--preview-scale');
        /* No `scale()` token at all, not even an identity one. The inline
           value outranks each surface's own hover rule without weakening the
           compositor-stretch oracle. */
        cardEl.style.setProperty('--card-hover-transform', 'rotate(0deg) translateY(0%)');
        cardEl.style.setProperty('--card-base-transform', 'scale(1)');
        /* The popup is the card it enlarges: the same size step and the same
           lines (card-desc-engraving.js). Fitted afresh at 2-3.5x it re-wrapped
           whenever a line sat on its wrap point. A popup with no source card,
           or a different text, keeps its own fit below; an adopted (engraved)
           description is left alone by it. */
        if (opts.sourceCard) CardDescEngraving.adopt(cardEl, opts.sourceCard);
        if (fitCardDescription) {
            fitCardDescription(cardEl, {
                descriptionFit: opts.descriptionFit,
                immediate: true,
            });
        }
        positionHoverPreview(preview, sourceRect, 1);
    }

    /**
     * In-game popup preview (touch tap inspection of hand/board cards).
     * Reuses the shared #card-hover-preview recipe from the catalog surfaces
     * (Collection, Moon Exchange, Card Packs, Moon Dream) so the enlarged
     * card renders identically everywhere: one shared element, viewport
     * clamped, same z-index, same stellar handling.
     *
     * `options.interactive` chooses which of the two established popup
     * behaviours this one gets. The BATTLE popup is pointer-transparent — a
     * press falls through to the card it enlarged, which is the same card the
     * press was for, and CardDrag keeps seeing its pointerdown. A popup over a
     * MODAL surface must not do that: underneath it is a dismissing backdrop,
     * so a press on the enlarged card would close the screen the reader is
     * reading. Interactive popups take the press themselves; their caller
     * decides what it means.
     *
     * @param {HTMLElement} cardEl the card the popup is opened from
     * @param {object} card the card data it is built from
     * @param {{ interactive?: boolean }} [options]
     */
    async function showCardPreviewFor(cardEl, card, options = {}) {
        if (!cardEl || !card) return false;
        // A mid-tap re-render can detach the pressed element: its 0x0 rect at
        // (0,0) would clamp the enlarged popup into the viewport corner with
        // stale card data. Skip; the document-level pointer end still retires
        // the pending tap safely.
        if (!cardEl.isConnected) return false;
        clearHoverPreview();
        const generation = hoverPreviewGeneration;

        /* Art readiness must NOT gate the inspection.
           A card whose art is missing or failed to raster could not be enlarged
           at all — reported as "I cannot tap it to enlarge, and it doesn't have
           a picture", which is one defect, not two: the missing picture is
           exactly what blocked the tap. That is backwards. The card's TEXT is
           what a player enlarges a card to read — its cost, its rules, its
           name — and none of that depends on the art.
           docs/asset-loading.md law 2 says the same thing in general: never
           hide an element because its media is not ready; gate the art-well
           swap, never the element. So the preview opens regardless, and the
           art well shows whatever state it is in.
           The generation and connection checks stay — those are about the
           gesture still being current, not about the picture. */
        const prepared = await withArtGrace(prepareCardRasterForInteraction(cardEl, 'hover'));
        if (
            generation !== hoverPreviewGeneration
            || !cardEl.isConnected
        ) return false;
        if (!prepared && window.CLIENT_DEBUG_MODE) {
            console.log('[Cards] Enlarging without a settled raster; art well shows its own state.');
        }

        const sourceRect = cardEl.getBoundingClientRect();

        const preview = document.createElement('div');
        preview.id = 'card-hover-preview';
        preview.className = options.interactive === true
            ? 'card-hover-preview'
            : 'card-hover-preview game-hover-preview';
        preview.style.zIndex = HOVER_PREVIEW_Z_INDEX;
        preview.style.left = `${Math.round(sourceRect.left)}px`;
        preview.style.top = `${Math.round(sourceRect.top)}px`;
        preview.style.visibility = 'hidden';

        const variant = isStellarCard(card) ? 'stellar' : 'normal';
        const previewCard = createCardElement(card, `game-preview-${card.instanceId || card.id}`);
        previewCard.style.transformOrigin = HOVER_PREVIEW_OPTIONS.origin;
        previewCard.style.setProperty('--card-hover-transform', 'scale(1) rotate(0deg) translateY(0%)');
        if (variant === 'stellar') {
            // Preview playback semantics (restart loop video, fade audio in/out)
            // match the catalog previews.
            previewCard.dataset.stellarPreview = 'collection';
        }
        preview.appendChild(previewCard);

        document.body.appendChild(preview);

        // The popup is connected and measurable now. Finalize typography,
        // scale, and clamping in this same task so the browser cannot paint an
        // unscaled or differently-fitted intermediate frame.
        if (preview.isConnected) {
            // The popup adopts the card it enlarges: its size step and lines.
            applyPreviewScale(previewCard, variant, sourceRect, preview, {
                ...HOVER_PREVIEW_OPTIONS,
                sourceCard: cardEl,
            });
        }
        /* Art settlement is raced against a hard cap, never waited on outright.
           Waiting outright is what made a picture-less card un-enlargeable: the
           settle only resolves when the art reaches a terminal state, so a card
           whose art never arrives held the popup hidden until its load timeout
           — indistinguishable, to the player, from tapping doing nothing.
           The cap is short enough that a normal card (already rastered in the
           hand) wins the race and appears fully sharp with no intermediate
           frame, and long enough that we are not flashing unsettled art for the
           sake of it. Past it the card presents anyway with its text, which is
           what it was enlarged for, and the art well finishes on its own.
           docs/asset-loading.md law 2: gate the art swap, cap any visible
           gate, never gate the element. */
        const settled = await withArtGrace(settleCardRasterForPresentation(previewCard, card, {
            oversample: 1,
            stableGeometry: true,
            sourceCard: cardEl,
            coverVisualExpansion: true,
        }));
        if (
            generation !== hoverPreviewGeneration
            || !cardEl.isConnected
            || !preview.isConnected
        ) {
            preview.remove();
            return false;
        }
        // `null` is the cap firing: art is still in flight and keeps settling on
        // its own. `false` means this presentation attempt ended, but the card's
        // source loader may still be recovering in the background.
        if (settled === false) {
            // Keep the complete physical card face inspectable while withholding
            // only an undecoded paint. Never replace its real source with the
            // Moon Fuse generation placeholder.
            presentUnavailableArtWell(previewCard);
        }

        if (variant === 'stellar') {
            setStellarHover(previewCard, true);
        }
        preview.style.removeProperty('visibility');
        /* Revealed, so it leans under a finger like every other enlarged
           card. The battle popup is pointer-transparent (a press falls
           through to the card it enlarged), so it cannot be its own tilt
           host: CardTilt listens on the document for it. An INTERACTIVE
           popup takes the pointer itself and its caller re-arms tilt on it. */
        if (options.interactive !== true) {
            try { CardTilt?.registerTransparentPreview?.(previewCard); } catch (_) { /* presentation only */ }
        }
        return true;
    }

    /** Keep a preview's text/frame visible without inventing replacement art. */
    function presentUnavailableArtWell(cardElement) {
        const img = cardElement?.querySelector?.('.card-image > img.card-art-paint')
            || cardElement?.querySelector?.('.card-image img');
        if (!img || img.classList.contains('moon-fuse-placeholder-art')) return;
        img.classList.remove('placeholder-art', 'character-portrait');
        if (img.getAttribute('src') === CARD_IMAGE_PLACEHOLDER) img.removeAttribute('src');
        if (img.classList.contains('card-art-retrying')) img.classList.add('card-art-loading');
    }

    /** Dismiss the in-game popup preview (shared element, stellar-safe). */
    function clearCardPreview() {
        // The popup's pill leaves with it (clearHoverPreview): one frame, never
        // fading out beside a card that is already gone.
        clearHoverPreview();
    }

    Cards.positionHoverPreview = positionHoverPreview;
    // The popup art rule above, for a popup built outside this module (the
    // Foundry's): the cap on the art settlement, and the frame-and-text face
    // a card keeps when its art could not settle.
    Cards.withHoverPreviewArtGrace = withArtGrace;
    Cards.presentUnavailableArtWell = presentUnavailableArtWell;
    Cards.clearHoverPreview = clearHoverPreview;
    Cards.applyPreviewScale = applyPreviewScale;
    Cards.showCardPreviewFor = showCardPreviewFor;
    Cards.reflowHand = reflowHand;
    Cards.releaseHandForPlayer = releaseHandForPlayer;
    Cards.clearCardPreview = clearCardPreview;
    Cards.HOVER_PREVIEW_OPTIONS = HOVER_PREVIEW_OPTIONS;
    Cards.HOVER_PREVIEW_Z_INDEX = HOVER_PREVIEW_Z_INDEX;

    // Log that the module is loaded
    if (window.CLIENT_DEBUG_MODE) console.log('Cards module loaded!', Cards);
})();

export { Cards };
